import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription } from './components/ui/alert';
import { Separator } from './components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './components/ui/sheet';
import { HELP_ROLES, HELP_CHANGES, SCREEN_LABELS, ROLE_SCREENS, screenForPath } from './help/content';
import buildInfo from './buildInfo.json';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || `http://${window.location.hostname}:8000`;
const API = `${BACKEND_URL}/api`;
// Published for static pages outside this bundle — the atlas at /atlas/ is served
// same-origin with the frontend but the API is not (api.earthblocks.in), and a
// plain HTML file cannot read a REACT_APP_* build variable.
try { localStorage.setItem('eb_api', API); } catch (e) {}

// Version and build timestamp baked in at build time (see
// scripts/generate-build-info.js) so the page can say which deployed build is
// actually running. Displayed in IST (Bangalore).
const APP_VERSION = buildInfo.version || '0.0.0';
const BUILD_TIME = (() => {
  try {
    return new Date(buildInfo.buildTime).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return buildInfo.buildTime;
  }
})();

// ── Axios auth interceptors — attach the access token, and on a 401 silently
//    renew it with the stored refresh token so sessions last the full 7-day
//    refresh-token window (not just the 60-min access token). This is what keeps
//    users signed in across app reopens on mobile. ──
axios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('eb_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let _refreshing = null;  // in-flight refresh promise, so concurrent 401s share one call
async function _refreshAccessToken() {
  const rt = localStorage.getItem('eb_refresh');
  if (!rt) return null;
  try {
    const r = await axios.post(`${API}/auth/refresh`, { refresh_token: rt }, { _skipAuthRefresh: true });
    localStorage.setItem('eb_token', r.data.access_token);
    if (r.data.refresh_token) localStorage.setItem('eb_refresh', r.data.refresh_token);
    return r.data.access_token;
  } catch {
    return null;   // refresh token invalid or expired (>7 days) — real logout
  }
}

axios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;
    // On a 401, try to renew the access token once, then retry the original request.
    if (status === 401 && !cfg._retry && !cfg._skipAuthRefresh && localStorage.getItem('eb_refresh')) {
      cfg._retry = true;
      if (!_refreshing) _refreshing = _refreshAccessToken().finally(() => { _refreshing = null; });
      const newToken = await _refreshing;
      if (newToken) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newToken}`;
        return axios(cfg);  // retry with the fresh token
      }
    }
    if (status === 401) {
      // No refresh token, or refresh failed — log out.
      localStorage.removeItem('eb_token');
      localStorage.removeItem('eb_refresh');
      invalidateCachedMe();  // drop stale user role so admin UI doesn't linger
      const p = window.location.pathname;
      // Only redirect from protected pages; landing page and public routes stay put
      if (p.startsWith('/factory') || p.startsWith('/delivery') || p.startsWith('/dashboard')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);

// Cache invalidator — declared here (defined near getCachedMe) so the interceptor
// can call it before the cache exists. Set to a no-op until getCachedMe is defined.
let invalidateCachedMe = () => {};

// ─── LIVE NOTIFICATIONS (Server-Sent Events) ─────────────────────────────────
// One EventSource per protected-route mount. Backend pushes delivery_confirmed
// and delivery_status_changed; any component can subscribe via EB_EVENTS.
// Chosen over polling because 4-5 deliveries/day makes a 30s poll ~2800 wasted
// requests per user per day. SSE is one-way HTTP push — plays well with Railway,
// no service-worker plumbing.
const EB_EVENTS = typeof window !== 'undefined' ? new EventTarget() : null;

function useAppEventStream() {
  React.useEffect(() => {
    if (!EB_EVENTS || typeof EventSource === 'undefined') return;
    let es = null;
    let reconnectTimer = null;
    let cancelled = false;
    let backoff = 2000;

    const dispatch = (type, data) => {
      try { EB_EVENTS.dispatchEvent(new CustomEvent(type, { detail: data })); } catch {}
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, 30000);
    };

    const connect = async () => {
      if (cancelled) return;
      let token = localStorage.getItem('eb_token');
      if (!token) { scheduleReconnect(); return; }
      try {
        es = new EventSource(`${API}/events/stream?token=${encodeURIComponent(token)}`);
      } catch { scheduleReconnect(); return; }

      es.addEventListener('delivery_confirmed', (ev) => {
        try { dispatch('delivery_confirmed', JSON.parse(ev.data)); } catch {}
      });
      es.addEventListener('delivery_status_changed', (ev) => {
        try { dispatch('delivery_status_changed', JSON.parse(ev.data)); } catch {}
      });
      // Accounts raised the invoice for a loaded truck → the load is cleared to depart.
      es.addEventListener('delivery_documents_ready', (ev) => {
        try { dispatch('delivery_documents_ready', JSON.parse(ev.data)); } catch {}
      });
      // A payment landed in Zoho → the load's payment gate was re-checked. Fires the
      // moment the money is recorded, so Loading Completed turns on while the yard
      // is looking at the screen instead of on the next page open.
      es.addEventListener('delivery_funds_updated', (ev) => {
        try { dispatch('delivery_funds_updated', JSON.parse(ev.data)); } catch {}
      });
      // One catch-all nudge for the sales strip. The server sends this after any
      // event that changes what needs doing, so the strip needs a single listener
      // instead of one per topic.
      es.addEventListener('sales_ticker', (ev) => {
        try { dispatch('sales_ticker', JSON.parse(ev.data)); } catch { dispatch('sales_ticker', {}); }
      });
      es.onopen = () => { backoff = 2000; };
      es.onerror = async () => {
        try { es.close(); } catch {}
        es = null;
        if (cancelled) return;
        // Access token may have expired — try a silent refresh before reconnecting
        try { await _refreshAccessToken(); } catch {}
        scheduleReconnect();
      };
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      try { es?.close(); } catch {}
    };
  }, []);
}

function _ebChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.setValueAtTime(0.15, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.4);
  } catch {}
}

function _ebOsNotify(title, body) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    try { new Notification(title, { body, icon: '/eb-logo.png', tag: 'eb-delivery' }); } catch {}
  } else if (Notification.permission === 'default') {
    // Request quietly — some browsers require a user gesture and will silently deny.
    try { Notification.requestPermission().catch(() => {}); } catch {}
  }
}

const _EB_STATUS_LABEL = { loaded: 'loaded', in_transit: 'departed', delivered: 'delivered', confirmed: 'confirmed', cancelled: 'cancelled' };

// Local calendar date as YYYY-MM-DD (not toISOString, which is UTC and is a day
// behind in India until 05:30).
const localISODate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Global overlay mounted inside ProtectedRoute — opens the SSE connection and
// renders toast slide-ins. The bell (per-portal) is separate.
const NotificationsHost = () => {
  useAppEventStream();
  const navigate = useNavigate();
  const [toasts, setToasts] = React.useState([]);

  const pushToast = React.useCallback((t) => {
    const id = Date.now() + Math.random();
    setToasts((cur) => [...cur, { ...t, id }]);
    setTimeout(() => setToasts((cur) => cur.filter(x => x.id !== id)), 10000);
  }, []);

  React.useEffect(() => {
    if (!EB_EVENTS) return;
    const onConfirmed = (ev) => {
      const d = ev.detail || {};
      const body = `${d.estimate_number || ''} — ${d.customer_name || ''} · ${d.total_qty || 0} pcs${d.is_stock_transfer ? ' (Free Delivery)' : ''}`;
      pushToast({ title: '🚚 New truck booked', body, target: `/delivery/${d.delivery_id}` });
      _ebChime();
      _ebOsNotify('🚚 New truck booked', body);
    };
    const onStatus = (ev) => {
      const d = ev.detail || {};
      if (d.status === 'confirmed') return;  // avoid double-toast with delivery_confirmed
      const body = `${d.estimate_number || ''} — ${d.customer_name || ''}`;
      pushToast({
        title: `🚛 Delivery ${_EB_STATUS_LABEL[d.status] || d.status}`,
        body, target: `/delivery/${d.delivery_id}`,
      });
    };
    const onDocs = (ev) => {
      const d = ev.detail || {};
      const body = `${d.estimate_number || ''} load ${d.call_off_seq || 1} — ${d.customer_name || ''}${d.invoice_number ? ` · ${d.invoice_number}` : ''}`;
      pushToast({ title: '📄 Invoice OK — truck may depart', body, target: `/delivery/${d.delivery_id}` });
      _ebChime();
      _ebOsNotify('📄 Invoice OK', body);
    };
    // Only the loads this payment actually unlocked are worth a toast — a refresh
    // that leaves a load short is not news to anyone.
    const onFunds = (ev) => {
      const d = ev.detail || {};
      if (!d.funded) return;
      const body = `${d.estimate_number || ''} load ${d.call_off_seq || 1} — ${d.customer_name || ''}`;
      pushToast({ title: '💰 Payment cleared — ready to load', body, target: `/delivery/${d.delivery_id}` });
      _ebChime();
      _ebOsNotify('💰 Payment cleared', body);
    };
    EB_EVENTS.addEventListener('delivery_confirmed', onConfirmed);
    EB_EVENTS.addEventListener('delivery_status_changed', onStatus);
    EB_EVENTS.addEventListener('delivery_documents_ready', onDocs);
    EB_EVENTS.addEventListener('delivery_funds_updated', onFunds);
    return () => {
      EB_EVENTS.removeEventListener('delivery_confirmed', onConfirmed);
      EB_EVENTS.removeEventListener('delivery_status_changed', onStatus);
      EB_EVENTS.removeEventListener('delivery_documents_ready', onDocs);
      EB_EVENTS.removeEventListener('delivery_funds_updated', onFunds);
    };
  }, [pushToast]);

  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-3 right-3 z-50 flex flex-col gap-2 pointer-events-none max-w-[calc(100vw-1.5rem)]">
      {toasts.map(t => (
        <div key={t.id}
          onClick={() => { if (t.target) navigate(t.target); setToasts(cur => cur.filter(x => x.id !== t.id)); }}
          className="pointer-events-auto w-72 sm:w-80 bg-white border border-amber-300 rounded-lg shadow-lg px-3 py-2.5 cursor-pointer hover:shadow-xl transition-shadow eb-toast-slide">
          <div className="text-sm font-semibold text-stone-900">{t.title}</div>
          <div className="text-xs text-stone-600 mt-0.5 truncate">{t.body}</div>
        </div>
      ))}
    </div>
  );
};

// Bell with unread-count badge — mounts inline in the DeliveryPortal header.
// Persists the recent event list across dropdown open/close within one mount.
const NotificationsBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = React.useState([]);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!EB_EVENTS) return;
    const push = (kind) => (ev) => {
      const d = ev.detail || {};
      if (kind === 'status' && d.status === 'confirmed') return;
      setUnread(cur => [{ id: Date.now() + Math.random(), kind, ...d }, ...cur].slice(0, 25));
    };
    const onConfirmed = push('confirmed');
    const onStatus = push('status');
    const onDocs = push('docs');
    const onFunds = (ev) => { if ((ev.detail || {}).funded) push('funds')(ev); };
    EB_EVENTS.addEventListener('delivery_confirmed', onConfirmed);
    EB_EVENTS.addEventListener('delivery_status_changed', onStatus);
    EB_EVENTS.addEventListener('delivery_documents_ready', onDocs);
    EB_EVENTS.addEventListener('delivery_funds_updated', onFunds);
    return () => {
      EB_EVENTS.removeEventListener('delivery_confirmed', onConfirmed);
      EB_EVENTS.removeEventListener('delivery_status_changed', onStatus);
      EB_EVENTS.removeEventListener('delivery_documents_ready', onDocs);
      EB_EVENTS.removeEventListener('delivery_funds_updated', onFunds);
    };
  }, []);

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="relative p-1.5 rounded-full hover:bg-stone-100 text-lg leading-none"
        title="Notifications">
        🔔
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-white border border-stone-200 rounded-lg shadow-xl z-40 max-h-96 overflow-y-auto">
          <div className="px-3 py-2 border-b border-stone-100 flex items-center justify-between">
            <div className="text-xs font-bold text-stone-700">Notifications</div>
            {unread.length > 0 && (
              <button onClick={(e) => { e.stopPropagation(); setUnread([]); }} className="text-[11px] text-stone-500 hover:underline">Clear</button>
            )}
          </div>
          {unread.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-stone-400">No new notifications</div>
          ) : (
            <div className="divide-y divide-stone-100">
              {unread.map(n => (
                <button key={n.id}
                  onClick={() => { if (n.delivery_id) navigate(`/delivery/${n.delivery_id}`); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50">
                  <div className="text-xs font-medium text-stone-800">
                    {n.kind === 'confirmed'
                      ? `🚚 New truck booked${n.call_off_seq ? ` (load ${n.call_off_seq})` : ''}`
                      : n.kind === 'docs'
                        ? `📄 Invoice OK — load ${n.call_off_seq || 1}`
                        : n.kind === 'funds'
                          ? `💰 Payment cleared — load ${n.call_off_seq || 1}`
                          : `🚛 → ${_EB_STATUS_LABEL[n.status] || n.status}`}
                  </div>
                  <div className="text-[11px] text-stone-600 truncate">
                    {n.estimate_number} — {n.customer_name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// DD MM YYYY formatter — used everywhere dates are displayed
const fmtDate = (d) => {
  if (!d) return '—';
  // Direct YYYY-MM-DD parse (Zoho format) — avoids timezone shifts
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day} ${m} ${y}`;
  }
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dd} ${mm} ${dt.getFullYear()}`;
};

const EB_LOGO = '/eb-logo.png';

// Password input with show/hide eye toggle — used across login, change password, create user, reset password
const PasswordInput = React.forwardRef(({ className = '', ...props }, ref) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={`pr-9 ${className}`}
      />
      <button
        type="button"
        aria-label={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-stone-400 hover:text-stone-700 focus:outline-none"
      >
        {visible ? (
          // eye-off
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          // eye
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
});

// Customer Portal Landing Page
const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <img src={EB_LOGO} alt="Earth Blocks" className="h-12 sm:h-14 object-contain" />
            <Button asChild variant="outline" size="sm" className="text-stone-600 border-stone-300">
              <Link to="/dashboard">Staff Dashboard</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Simple hero — Customer Portal */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-800 to-stone-800 text-white py-8 sm:py-14">
        {/* Diagonal light-sweep shimmer (see .eb-shimmer-overlay in index.css) */}
        <div
          aria-hidden="true"
          className="eb-shimmer-overlay pointer-events-none absolute inset-y-0 -inset-x-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-xl sm:text-3xl font-bold mb-2">Customer Portal</h2>
          <p className="text-amber-200 text-sm sm:text-lg">Compressed Stabilized Earth Blocks — Quote, Order & Track</p>
        </div>
      </div>

      {/* Main Content — two clear CTAs */}
      <main className="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="grid grid-cols-3 gap-2 sm:gap-6">
          {/* Request a Quote card */}
          <button
            onClick={() => navigate('/quote-request')}
            className="text-left bg-white rounded-xl sm:rounded-2xl border border-stone-200 hover:border-amber-500 hover:shadow-xl transition-all group focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <div className="p-3 sm:p-8 text-center space-y-1.5 sm:space-y-3">
              <div className="bg-amber-800 w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto group-hover:bg-amber-700 transition-colors">
                <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-stone-900">Quote</h3>
              <p className="text-stone-500 text-xs sm:text-sm hidden sm:block">New customer? Get a detailed quote for your earth block requirements.</p>
              <div className="w-full bg-amber-800 group-hover:bg-amber-700 text-white font-semibold py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm">
                Get Quote →
              </div>
            </div>
          </button>

          {/* Reorder card */}
          <button
            onClick={() => navigate('/reorder')}
            className="text-left bg-white rounded-xl sm:rounded-2xl border border-stone-200 hover:border-stone-500 hover:shadow-xl transition-all group focus:outline-none focus:ring-2 focus:ring-stone-500"
          >
            <div className="p-3 sm:p-8 text-center space-y-1.5 sm:space-y-3">
              <div className="bg-stone-700 w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto group-hover:bg-stone-600 transition-colors">
                <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-stone-900">Reorder</h3>
              <p className="text-stone-500 text-xs sm:text-sm hidden sm:block">Existing customer? Quickly reorder using your phone, email, or GSTIN.</p>
              <div className="w-full bg-green-200 group-hover:bg-green-300 text-green-900 font-semibold py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm">
                Reorder →
              </div>
            </div>
          </button>

          {/* Atlas card — admin-only tool; ProtectedRoute enforces access on click */}
          <button
            onClick={() => navigate('/atlas')}
            className="text-left bg-white rounded-xl sm:rounded-2xl border border-stone-200 hover:border-emerald-500 hover:shadow-xl transition-all group focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <div className="p-3 sm:p-8 text-center space-y-1.5 sm:space-y-3">
              <div className="bg-emerald-700 w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto group-hover:bg-emerald-600 transition-colors">
                <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h6v6H4V6zm10 0h6v6h-6V6zM4 16h6v4H4v-4zm10 0h6v4h-6v-4z" />
                </svg>
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-stone-900">Wall Atlas</h3>
              <p className="text-stone-500 text-xs sm:text-sm hidden sm:block">Sign in to open the wall atlas.</p>
              <div className="w-full bg-emerald-700 group-hover:bg-emerald-600 text-white font-semibold py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm">
                Atlas →
              </div>
            </div>
          </button>
        </div>

        {/* Track an existing order */}
        <button
          onClick={() => navigate('/track')}
          className="mt-4 w-full text-left bg-white rounded-xl border border-stone-200 hover:border-amber-400 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <div className="px-5 py-3 flex items-center gap-3">
            <div className="bg-amber-100 w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-amber-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold text-stone-900">Track your order</h3>
              <p className="text-xs text-stone-500">Already ordered? Check your delivery status with your order number.</p>
            </div>
            <span className="text-amber-800 font-semibold text-sm shrink-0 group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </button>
      </main>

      {/* Product categories — hover reveals real catalog photo */}
      <section id="products" className="py-14 bg-white border-t border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-widest text-amber-800 font-semibold">Our range</p>
            <h2 className="text-3xl font-bold text-stone-900 mt-2">Built for every wall</h2>
            <p className="text-stone-500 mt-2 text-sm max-w-xl mx-auto">
              Four block families — hover to see the real product.{' '}
              <a
                href="https://www.earthblocks.in/Brick-Catalogue"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-800 hover:text-amber-900 underline underline-offset-2"
              >
                Full catalogue ↗
              </a>
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              {
                emoji: '🔗',
                title: 'Hollow Interlocking',
                sub: '6″ walls',
                img: 'https://www.earthblocks.in/Interlocking%20brick%20with%20sizes%20%20.png',
              },
              {
                emoji: '🧠',
                title: 'Smart Earth Blocks',
                sub: '9″ & 4.2″ walls',
                img: 'https://www.earthblocks.in/Smart%20Brick%20FB%20Pic%20full100mm%20%20.png',
              },
              {
                emoji: '🧱',
                title: 'Solid Earth Blocks',
                sub: '9″ or 7.5″ walls',
                img: 'https://www.earthblocks.in/9x7.5x4%20solid.png',
              },
              {
                emoji: '📏',
                title: '10×10 & 10×5 Bricks',
                sub: '10″ & 5″ walls',
                img: 'https://www.earthblocks.in/10%20x%2010%20full.png',
              },
              {
                emoji: '🪨',
                title: 'Crushed Soil',
                sub: 'For pointing',
                img: null,
              },
            ].map(({ emoji, title, sub, img }) => (
              <div
                key={title}
                className="group relative bg-white rounded-2xl border border-stone-200 p-5 text-center overflow-hidden transition-all duration-300 hover:border-amber-400 hover:shadow-lg hover:-translate-y-1"
              >
                {/* Visual layer — emoji shows by default, product photo fades in on hover */}
                <div className="relative h-16 mb-2">
                  <div className="absolute inset-0 flex items-center justify-center text-4xl transition-all duration-300 group-hover:opacity-0 group-hover:scale-50">
                    {emoji}
                  </div>
                  {img && (
                    <img
                      src={img}
                      alt={title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain opacity-0 scale-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100"
                    />
                  )}
                </div>
                <h3 className="font-semibold text-stone-800 text-sm relative">{title}</h3>
                <p className="text-xs text-stone-500 mt-0.5 relative">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-400 text-sm">© 2026 EarthBlocks India Pvt. Ltd. All rights reserved.</p>
          <p className="text-xs text-stone-400 mt-1">India's first factory made CSEB Blocks.</p>
          <p className="text-xs text-stone-400">Pioneers in converting soil to CSEB since 2016</p>
          <p className="text-xs text-stone-400">Solutions for a better tomorrow!</p>
          <p className="text-[10px] text-stone-300 mt-3">Last updated: {BUILD_TIME} IST</p>
        </div>
      </footer>
    </div>
  );
};

// Brick weights mapping (in kg) from Earth Blocks catalog
const BRICK_WEIGHTS = {
  // Hollow Interlocking Blocks - 6" Walls
  'hollow interlocking': 7.5,
  'corner': 7.5,
  'u block': 7.0,
  'half running': 3.8,
  'half corner': 3.8,
  
  // Smart Earth Blocks - 9" & 4.2" Walls (all starting with 9)
  '994': 11.0,   // 4" Full
  '993': 8.5,    // 3" Full
  '944': 5.2,    // 4" Half
  '943': 3.8,    // 3" Half
  
  // Solid Earth Blocks
  '10x10': 11.3,     // 10x10 Full
  '10x5': 5.6,       // 10x5 Half
  '9x7.5': 9.0,      // 9x7.5x4 Solid Full
  'solid half': 4.5, // 4.5x7.5x4 Solid Half
  
  // Default
  'default': 8.0
};

// Function to get brick weight based on name
const getBrickWeight = (brickName) => {
  const name = brickName.toLowerCase();
  
  // Check specific patterns
  if (name.includes('994')) return BRICK_WEIGHTS['994'];
  if (name.includes('993')) return BRICK_WEIGHTS['993'];
  if (name.includes('944')) return BRICK_WEIGHTS['944'];
  if (name.includes('943')) return BRICK_WEIGHTS['943'];
  if (name.includes('10x10') || name.includes('10 x 10')) return BRICK_WEIGHTS['10x10'];
  if (name.includes('10x5') || name.includes('10 x 5')) return BRICK_WEIGHTS['10x5'];
  if (name.includes('9x7.5') || name.includes('9 x 7.5')) return BRICK_WEIGHTS['9x7.5'];
  if (name.includes('solid half') || name.includes('solid') && name.includes('half')) return BRICK_WEIGHTS['solid half'];
  if (name.includes('u block') || name.includes('u brick')) return BRICK_WEIGHTS['u block'];
  if (name.includes('corner')) return BRICK_WEIGHTS['corner'];
  if (name.includes('half')) return BRICK_WEIGHTS['half running'];
  if (name.includes('hollow') || name.includes('interlocking')) return BRICK_WEIGHTS['hollow interlocking'];
  
  return BRICK_WEIGHTS['default'];
};

/* WHO LOADS THE TRUCK DECIDES WHO PAYS FOR THE LOADING.

   EB Contracted Transport quotes loading, unloading and the haul as one trip
   rate, so nothing is added on top of it. Outstation and Porter are both the
   customer's own vehicle turning up at our yard: our men fill it, their men
   empty it at the far end. That loading is charged at ₹1 per brick, rounded
   to the nearest ₹10, as one summarized line rather than a few paise smeared
   across every block on the estimate.

   One predicate and one sum, because this figure is printed three times on a
   quote screen before it goes to Zoho as a line item, and copies of the same
   expression is how a preview and an estimate come to disagree. They already
   had: the reorder screen's summary bar charged the raw brick count while the
   line item it submitted rounded to the nearest ₹10. */
const LOADING_BY_US = new Set(['outstation', 'porter']);
const LOADING_LINE_NAME = {
  outstation: 'Loading Charges (Outstation)',
  porter: 'Loading Charges (Porter)',
};

const loadingChargeFor = (transportType, items) => {
  if (!LOADING_BY_US.has(transportType)) return { bricks: 0, amount: 0 };
  const bricks = Math.max(0, Math.round(
    (items || []).reduce((s, i) => s + Number(i.quantity || 0), 0)
  ));
  return { bricks, amount: Math.round(bricks / 10) * 10 };
};

/* The charge as the line item Zoho is sent, or null when there is nothing to
   charge. The name keeps the words "Loading Charges" in it on purpose:
   isLoadingChargeLine matches on them to strip a stale charge off a repeated
   order before recomputing, and it has to recognise a Porter one too. */
const loadingChargeLine = (transportType, items) => {
  const { bricks, amount } = loadingChargeFor(transportType, items);
  if (amount <= 0) return null;
  return {
    item_id: '',
    name: LOADING_LINE_NAME[transportType] || 'Loading Charges',
    quantity: 1,
    rate: amount,
    unit: 'Nos',
    description: `Loading @ ₹1 per brick × ${bricks.toLocaleString('en-IN')} bricks (rounded to nearest ₹10)`,
  };
};

/* THE 10x10 IS ON HOLD.

   One predicate, because four places test for this block by name and a hold
   three of them know about is a hold that still sells. The catalogue calls it
   "10x10 Interlocking Blocks CSEB 250x250x90" and its half "10x5 ..." - and
   the word interlock in that name is why the test has to run ahead of the 6"
   hollow test everywhere it appears. A 250 block routed into the 6" section
   would be worse than one that is plainly not for sale.

   The reorder screen keeps the items in its catalogue on purpose: they are
   what a past 10x10 order resolves against for its rate and its weight, and
   pulling them out would make repeating that order silently drop a line. So
   the hold is drawn where a customer would go looking for the range, and
   enforced where an order is built and sent. */
const isTenByTen = (name) => {
  const n = (name || '').toLowerCase();
  return (n.includes('10x5') || n.includes('10 x 5') || n.includes('10x10') || n.includes('10 x 10'))
    && !n.includes('solid');
};
const ON_HOLD_NOTE = 'On hold - not taking new orders';

/* A range the customer cannot buy today reads as a flat row and not an
   accordion. Either there is nothing behind the chevron - announced, with its
   items still inactive in Books - or there must not be: a range on hold keeps
   its items in the catalogue, and an accordion would sell them. Both say why,
   in the same shape, so the two reasons are not two designs. */
const NotSellingRow = ({ category }) => (
  <div className="border border-amber-200 bg-amber-50/50 rounded-lg px-3 py-2.5 flex items-center gap-2">
    <span className="text-lg">{category.icon}</span>
    <div className="text-left">
      <h4 className="font-semibold text-sm text-stone-800">{category.title}</h4>
      <p className="text-[11px] text-amber-700 font-medium">{category.note}</p>
    </div>
  </div>
);

// Main Dashboard Component
// Gear-icon dropdown that opens Activity / Settings tabs (admin/manager only).
// Keeps the top tab row lean while the less-frequent views stay one click away.
const AdminMenu = ({ onOpenTab }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);
  const pick = (v) => (e) => { e.stopPropagation(); setOpen(false); onOpenTab(v); };
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-stone-300 bg-white text-stone-600 hover:text-stone-900 hover:bg-stone-50"
        title="Admin"
        aria-label="Admin menu"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 w-40 rounded-md border border-stone-200 bg-white shadow-lg py-1 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" onClick={pick('activity')}
            className="w-full text-left px-3 py-1.5 hover:bg-stone-50 text-stone-700">📜 Activity</button>
          <button type="button" onClick={pick('settings')}
            className="w-full text-left px-3 py-1.5 hover:bg-stone-50 text-stone-700">⚙️ Settings</button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [inventoryItems, setInventoryItems] = useState(null); // null = still loading
  const [openOrderCount, setOpenOrderCount] = useState(null);
  const [pendingDelCount, setPendingDelCount] = useState(null);
  const [awaitingDocsCount, setAwaitingDocsCount] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');

  // Auth check is the ONLY thing that blocks the shell. Data loads in the background.
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(r => setCurrentUser(r.data))
      .catch(() => navigate('/login'))
      .finally(() => setAuthChecked(true));
    loadDashboardData();
  }, []);

  // Keep the tiles honest while the page is open: a load changing state, or its
  // documents arriving, refreshes the counts (SSE → EB_EVENTS bridge).
  useEffect(() => {
    if (!EB_EVENTS) return;
    const types = ['delivery_confirmed', 'delivery_status_changed', 'delivery_documents_ready',
                   'delivery_funds_updated'];
    const h = () => loadDashboardData();
    types.forEach(t => EB_EVENTS.addEventListener(t, h));
    return () => types.forEach(t => EB_EVENTS.removeEventListener(t, h));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('eb_token');
    localStorage.removeItem('eb_refresh');
    invalidateCachedMe();
    navigate('/login');
  };

  const isAdmin = currentUser?.role === 'admin';
  const isManagerOrAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  // Matches the /atlas ProtectedRoute, which counts extra_roles as well as the
  // primary role — so the link shows exactly when the route would let you in.
  const canUseAtlas = !!currentUser && [currentUser.role, ...(currentUser.extra_roles || [])].includes('admin');

  // Each request loads independently so a slow Zoho call doesn't block the shell.
  const loadDashboardData = () => {
    axios.get(`${API}/inventory/items`)
      .then(r => setInventoryItems(r.data))
      .catch(() => {});
    axios.get(`${API}/orders?open=true`)
      .then(r => setOpenOrderCount((r.data.orders || []).length))
      .catch(() => setOpenOrderCount(0));
    axios.get(`${API}/deliveries`)
      .then(r => setPendingDelCount((r.data.deliveries || []).filter(d => d.status !== 'delivered' && d.status !== 'cancelled').length))
      .catch(() => setPendingDelCount(0));
    axios.get(`${API}/deliveries/awaiting-documents?count_only=true`)
      .then(r => setAwaitingDocsCount(Number(r.data.count) || 0))
      .catch(() => setAwaitingDocsCount(0));
  };

  const syncFromZoho = async () => {
    try {
      const response = await axios.post(`${API}/zoho/sync-inventory`);
      alert(`Synced ${response.data.synced_count} items from Zoho Books!`);
      loadDashboardData();
    } catch (error) {
      if (error.response?.status === 401) {
        alert('Please authorize Zoho Books connection first');
      } else {
        alert('Error syncing from Zoho Books');
      }
    }
  };

  // Only block for the auth check — data loads in background so the shell renders fast
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto mb-3"></div>
          <p className="text-xs text-stone-500">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop header */}
          <div className="hidden sm:flex justify-between items-center py-3">
            <div className="flex items-center gap-3">
              <img src={EB_LOGO} alt="Earth Blocks" className="h-12 object-contain" />
              <div>
                <p className="text-xs text-stone-400 leading-tight">Staff Dashboard · CSEB Manufacturing & Delivery</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <PortalNav admin={canUseAtlas} />
              {currentUser && (
                <>
                  <span className="text-xs text-stone-500 ml-2 hidden md:inline">
                    {currentUser.full_name || currentUser.email}
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-stone-200 text-stone-700 text-[10px] font-medium">{currentUser.role}</span>
                  </span>
                  {isManagerOrAdmin && (
                    <AdminMenu onOpenTab={(v) => setActiveTab(v)} />
                  )}
                  <Button variant="outline" size="sm" onClick={handleLogout}>Logout</Button>
                </>
              )}
            </div>
          </div>
          {/* Mobile header */}
          <div className="sm:hidden py-3 space-y-2">
            <div className="flex justify-center">
              <img src={EB_LOGO} alt="Earth Blocks" className="h-10 object-contain" />
            </div>
            <PortalNav full admin={canUseAtlas} />
            <div className="flex items-center justify-end gap-1">
              {isManagerOrAdmin && (
                <AdminMenu onOpenTab={(v) => setActiveTab(v)} />
              )}
              <Button variant="outline" size="sm" onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats - tap a card to jump to its screen */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-6">
          {[
            { label: 'Open Orders', value: openOrderCount, sub: 'quantity still to supply', color: 'bg-amber-800', light: 'text-amber-200', tab: 'salesorders' },
            { label: 'Pending Loads', value: pendingDelCount, sub: 'not yet delivered', color: 'bg-stone-700', light: 'text-stone-300', tab: 'loading' },
            { label: 'Waiting for Invoice', value: awaitingDocsCount, sub: 'loaded, waiting on Accounts', color: awaitingDocsCount ? 'bg-orange-700' : 'bg-stone-600', light: 'text-orange-100', tab: 'loading' },
            { label: 'Active Products', value: inventoryItems === null ? null : inventoryItems.length, sub: 'in inventory', color: 'bg-amber-700', light: 'text-amber-200', tab: 'inventory' },
            { label: 'Low Stock', value: inventoryItems === null ? null : inventoryItems.filter(i => i.stock_quantity < i.reorder_level).length, sub: 'on hand below reorder level', color: 'bg-red-700', light: 'text-red-200', tab: 'inventory' },
          ].map(({ label, value, sub, color, light, tab }) => (
            <Card
              key={label}
              role="button"
              tabIndex={0}
              aria-label={`${label}: ${value === null ? 'loading' : value}. Open ${tab} screen`}
              onClick={() => setActiveTab(tab)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(tab); } }}
              className={`${color} text-white border-0 cursor-pointer select-none transition hover:brightness-110 active:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70`}
            >
              <CardContent className="p-2.5 sm:p-5">
                <p className={`${light} text-[11px] sm:text-xs font-medium leading-tight`}>{label}</p>
                <p className="text-xl sm:text-3xl font-bold mt-0.5 sm:mt-1">{value === null ? '…' : value}</p>
                <p className={`${light} hidden sm:block text-xs mt-1`}>{sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 h-auto">
            <TabsTrigger value="analytics" className="h-auto py-1.5 whitespace-normal leading-tight text-center border border-transparent data-[state=active]:border-stone-300">Analytics</TabsTrigger>
            <TabsTrigger value="loading" className="h-auto py-1.5 whitespace-normal leading-tight text-center border border-transparent data-[state=active]:border-stone-300">Loading</TabsTrigger>
            <TabsTrigger value="estimates" className="h-auto py-1.5 whitespace-normal leading-tight text-center border border-transparent data-[state=active]:border-stone-300">Quotes</TabsTrigger>
            <TabsTrigger value="salesorders" className="h-auto py-1.5 whitespace-normal leading-tight text-center border border-transparent data-[state=active]:border-stone-300">Orders</TabsTrigger>
            <TabsTrigger value="inventory" className="h-auto py-1.5 whitespace-normal leading-tight text-center border border-transparent data-[state=active]:border-stone-300">Inventory</TabsTrigger>
          </TabsList>

          <TabsContent value="estimates" className="space-y-4">
            <EstimatesSection onDeliveryCreated={loadDashboardData} />
          </TabsContent>

          <TabsContent value="salesorders" className="space-y-4">
            <SalesOrdersSection />
          </TabsContent>

          <TabsContent value="inventory" className="space-y-4" data-testid="inventory-panel">
            <InventorySection items={inventoryItems || []} onUpdate={loadDashboardData} currentUser={currentUser} />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4" data-testid="analytics-panel">
            <AnalyticsSection />
          </TabsContent>

          <TabsContent value="loading" className="space-y-4" data-testid="loading-panel">
            <LoadingQueueSection />
          </TabsContent>

          {isManagerOrAdmin && (
            <TabsContent value="activity" className="space-y-4">
              <ActivitySection />
            </TabsContent>
          )}

          {isManagerOrAdmin && (
            <TabsContent value="settings" className="space-y-4">
              <SettingsSection onSyncZoho={syncFromZoho} />
              <UsersSection currentUser={currentUser} isAdmin={isAdmin} />
            </TabsContent>
          )}
        </Tabs>
      </main>

      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
};

// ─── CHANGE MY PASSWORD MODAL ──────────────────────────────────────────────

const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setErr(''); setBusy(false); };
  const handleClose = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next.length < 6) { setErr('New password must be at least 6 characters'); return; }
    if (next !== confirm) { setErr('New passwords do not match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-my-password`, { current_password: current, new_password: next });
      alert('Password changed successfully.');
      handleClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || 'Failed to change password');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change my password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Current password</Label>
            <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          </div>
          <div>
            <Label className="text-xs">New password</Label>
            <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          </div>
          <div>
            <Label className="text-xs">Confirm new password</Label>
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
          {err && <p className="text-xs text-red-600">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-amber-800 hover:bg-amber-900 text-white">
              {busy ? 'Changing…' : 'Change password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── USERS MANAGEMENT SECTION (admin/manager) ──────────────────────────────

const ROLE_COLORS = {
  admin:     'bg-red-100 text-red-700 border-red-200',
  manager:   'bg-purple-100 text-purple-700 border-purple-200',
  staff:     'bg-blue-100 text-blue-700 border-blue-200',
  transport: 'bg-amber-100 text-amber-800 border-amber-200',
  factory:   'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// Share a sign-in invite for a user. Uses the native share sheet on mobile
// (WhatsApp, SMS, email, etc.) and falls back to a small menu on desktop.
// Reusable square action button — used across the Users table (Share / Edit /
// Reset / Disable / Delete) and elsewhere for row-level actions. 32×32 with a
// tinted background matching the action's semantics so intent is legible at a glance.
const IconActionButton = React.forwardRef(({ tone = 'stone', title, onClick, disabled, children, ...rest }, ref) => {
  const tones = {
    blue:  'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300',
    stone: 'border-stone-200 bg-stone-50 text-stone-700 hover:bg-stone-100 hover:border-stone-400',
    amber: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-300',
    red:   'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300',
    green: 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300',
  };
  return (
    <button
      ref={ref}
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded-md border transition-colors text-sm leading-none shrink-0 disabled:opacity-50 disabled:pointer-events-none ${tones[tone]}`}
      {...rest}
    >{children}</button>
  );
});

const ShareInviteButton = ({ user }) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const inviteText =
    `EarthBlocks sign-in\n\n` +
    `Login: https://app.earthblocks.in/login\n` +
    `Email: ${user.email}` +
    (user.mobile ? `\nMobile: ${user.mobile}` : '') +
    (user.factory_warehouse ? `\nFactory: ${user.factory_warehouse}` : '') +
    `\nRole: ${user.role}\n\n` +
    `If you don't have a password yet, use the "Forgot password?" link on the sign-in page — a 6-digit code will be sent to your phone or email.`;

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: 'EarthBlocks sign-in',
        text: inviteText,
      });
    } catch (e) {
      // User cancelled or share failed — silently ignore
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (navigator.share) {
      nativeShare();
    } else {
      setOpenMenu(v => !v);
    }
  };

  const closeMenu = () => setOpenMenu(false);

  useEffect(() => {
    if (!openMenu) return;
    const onDocClick = () => setOpenMenu(false);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openMenu]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteText);
    } catch {
      const t = document.createElement('textarea');
      t.value = inviteText; document.body.appendChild(t); t.select();
      document.execCommand('copy'); document.body.removeChild(t);
    }
    setCopied(true); setTimeout(() => setCopied(false), 1200);
    closeMenu();
  };

  const waHref = (() => {
    const enc = encodeURIComponent(inviteText);
    if (user.mobile) {
      const digits = String(user.mobile).replace(/\D/g, '');
      const withCc = digits.length === 10 ? `91${digits}` : digits;
      return `https://wa.me/${withCc}?text=${enc}`;
    }
    return `https://wa.me/?text=${enc}`;
  })();

  const subject = 'Your EarthBlocks sign-in';
  const smsHref = user.mobile
    ? `sms:${String(user.mobile).replace(/\D/g,'').replace(/^(?!91)/, '91')}?body=${encodeURIComponent(inviteText)}`
    : `sms:?body=${encodeURIComponent(inviteText)}`;
  const emailHref = `mailto:${user.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(inviteText)}`;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={handleClick}
        className="w-8 h-8 flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors text-sm leading-none shrink-0"
        title="Share sign-in invite"
      >
        📤
      </button>
      {openMenu && (
        <div
          className="absolute right-0 top-full mt-1 z-20 w-40 rounded-md border border-stone-200 bg-white shadow-lg py-1 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <a href={waHref} target="_blank" rel="noreferrer"
             onClick={closeMenu}
             className="block px-3 py-1.5 hover:bg-stone-50 text-stone-700">💬 WhatsApp</a>
          <a href={smsHref} onClick={closeMenu}
             className="block px-3 py-1.5 hover:bg-stone-50 text-stone-700">📱 SMS</a>
          <a href={emailHref} onClick={closeMenu}
             className="block px-3 py-1.5 hover:bg-stone-50 text-stone-700">✉️ Email</a>
          <button type="button" onClick={copyToClipboard}
             className="w-full text-left px-3 py-1.5 hover:bg-stone-50 text-stone-700">
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      )}
    </div>
  );
};

const UsersSection = ({ currentUser, isAdmin }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(['admin', 'manager', 'staff', 'transport', 'factory']);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user object or null
  const [editTarget, setEditTarget] = useState(null);   // user object or null
  const [err, setErr] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/users/roles`).catch(() => ({ data: { roles } })),
      ]);
      setUsers(uRes.data || []);
      if (rRes.data?.roles) setRoles(rRes.data.roles);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const changeRole = async (user, newRole) => {
    if (!isAdmin) { alert('Only admins can change roles'); return; }
    if (newRole === user.role) return;
    if (user.id === currentUser.id) { alert('You cannot change your own role'); return; }
    try {
      await axios.put(`${API}/users/${user.id}/role`, { role: newRole });
      loadUsers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to change role');
    }
  };

  const toggleDisable = async (user) => {
    if (!isAdmin) { alert('Only admins can disable users'); return; }
    if (user.id === currentUser.id) { alert('You cannot disable your own account'); return; }
    if (!window.confirm(`${user.is_disabled ? 'Enable' : 'Disable'} user ${user.email}?`)) return;
    try {
      await axios.put(`${API}/users/${user.id}/disable`);
      loadUsers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const deleteUser = async (user) => {
    if (!isAdmin) { alert('Only admins can delete users'); return; }
    if (user.id === currentUser.id) { alert('You cannot delete your own account'); return; }
    // Two-step confirmation for a destructive, non-reversible action.
    const first = window.confirm(
      `Permanently delete user "${user.full_name || user.email}"?\n\nThis wipes their sign-in, activity log, Zoho connection and OTP tokens. Deliveries + WhatsApp history they touched are kept but unlinked from them. Cannot be undone.`
    );
    if (!first) return;
    const typed = window.prompt(`Type the user's email to confirm deletion:\n\n${user.email}`);
    if (typed?.trim().toLowerCase() !== (user.email || '').toLowerCase()) {
      alert('Email did not match — delete cancelled.');
      return;
    }
    try {
      await axios.delete(`${API}/users/${user.id}`);
      loadUsers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div className="min-w-0 flex-1 pr-2">
          <CardTitle className="text-lg">User Management</CardTitle>
          <CardDescription className="text-xs">
            {users.length} user{users.length !== 1 ? 's' : ''} · {isAdmin ? 'You are an admin' : 'Read-only view (manager)'}
          </CardDescription>
          {/* Expandable role legend — collapsed by default so it doesn't clutter,
              but discoverable when someone wonders what a role actually grants. */}
          <details className="mt-1.5">
            <summary className="text-[11px] text-amber-800 cursor-pointer select-none hover:underline list-none">
              <span className="inline-block transition-transform duration-150" style={{fontSize:'9px'}}>▶</span> What does each role get access to?
            </summary>
            <div className="mt-2 space-y-1 text-[11px] text-stone-600 border-l-2 border-stone-200 pl-3 max-w-2xl">
              <div className="flex items-baseline gap-2">
                <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${ROLE_COLORS.admin}`}>admin</span>
                <span>Everything — Dashboard, Factory Portal, Delivery Portal, user management, all destructive actions (delete users, edit deliveries, reverse adjustments).</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${ROLE_COLORS.manager}`}>manager</span>
                <span>Dashboard tabs (Analytics / Quotes / Orders / Inventory / Loading), user management (read-only), Activity log. Cannot delete users or edit deliveries.</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${ROLE_COLORS.staff}`}>staff</span>
                <span>Dashboard tabs only — can approve quotes, view inventory. No admin actions, no factory or delivery portal.</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${ROLE_COLORS.factory}`}>factory</span>
                <span>Factory Portal only — shift production logging, POD photo review. Locked to their assigned factory warehouse.</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${ROLE_COLORS.transport}`}>transport</span>
                <span>Delivery Portal only — delivery queue, dispatch (Loading Completed / Mark Delivered), POD photo capture, WhatsApp hand-off to Accounts.</span>
              </div>
              <p className="text-[10px] text-stone-500 italic pt-1.5 border-t border-stone-100 mt-1.5">
                💡 A user can hold multiple roles — click ✎ Edit to grant a factory user extra Delivery access, for example. Sign-in lands on their highest-priority role's portal; a switcher links the others.
              </p>
            </div>
          </details>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="bg-amber-800 hover:bg-amber-900 text-white">
            + Add User
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
        {loading ? (
          <div className="text-center py-8 text-stone-400 text-sm">Loading users…</div>
        ) : (
          <>
          {/* Mobile card layout */}
          <div className="sm:hidden space-y-3">
            {users.map(u => (
              <div key={u.id} className="border border-stone-200 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-stone-800 truncate">
                      {u.full_name || '—'}
                      {u.id === currentUser.id && <span className="ml-1 text-[10px] text-stone-400">(you)</span>}
                    </p>
                    <p className="text-xs text-stone-500 truncate">{u.email}</p>
                    {u.mobile && <p className="text-xs text-stone-500 font-mono">{u.mobile}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${u.is_disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {u.is_disabled ? 'Disabled' : 'Active'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    {isAdmin && u.id !== currentUser.id ? (
                      <select value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                        className={`text-xs border rounded px-2 py-1 ${ROLE_COLORS[u.role] || 'bg-stone-100 text-stone-700'}`}>
                        {roles.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : (
                      <span className={`inline-block text-xs px-2 py-0.5 rounded border ${ROLE_COLORS[u.role] || 'bg-stone-100 text-stone-700'}`}>{u.role}</span>
                    )}
                    {(u.extra_roles || []).map(r => (
                      <span key={r} className="text-[10px] px-1.5 py-0.5 ml-1 rounded bg-stone-100 text-stone-600 border border-stone-200">+ {r}</span>
                    ))}
                    {u.factory_warehouse && <span className="text-[10px] text-stone-500 ml-2">🏭 {warehouseDisplayName(u.factory_warehouse)}</span>}
                  </div>
                  <div className="flex items-center justify-evenly gap-2 flex-1 max-w-[16rem]">
                    <ShareInviteButton user={u} />
                    {isAdmin && (
                      <IconActionButton tone="stone" onClick={() => setEditTarget(u)}
                        title="Edit name, mobile, roles + factory">✎</IconActionButton>
                    )}
                    {isAdmin && u.id !== currentUser.id && (
                      <>
                        <IconActionButton tone="amber" onClick={() => setResetTarget(u)}
                          title="Reset password + share">🔑</IconActionButton>
                        <IconActionButton tone={u.is_disabled ? 'green' : 'red'} onClick={() => toggleDisable(u)}
                          title={u.is_disabled ? 'Enable user' : 'Disable user'}>
                          {u.is_disabled ? '✓' : '⊘'}
                        </IconActionButton>
                        <IconActionButton tone="red" onClick={() => deleteUser(u)}
                          title="Delete permanently">🗑</IconActionButton>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <div className="grid grid-cols-[1fr_1.4fr_9rem_8rem_7rem_15rem] text-xs font-semibold text-stone-500 uppercase tracking-wide py-2 border-b border-stone-200 min-w-[880px]">
              <div>Name</div>
              <div>Email</div>
              <div>Mobile</div>
              <div>Role / Factory</div>
              <div>Status</div>
              <div className="text-center">Actions</div>
            </div>
            {users.map(u => (
              <div key={u.id} className="grid grid-cols-[1fr_1.4fr_9rem_8rem_7rem_15rem] items-center py-3 border-b border-stone-100 text-sm min-w-[880px]">
                <div className="text-stone-800 truncate pr-2">
                  {u.full_name || '—'}
                  {u.id === currentUser.id && <span className="ml-2 text-[10px] text-stone-400">(you)</span>}
                </div>
                <div className="text-stone-600 truncate pr-2">{u.email}</div>
                <div className="text-stone-600 font-mono text-xs">{u.mobile || '—'}</div>
                <div>
                  {isAdmin && u.id !== currentUser.id ? (
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 ${ROLE_COLORS[u.role] || 'bg-stone-100 text-stone-700'}`}
                    >
                      {roles.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className={`inline-block text-xs px-2 py-0.5 rounded border ${ROLE_COLORS[u.role] || 'bg-stone-100 text-stone-700'}`}>{u.role}</span>
                  )}
                  {(u.extra_roles || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.extra_roles.map(r => (
                        <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 border border-stone-200">+ {r}</span>
                      ))}
                    </div>
                  )}
                  {u.factory_warehouse && (
                    <div className="text-[10px] text-stone-500 mt-0.5 truncate">🏭 {warehouseDisplayName(u.factory_warehouse)}</div>
                  )}
                </div>
                <div>
                  <span className={`inline-block text-xs px-2 py-0.5 rounded ${u.is_disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {u.is_disabled ? 'Disabled' : 'Active'}
                  </span>
                </div>
                <div className="flex items-center justify-evenly">
                  <ShareInviteButton user={u} />
                  {isAdmin && (
                    <IconActionButton tone="stone" onClick={() => setEditTarget(u)}
                      title="Edit name, mobile, roles + factory">✎</IconActionButton>
                  )}
                  {isAdmin && u.id !== currentUser.id && (
                    <>
                      <IconActionButton tone="amber" onClick={() => setResetTarget(u)}
                        title="Reset password + share">🔑</IconActionButton>
                      <IconActionButton tone={u.is_disabled ? 'green' : 'red'} onClick={() => toggleDisable(u)}
                        title={u.is_disabled ? 'Enable user' : 'Disable user'}>
                        {u.is_disabled ? '✓' : '⊘'}
                      </IconActionButton>
                      <IconActionButton tone="red" onClick={() => deleteUser(u)}
                        title="Delete permanently">🗑</IconActionButton>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </CardContent>

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} roles={roles} onCreated={loadUsers} isAdmin={isAdmin} />
      <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onDone={loadUsers} />
      <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} onSaved={loadUsers} />
    </Card>
  );
};

// Roles in priority order (highest first) — the top selected one becomes the primary role.
const ALL_ROLES = ['admin', 'manager', 'staff', 'factory', 'transport'];
const ROLE_HELP = {
  admin: 'Full access', manager: 'Management', staff: 'Dashboard/sales',
  factory: 'Factory/inventory portal', transport: 'Delivery portal',
};

// Admin-only edit of a user's name / mobile / factory warehouse / roles.
const EditUserModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({ full_name: '', mobile: '', factory_warehouse: '', roles: [] });
  const [warehouses, setWarehouses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!user) return;
    setForm({
      full_name: user.full_name || '',
      mobile: user.mobile || '',
      factory_warehouse: user.factory_warehouse || '',
      roles: [user.role, ...(user.extra_roles || [])].filter(Boolean),
    });
    setErr(''); setBusy(false);
    axios.get(`${API}/production/warehouses`)
      .then(r => setWarehouses(r.data?.warehouses || []))
      .catch(() => setWarehouses([]));
  }, [user]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleRole = (r) => setForm(f => ({
    ...f, roles: f.roles.includes(r) ? f.roles.filter(x => x !== r) : [...f.roles, r],
  }));

  const save = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.roles.length === 0) { setErr('Select at least one role'); return; }
    if (form.roles.includes('factory') && !form.factory_warehouse) {
      setErr('Factory access requires a factory location'); return;
    }
    // Primary = highest-priority selected role; the rest become extra (additional) roles.
    const primary = ALL_ROLES.find(r => form.roles.includes(r)) || form.roles[0];
    const extra_roles = form.roles.filter(r => r !== primary);
    setBusy(true);
    try {
      await axios.put(`${API}/users/${user.id}`, {
        full_name: form.full_name,
        mobile: form.mobile,
        factory_warehouse: form.factory_warehouse || null,
        role: primary,
        extra_roles,
      });
      await onSaved();
      onClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription className="text-xs">
            {user?.email} · {user?.role}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Full name</Label>
            <Input value={form.full_name} onChange={set('full_name')} placeholder="Ravi Kumar" />
          </div>
          <div>
            <Label className="text-xs">Mobile (WhatsApp) <span className="text-stone-400">— for login + password reset</span></Label>
            <Input value={form.mobile} onChange={set('mobile')} placeholder="9876543210" inputMode="numeric" />
          </div>
          <div>
            <Label className="text-xs">Roles / access <span className="text-stone-400">— tick all portals this user can use</span></Label>
            <div className="grid grid-cols-1 gap-1 mt-1">
              {ALL_ROLES.map(r => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer rounded px-1.5 py-1 hover:bg-stone-50">
                  <input type="checkbox" checked={form.roles.includes(r)} onChange={() => toggleRole(r)}
                    className="h-4 w-4 rounded border-stone-300 text-amber-700 focus:ring-amber-500" />
                  <span className="capitalize font-medium text-stone-800">{r}</span>
                  <span className="text-[11px] text-stone-400">{ROLE_HELP[r]}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-stone-400 mt-1">Sign-in lands on the highest role's portal; a switcher links the others.</p>
          </div>
          {form.roles.includes('factory') && (
            <div>
              <Label className="text-xs">Factory location <span className="text-red-600">*</span></Label>
              <select
                value={form.factory_warehouse}
                onChange={set('factory_warehouse')}
                className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Pick a factory…</option>
                {warehouses.map(w => (
                  <option key={w.warehouse_id} value={w.warehouse_name}>{warehouseDisplayName(w.warehouse_name)}</option>
                ))}
              </select>
              <p className="text-xs text-stone-400 mt-1">
                Factory users are locked to this location.
              </p>
            </div>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-amber-800 hover:bg-amber-900 text-white">
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Random 6-digit numeric password — simple to read out over WhatsApp
const genPassword = () => {
  const arr = new Uint32Array(6);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  let out = '';
  for (let i = 0; i < 6; i++) out += String(arr[i] % 10);
  return out;
};

// Credentials confirmation view — shown after create/reset succeeds.
// Admin can view the plaintext password, copy or one-click WhatsApp share.
const CredentialsReady = ({ email, mobile, password, role, factoryWarehouse, onDone, mode = 'created' }) => {
  const [copied, setCopied] = useState(false);
  const factoryLine = factoryWarehouse ? `\nFactory: ${factoryWarehouse}` : '';
  const shareText =
    mode === 'reset'
      ? `EarthBlocks · password reset\n\nLogin: https://app.earthblocks.in/login\nEmail: ${email}\nNew password: ${password}\n\nTo change it later, use the Forgot password? link on the sign-in page.`
      : `EarthBlocks account created\n\nLogin: https://app.earthblocks.in/login\nEmail: ${email}\nPassword: ${password}\nRole: ${role}${factoryLine}\n\nYou can also log in with your mobile number. To change your password later, use the Forgot password? link on the sign-in page.`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      const t = document.createElement('textarea');
      t.value = shareText; document.body.appendChild(t); t.select();
      document.execCommand('copy'); document.body.removeChild(t);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    }
  };

  // wa.me link — mobile-first send. If we know the recipient's mobile, prefill it
  // (strip non-digits, ensure India country code); otherwise open the picker.
  const waHref = (() => {
    const encoded = encodeURIComponent(shareText);
    if (mobile) {
      const digits = String(mobile).replace(/\D/g, '');
      const withCc = digits.length === 10 ? `91${digits}` : digits;
      return `https://wa.me/${withCc}?text=${encoded}`;
    }
    return `https://wa.me/?text=${encoded}`;
  })();

  return (
    <div className="space-y-3 py-2">
      <div className="rounded-md border border-green-200 bg-green-50 p-3">
        <p className="text-sm font-medium text-green-800">
          {mode === 'reset' ? '✓ Password reset' : '✓ User created'}
        </p>
        <p className="text-xs text-green-700 mt-1">
          Share these credentials with the user securely. This is the only time the password will be shown.
        </p>
      </div>

      <div className="space-y-2 rounded-md border border-stone-200 bg-stone-50 p-3 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-stone-500">Email</span>
          <span className="font-mono text-stone-800 truncate">{email}</span>
        </div>
        {mobile && (
          <div className="flex justify-between gap-3">
            <span className="text-stone-500">Mobile</span>
            <span className="font-mono text-stone-800">{mobile}</span>
          </div>
        )}
        {role && (
          <div className="flex justify-between gap-3">
            <span className="text-stone-500">Role</span>
            <span className="text-stone-800">{role}</span>
          </div>
        )}
        {factoryWarehouse && (
          <div className="flex justify-between gap-3">
            <span className="text-stone-500">Factory</span>
            <span className="text-stone-800">{factoryWarehouse}</span>
          </div>
        )}
        <div className="flex justify-between gap-3">
          <span className="text-stone-500">Password</span>
          <span className="font-mono text-stone-800 select-all">{password}</span>
        </div>
      </div>

      <DialogFooter className="gap-2 flex-wrap">
        <Button type="button" variant="outline" onClick={copy}>
          {copied ? '✓ Copied' : '📋 Copy'}
        </Button>
        <a href={waHref} target="_blank" rel="noreferrer"
           className="inline-flex items-center justify-center rounded-md border border-green-300 bg-green-50 hover:bg-green-100 text-green-800 h-9 px-3 text-sm font-medium">
          💬 Send on WhatsApp
        </a>
        <Button type="button" onClick={onDone} className="bg-amber-800 hover:bg-amber-900 text-white">
          Done
        </Button>
      </DialogFooter>
    </div>
  );
};

const CreateUserModal = ({ open, onClose, roles, onCreated, isAdmin }) => {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('staff');
  const [factoryWarehouse, setFactoryWarehouse] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null); // { email, mobile, password, role } after success

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/production/warehouses`)
      .then(r => setWarehouses(r.data?.warehouses || []))
      .catch(() => setWarehouses([]));
  }, [open]);

  const reset = () => {
    setEmail(''); setMobile(''); setName(''); setPassword(''); setRole('staff');
    setFactoryWarehouse(''); setErr(''); setBusy(false); setCreated(null);
  };
  const close = () => { reset(); onClose(); };
  const doneAfterCreate = () => { onCreated(); close(); };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email || !name || !password) { setErr('Name, email, and password are required'); return; }
    if (password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (role === 'factory' && !factoryWarehouse) {
      setErr('Pick the factory this user works at'); return;
    }
    setBusy(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanMobile = mobile.trim();
      await axios.post(`${API}/auth/register`, {
        email: cleanEmail,
        password,
        full_name: name.trim(),
        role,
        mobile: cleanMobile || null,
        factory_warehouse: role === 'factory' ? factoryWarehouse : null,
      });
      setCreated({ email: cleanEmail, mobile: cleanMobile, password, role, factoryWarehouse });
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to create user');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (created ? doneAfterCreate() : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{created ? 'Credentials ready' : 'Add User'}</DialogTitle>
        </DialogHeader>

        {created ? (
          <CredentialsReady
            email={created.email}
            mobile={created.mobile}
            password={created.password}
            role={created.role}
            factoryWarehouse={created.factoryWarehouse}
            onDone={doneAfterCreate}
            mode="created"
          />
        ) : (
          <form onSubmit={submit} className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ravi Kumar" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ravi@earthblocks.in" />
            </div>
            <div>
              <Label className="text-xs">Mobile (WhatsApp) <span className="text-stone-400">— for login + password reset</span></Label>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" inputMode="numeric" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Initial password</Label>
                <button
                  type="button"
                  onClick={() => setPassword(genPassword())}
                  className="text-xs text-amber-700 hover:text-amber-900 underline"
                >
                  Generate
                </button>
              </div>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6 digits (or click Generate)" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm">
                {roles.map(r => (
                  <option key={r} value={r} disabled={r === 'admin' && !isAdmin}>
                    {r}{r === 'admin' && !isAdmin ? ' (admin only)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-stone-400 mt-1">
                admin: full access · manager: read-only user mgmt · staff: dashboard · transport: /delivery · factory: /factory
              </p>
            </div>
            {role === 'factory' && (
              <div>
                <Label className="text-xs">Factory location <span className="text-red-600">*</span></Label>
                <select
                  value={factoryWarehouse}
                  onChange={(e) => setFactoryWarehouse(e.target.value)}
                  className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Pick a factory…</option>
                  {warehouses.map(w => (
                    <option key={w.warehouse_id} value={w.warehouse_name}>{warehouseDisplayName(w.warehouse_name)}</option>
                  ))}
                </select>
                <p className="text-xs text-stone-400 mt-1">
                  Factory users are locked to this location — they can't log production for any other factory.
                </p>
              </div>
            )}
            {err && <p className="text-xs text-red-600">{err}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-amber-800 hover:bg-amber-900 text-white">
                {busy ? 'Creating…' : 'Create user'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordModal = ({ user, onClose, onDone }) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null); // { email, password } after success

  const closeAll = () => {
    setPassword(''); setBusy(false); setErr(''); setDone(null);
    onClose();
  };
  const doneAfterReset = () => { onDone(); closeAll(); };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { user_id: user.id, new_password: password });
      setDone({ email: user.email, password });
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to reset password');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && (done ? doneAfterReset() : closeAll())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{done ? 'Credentials ready' : 'Reset password'}</DialogTitle>
          {!done && <DialogDescription>Setting a new password for {user?.email}</DialogDescription>}
        </DialogHeader>

        {done ? (
          <CredentialsReady
            email={done.email}
            mobile={user?.mobile}
            password={done.password}
            role={user?.role}
            factoryWarehouse={user?.factory_warehouse}
            onDone={doneAfterReset}
            mode="reset"
          />
        ) : (
          <form onSubmit={submit} className="space-y-3 py-2">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">New password</Label>
                <button
                  type="button"
                  onClick={() => setPassword(genPassword())}
                  className="text-xs text-amber-700 hover:text-amber-900 underline"
                >
                  Generate
                </button>
              </div>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6 digits (or click Generate)" />
            </div>
            <p className="text-xs text-stone-400">
              The user should log in with this password. To change it later, they can use the Forgot password? link on the sign-in page.
            </p>
            {err && <p className="text-xs text-red-600">{err}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAll}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-amber-800 hover:bg-amber-900 text-white">
                {busy ? 'Resetting…' : 'Reset password'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Inventory Management Section
const inventoryCategorize = (items) => {
  const categories = {
    interlocking: { title: 'Hollow Interlocking Blocks — 6" Walls', icon: '🔗', items: [] },
    octa:         { title: 'Octa Interlocking Blocks — 8" Walls',   icon: '🔷', items: [] },
    smart:        { title: 'Smart Earth Blocks — 9" & 4.2" Walls',  icon: '🧠', items: [] },
    solid:        { title: 'Solid Earth Blocks — 9" or 7.5" Walls', icon: '🧱', items: [] },
    small:        { title: '10×10 & 10×5 Bricks — 10" & 5" Walls', icon: '📏', items: [] },
    pointing:     { title: 'Crushed Soil for Pointing',             icon: '🪨', items: [] },
  };
  items.forEach(item => {
    if ((item.status && item.status !== 'active') || !item.sku?.trim()) return;
    const n = item.name.toLowerCase();
    if (n.includes('cement')) return;
    if (n.includes('crushed') || n.includes('pointing') || n.includes('soil')) categories.pointing.items.push(item);
    else if (n.includes('octa')) categories.octa.items.push(item);
    else if (n.includes('smart')) categories.smart.items.push(item);
    else if (isTenByTen(item.name)) categories.small.items.push(item);
    else if (n.includes('interlock') || n.includes('hollow') || n.includes('corner') || (n.includes('half') && !n.includes('solid') && !n.includes('smart'))) categories.interlocking.items.push(item);
    else if (n.includes('solid')) categories.solid.items.push(item);
  });
  return categories;
};

// ─── ACTIVITY LOG ──────────────────────────────────────────────────────────
const ACTIVITY_LABELS = {
  login: 'Signed in',
  user_created: 'Created a user',
  user_updated: 'Updated a user',
  user_disabled: 'Disabled a user',
  user_enabled: 'Enabled a user',
  user_deleted: 'Deleted a user',
  estimate_approved_invoice: 'Approved quote (Invoice)',
  estimate_approved_stock_transfer: 'Approved quote (Free Delivery)',
  estimate_approved: 'Opened an order',
  order_opened: 'Opened an order',
  order_closed: 'Closed an order',
  customer_claimed: 'Claimed a customer',
  customer_reassigned: 'Reassigned a customer',
  call_off_created: 'Booked a truck against an order',
  call_off_stock_override: 'Overrode the stock check on a load',
  delivery_cancelled: 'Cancelled a load',
  delivery_rescheduled: 'Rescheduled a load',
  delivery_ready_for_invoice: 'Loading completed — Accounts notified',
  delivery_loading_override: 'Overrode the payment check to load',
  delivery_unloaded: 'Unloaded a truck (back to confirmed)',
  delivery_documents_ready: 'Invoice OK — truck may depart',
  delivery_documents_overdue: 'Reminded Accounts — invoice overdue',
  delivery_invoice_linked: 'Linked an invoice to a load by hand',
  delivery_payment_received: 'Payment received — loads cleared to load',
  password_reset_by_admin: 'Reset a user\'s password',
  password_reset_requested: 'Requested password reset (WhatsApp)',
  password_reset_completed: 'Completed password reset',
  password_changed_self: 'Changed own password',
  delivery_created_is_stock_transfer: 'Created a Free Delivery truck',
  delivery_dispatched_stock_transfer: 'Departed a Free Delivery truck',
  delivery_edited: 'Edited a delivery',
  delivery_customer_notified: 'Notified customer of status',
};

const ActivitySection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    const params = typeFilter ? { activity_type: typeFilter } : {};
    axios.get(`${API}/activities`, { params })
      .then(r => setItems(r.data || []))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load activity'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [typeFilter]);

  const relative = (iso) => {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.round(diff/60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff/3_600_000)}h ago`;
    return fmtDate(iso);
  };

  const types = Array.from(new Set(items.map(i => i.activity_type)));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-lg">Activity Log</CardTitle>
          <CardDescription className="text-xs">
            {items.length} event{items.length !== 1 ? 's' : ''} · latest 200
          </CardDescription>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-xs border border-stone-300 rounded px-2 py-1"
          >
            <option value="">All activity</option>
            {Object.keys(ACTIVITY_LABELS).map(t => (
              <option key={t} value={t}>{ACTIVITY_LABELS[t]}</option>
            ))}
            {types.filter(t => !ACTIVITY_LABELS[t]).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load}>↺ Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
        {loading ? (
          <div className="text-center py-8 text-stone-400 text-sm">Loading activity…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-stone-400 text-sm">No activity yet.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {items.map(a => (
              <div key={a.id} className="py-2 flex justify-between items-start gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="text-stone-800">
                    <span className="font-medium">{a.user_name || a.user_email}</span>
                    <span className="text-stone-500"> · </span>
                    <span>{ACTIVITY_LABELS[a.activity_type] || a.activity_type}</span>
                  </div>
                  {a.activity_details && Object.keys(a.activity_details).length > 0 && (
                    <div className="text-xs text-stone-500 mt-0.5 truncate">
                      {Object.entries(a.activity_details).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </div>
                  )}
                </div>
                <div className="text-xs text-stone-400 whitespace-nowrap">{relative(a.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── SETTINGS ──────────────────────────────────────────────────────────────
const SettingsSection = ({ onSyncZoho }) => {
  const [busy, setBusy] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const handleSync = async () => {
    setBusy(true);
    try {
      await onSyncZoho();
      setLastSync(new Date().toLocaleString());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Zoho Books integration</CardTitle>
          <CardDescription className="text-xs">
            Manage the connection between EarthBlocks and Zoho Books.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3 border-b border-stone-100 pb-3">
            <div>
              <div className="text-sm font-medium text-stone-800">Sync inventory</div>
              <div className="text-xs text-stone-500">
                Pull the latest items, rates, and stock levels from Zoho Books.
                {lastSync && <span className="ml-1">Last sync: {lastSync}.</span>}
              </div>
            </div>
            <Button onClick={handleSync} disabled={busy} variant="outline" size="sm" data-testid="sync-zoho-btn">
              {busy ? 'Syncing…' : '↺ Sync now'}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-stone-800">Connection setup</div>
              <div className="text-xs text-stone-500">
                Reconnect Zoho Books, rotate the refresh token, or change the org.
              </div>
            </div>
            <Button asChild size="sm" data-testid="zoho-setup-btn">
              <Link to="/zoho-setup">Open setup</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <StockTransferDeliveriesList />
    </div>
  );
};

const StockTransferDeliveriesList = () => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${API}/deliveries`)
      .then(r => setRows((r.data.deliveries || []).filter(d => d.is_stock_transfer).slice(0, 20)))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load'));
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent Free Delivery trucks</CardTitle>
        <CardDescription className="text-xs">
          Samples, internal use, wastage, damage, and other free movements.
          Every row also lands as a Zoho inventory adjustment with reason starting
          <span className="font-mono"> "Stock Transfer · " </span>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
        {rows === null ? (
          <div className="text-center py-6 text-stone-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-stone-400 text-sm">No Free Delivery trucks yet.</div>
        ) : (
          <div className="divide-y divide-stone-100 text-sm">
            {rows.map(d => (
              <Link
                key={d.delivery_id}
                to={`/delivery/${d.delivery_id}`}
                className="flex flex-wrap gap-x-4 gap-y-1 py-2 hover:bg-stone-50 px-2 -mx-2 rounded"
              >
                <span className="text-stone-800 font-medium min-w-[8rem]">{d.customer_name || '—'}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                  {STOCK_TRANSFER_LABEL(d.stock_transfer_category)}
                </span>
                <span className="text-xs text-stone-500 truncate flex-1 min-w-[10rem]">
                  {d.stock_transfer_reason || '—'}
                </span>
                <span className="text-xs text-stone-400 whitespace-nowrap">
                  {d.status} · {fmtDate(d.created_time)}
                </span>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const InventorySection = ({ items, onUpdate, currentUser }) => {
  // Admin (primary role OR extra_roles) can close open SOs to release stuck
  // commitments. Sales reps / managers see the drill-down but not the button.
  const isAdmin = !!currentUser && (
    currentUser.role === 'admin' || (currentUser.extra_roles || []).includes('admin')
  );
  // Refresh pulls the current stock snapshot FROM Zoho into the app cache. Writing
  // stock happens exclusively through the Factory Portal (production logs) and the
  // Delivery flow (Stock Transfer decrement); the old "Update Stock" form on this
  // page created stray adjustments outside those paths and was removed 2026-07-14.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  // Click a row to expand the drill-down of open SOs committing this item's stock.
  // openOrdersById caches the drill-down response per zoho_item_id so re-expanding
  // is instant; loadingOrdersId tracks which item is currently fetching.
  const [expandedId, setExpandedId] = useState(null);
  const [openOrdersById, setOpenOrdersById] = useState({});
  const [loadingOrdersId, setLoadingOrdersId] = useState(null);
  const [closingSoId, setClosingSoId] = useState(null);
  const categories = inventoryCategorize(items);
  // Stock rule (2026-09): the app shows and gates on PHYSICAL on-hand. Zoho's
  // "available for sale" is net of every open sales order, and orders run for
  // months and are rarely taken in full — so it is not a loading signal. The open
  // SO backlog is shown separately as "Ordered, not supplied".
  const onHandOf = (i) => Number(i.stock_quantity) || 0;
  const lowStockCount = items.filter(i => onHandOf(i) < Number(i.reorder_level)).length;

  const toggleExpand = async (item) => {
    const key = item.zoho_item_id || item.id;
    if (expandedId === key) { setExpandedId(null); return; }
    setExpandedId(key);
    if (openOrdersById[key] === undefined && item.zoho_item_id) {
      setLoadingOrdersId(key);
      try {
        const r = await axios.get(`${API}/inventory/items/${item.zoho_item_id}/open-orders`);
        setOpenOrdersById(prev => ({ ...prev, [key]: r.data.orders || [] }));
      } catch (err) {
        setOpenOrdersById(prev => ({ ...prev, [key]: [] }));
      } finally {
        setLoadingOrdersId(null);
      }
    }
  };

  // Admin-only: close an open SO in Zoho so the remaining stock commitment
  // releases (voided when nothing is invoiced yet, otherwise trimmed to the
  // invoiced quantities). Refreshes the item's drill-down + the underlying
  // stock list so Committed / Available update straight away.
  const closeOrder = async (itemKey, order) => {
    const so = order.salesorder_number;
    const remaining = Number(order.quantity).toLocaleString();
    const ok = window.confirm(
      `Close ${so} (${order.customer_name})?\n\n` +
      `${remaining} ${order.unit || 'pcs'} still committed will be released back into Available for Sale. ` +
      `The order is closed in Zoho (voided, or trimmed to what was already invoiced) and disappears from this list.\n\nThis cannot be undone from the app.`
    );
    if (!ok) return;
    setClosingSoId(order.salesorder_id);
    try {
      const r = await axios.post(`${API}/inventory/salesorders/${order.salesorder_id}/close`);
      // Optimistically remove the closed SO from the drawer; force a refresh
      // in the background so Committed / Available numbers update too.
      setOpenOrdersById(prev => ({
        ...prev,
        [itemKey]: (prev[itemKey] || []).filter(o => o.salesorder_id !== order.salesorder_id),
      }));
      // Kick off a full sync — the numbers on the row should catch up on next Refresh.
      axios.post(`${API}/zoho/sync-inventory`).then(() => onUpdate()).catch(() => {});
      const msg = r.data.already_closed
        ? `${so} was already closed in Zoho.`
        : `${so} closed. ${remaining} ${order.unit || 'pcs'} released back to Available.`;
      alert(msg);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not close the sales order.');
    } finally {
      setClosingSoId(null);
    }
  };

  const refreshFromZoho = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/zoho/sync-inventory`);
      await onUpdate();  // parent reloads /inventory/items so the list re-renders
      setRefreshedAt(new Date());
    } catch (err) {
      alert(err.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">Inventory</h2>
          {lowStockCount > 0 && (
            <Badge variant="destructive" className="text-xs">{lowStockCount} Low Stock</Badge>
          )}
          <span className="text-sm text-gray-500">{items.length} items</span>
          {refreshedAt && (
            <span className="text-xs text-gray-400">
              Refreshed {refreshedAt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={refreshFromZoho} disabled={refreshing}
          data-testid="refresh-stock-btn" title="Pull the latest stock snapshot from Zoho Books">
          {refreshing ? 'Refreshing…' : '🔄 Refresh'}
        </Button>
      </div>

      <div className="space-y-3">
        {Object.entries(categories).map(([key, cat]) => {
          if (cat.items.length === 0) return null;
          return (
            <Card key={key} className="overflow-hidden">
              <div className="bg-gray-50 border-b px-4 py-2 flex items-center gap-2">
                <span>{cat.icon}</span>
                <span className="font-semibold text-sm text-gray-700">{cat.title}</span>
                <span className="ml-auto text-xs text-gray-400">{cat.items.length} items</span>
              </div>
              <div className="divide-y">
                {cat.items.map(item => {
                  const onHand = Number(item.stock_quantity);
                  // "Ordered, not supplied" = Zoho committed = open sales-order quantity
                  // still to be supplied. It is the production backlog, not a stock deduction.
                  const backlog = Number(item.committed_stock ?? 0);
                  const reorder = Number(item.reorder_level) || 1;
                  const pct = Math.min((Math.max(onHand, 0) / reorder) * 100, 100);
                  // Low-stock trigger is on PHYSICAL on-hand — the only number a truck
                  // can be loaded against. Open orders are shown separately.
                  const isLow = onHand < reorder;
                  const isExpanded = expandedId === (item.zoho_item_id || item.id);
                  const orders = openOrdersById[item.zoho_item_id || item.id];
                  const isLoadingOrders = loadingOrdersId === (item.zoho_item_id || item.id);
                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => toggleExpand(item)}
                        className={`w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors ${isLow ? 'bg-red-50 hover:bg-red-100' : ''}`}
                        data-testid={`inv-row-${item.id}`}
                      >
                        {/* Name + SKU */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 leading-tight truncate">{item.name}</p>
                          <p className="text-xs text-gray-400">{item.sku}{item.dimensions ? ` · ${item.dimensions}` : ''}</p>
                        </div>
                        {/* Reorder bar (on-hand vs threshold) — hidden on small screens */}
                        <div className="hidden sm:flex flex-col items-end w-24 shrink-0">
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-0.5">
                            <div className={`h-1.5 rounded-full ${isLow ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400">{reorder.toLocaleString()} reorder</span>
                        </div>
                        {/* Two-column breakdown — On Hand (physical) / Ordered, not supplied (open SO backlog) */}
                        <div className="grid grid-cols-2 gap-2 text-right shrink-0 w-40 sm:w-56">
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-none">On Hand</p>
                            <p className={`text-sm font-bold tabular-nums ${isLow ? 'text-red-600' : 'text-green-700'}`}
                               data-testid={`stock-${item.id}`}>
                              {onHand.toLocaleString()}
                            </p>
                          </div>
                          <div title="Open sales-order quantity still to be supplied (production backlog)">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-none">Ordered, not supplied</p>
                            <p className={`text-sm font-medium tabular-nums ${backlog > 0 ? 'text-blue-700' : 'text-gray-300'}`}>
                              {backlog.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <span className="text-gray-300 text-xs shrink-0 w-3">{isExpanded ? '▼' : '▶'}</span>
                      </button>

                      {/* Expand drawer — open SOs committing this item's stock */}
                      {isExpanded && (
                        <div className="bg-gray-50 border-t px-4 py-3">
                          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Open orders committing this stock
                          </p>
                          {isLoadingOrders ? (
                            <p className="text-xs text-gray-400">Loading…</p>
                          ) : !orders || orders.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No open orders committing this stock.</p>
                          ) : (
                            <div className="space-y-1">
                              {orders.map(o => {
                                const remaining = Number(o.quantity);
                                const lineQty = Number(o.line_quantity ?? o.quantity);
                                const isPartial = o.quantity_invoiced > 0 && lineQty > remaining;
                                const isClosingThis = closingSoId === o.salesorder_id;
                                return (
                                  <div key={o.salesorder_id} className="flex items-center justify-between gap-2 text-xs">
                                    <div className="flex-1 min-w-0 flex items-center gap-2">
                                      <span className="font-mono font-medium text-gray-700 shrink-0">{o.salesorder_number}</span>
                                      <span className="text-gray-500 truncate">{o.customer_name}</span>
                                      {o.reference_number && (
                                        <span className="text-gray-400 shrink-0">· ref {o.reference_number}</span>
                                      )}
                                    </div>
                                    <span className="text-gray-400 shrink-0">{fmtDate(o.date)}</span>
                                    <span className="tabular-nums shrink-0 text-right"
                                          title={isPartial ? `${remaining.toLocaleString()} committed of ${lineQty.toLocaleString()} originally ordered (${Number(o.quantity_invoiced).toLocaleString()} already invoiced)` : ''}>
                                      <span className="font-semibold text-blue-700">
                                        {remaining.toLocaleString()} {o.unit || 'pcs'}
                                      </span>
                                      {isPartial && (
                                        <span className="text-[10px] text-gray-400 ml-1">
                                          / {lineQty.toLocaleString()}
                                        </span>
                                      )}
                                    </span>
                                    {/* Admin-only: close the SO so the remaining commitment releases. */}
                                    {isAdmin && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); closeOrder(item.zoho_item_id || item.id, o); }}
                                        disabled={isClosingThis || !!closingSoId}
                                        className="shrink-0 text-[10px] px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-wait font-medium"
                                        title="Close this sales order in Zoho — releases the remaining committed stock. Admin only."
                                      >
                                        {isClosingThis ? 'Closing…' : '✕ Close'}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// StockUpdateForm removed 2026-07-14 — the Dashboard's inline production-log
// form created audit-untracked adjustments (no photo / cement / labour, no
// per-factory routing). Production logging now lives exclusively on the
// Factory Portal, and this section only READS stock (via the Refresh button
// that hits /api/zoho/sync-inventory).


// Quotes Section
// Shared status colour maps
const EST_STATUS_COLORS = {
  draft:    'bg-gray-100 text-gray-600',
  sent:     'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  invoiced: 'bg-purple-100 text-purple-700',
  expired:  'bg-orange-100 text-orange-700',
};
const SO_STATUS_COLORS = {
  draft:               'bg-gray-100 text-gray-600',
  open:                'bg-blue-100 text-blue-700',
  confirmed:           'bg-green-100 text-green-700',
  void:                'bg-red-100 text-red-700',
  invoiced:            'bg-purple-100 text-purple-700',
  partially_invoiced:  'bg-yellow-100 text-yellow-700',
  closed:              'bg-gray-200 text-gray-500',
};

// Shared sort button used in both list sections
const SortHeader = ({ label, col, sortBy, sortDir, onSort, align = 'left' }) => {
  const active = sortBy === col;
  return (
    <button onClick={() => onSort(col)}
      className={`flex items-center gap-0.5 text-xs font-semibold uppercase tracking-wide select-none transition-colors w-full
        ${align === 'right' ? 'justify-end' : 'justify-start'}
        ${active ? 'text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}>
      {label}
      <span className="text-[10px] ml-0.5">{active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );
};

// Shared expandable line-items panel (lazy-loaded)
const LineItemsPanel = ({ id, detailUrl, detailKey }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    axios.get(detailUrl)
      .then(r => setItems((r.data[detailKey] || {}).line_items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [detailUrl, detailKey]);

  if (loading) return <div className="px-4 pb-3 text-xs text-gray-400">Loading items…</div>;
  if (!items || items.length === 0) return <div className="px-4 pb-3 text-xs text-gray-400">No items found.</div>;
  return (
    <div className="px-4 pb-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b">
            <th className="text-left py-1 text-gray-500 font-medium">Item</th>
            <th className="text-right py-1 text-gray-500 font-medium w-16">Qty</th>
            <th className="text-right py-1 text-gray-500 font-medium w-24">Rate</th>
            <th className="text-right py-1 text-gray-500 font-medium w-24">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {items.map((li, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <td className="py-1.5 pr-2 text-gray-800">{li.name}</td>
              <td className="py-1.5 text-right font-semibold text-gray-900">{Number(li.quantity).toLocaleString('en-IN')}</td>
              <td className="py-1.5 text-right text-gray-500">₹{Number(li.rate || 0).toLocaleString('en-IN')}</td>
              <td className="py-1.5 text-right font-medium text-gray-800">₹{Number(li.item_total || (li.quantity * li.rate) || 0).toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SalesOrdersSection = () => {
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => { loadSalesOrders(); }, [statusFilter]);

  const loadSalesOrders = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const resp = await axios.get(`${API}/zoho/salesorders${params}`);
      setSalesOrders(resp.data.salesorders || []);
      setExpanded({});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const sorted = [...salesOrders].sort((a, b) => {
    let av, bv;
    if (sortBy === 'date') { av = a.date || ''; bv = b.date || ''; }
    else if (sortBy === 'total') { av = Number(a.total) || 0; bv = Number(b.total) || 0; }
    else if (sortBy === 'customer') { av = (a.customer_name || '').toLowerCase(); bv = (b.customer_name || '').toLowerCase(); }
    else if (sortBy === 'status') { av = a.status || ''; bv = b.status || ''; }
    else { av = ''; bv = ''; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Orders</h2>
          <p className="text-xs text-gray-400">Live from Zoho Books{!loading && salesOrders.length > 0 ? ` · ${salesOrders.length} orders` : ''}</p>
        </div>
        <Button size="sm" variant="outline" onClick={loadSalesOrders}>↺ Refresh</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['', 'All'], ['open', 'Open'], ['confirmed', 'Confirmed'], ['invoiced', 'Invoiced'], ['void', 'Void']].map(([val, lbl]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${statusFilter === val ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-8">Loading from Zoho…</p>
      ) : sorted.length === 0 ? (
        <p className="text-center text-gray-400 py-8">No orders found.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          {/* Header — hidden on mobile */}
          <div className="hidden sm:grid grid-cols-[1fr_5rem_7rem_6rem_1.5rem] gap-x-3 px-4 py-2 bg-gray-50 border-b items-center">
            <SortHeader label="Customer" col="customer" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            <SortHeader label="Date" col="date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortHeader label="Amount" col="total" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortHeader label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <div />
          </div>

          {/* Rows */}
          <div className="divide-y">
            {sorted.map(so => (
              <div key={so.salesorder_id} className="bg-white">
                {/* Mobile card layout */}
                <div className="sm:hidden px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => toggleExpand(so.salesorder_id)}>
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="font-medium text-sm text-gray-900 truncate">{so.customer_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{so.salesorder_number} · {fmtDate(so.date)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-green-700">₹{Number(so.total).toLocaleString('en-IN')}</p>
                      <Badge className={`text-xs mt-1 ${SO_STATUS_COLORS[so.status] || 'bg-gray-100 text-gray-600'}`}>{so.status}</Badge>
                    </div>
                  </div>
                </div>
                {/* Desktop grid layout */}
                <div className="hidden sm:grid grid-cols-[1fr_5rem_7rem_6rem_1.5rem] gap-x-3 px-4 py-3 items-center hover:bg-gray-50 transition-colors">
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{so.customer_name}</p>
                    <p className="text-xs text-gray-400 font-mono mt-0.5">{so.salesorder_number}{so.location_name ? ` · ${so.location_name}` : ''}</p>
                  </div>
                  <p className="text-xs text-gray-500 text-right">{fmtDate(so.date)}</p>
                  <p className="text-sm font-bold text-green-700 text-right">₹{Number(so.total).toLocaleString('en-IN')}</p>
                  <div className="flex justify-end">
                    <Badge className={`text-xs ${SO_STATUS_COLORS[so.status] || 'bg-gray-100 text-gray-600'}`}>{so.status}</Badge>
                  </div>
                  <button onClick={() => toggleExpand(so.salesorder_id)}
                    className="text-gray-400 hover:text-blue-500 transition-colors text-base leading-none text-center">
                    {expanded[so.salesorder_id] ? '▲' : '▼'}
                  </button>
                </div>
                {expanded[so.salesorder_id] && (
                  <LineItemsPanel
                    id={so.salesorder_id}
                    detailUrl={`${API}/zoho/salesorders/${so.salesorder_id}`}
                    detailKey="salesorder"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const QuotesSection = ({ quotes }) => {
  const [search, setSearch] = useState('');

  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-800',
    accepted: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800',
    invoiced: 'bg-purple-100 text-purple-800',
    pending: 'bg-yellow-100 text-yellow-800',
  };

  const filtered = quotes.filter(q =>
    !search ||
    (q.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (q.estimate_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (q.customer_mobile || '').includes(search)
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Quotes</h2>
        <Input
          placeholder="Search by name / quote# / mobile..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-72"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No quotes found.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(q => {
            const lineItems = q.line_items || [];
            const totalQty = lineItems.reduce((s, li) => s + (Number(li.quantity) || 0), 0);
            return (
              <Card key={q.quote_id} className="hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{q.customer_name || '—'}</span>
                        {q.estimate_number && (
                          <span className="text-xs text-gray-500 font-mono">{q.estimate_number}</span>
                        )}
                        <Badge className={`text-xs ${statusColors[q.status] || 'bg-gray-100 text-gray-700'}`}>
                          {q.status || 'draft'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {q.customer_mobile && <span className="mr-2">{q.customer_mobile}</span>}
                        {q.customer_gstin && <span className="mr-2">GST: {q.customer_gstin}</span>}
                        <span>{fmtDate(q.created_time)}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0 space-y-1">
                      <p className="font-bold text-green-700">₹{Number(q.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                      <p className="text-xs text-gray-500">{totalQty.toLocaleString()} pcs</p>
                      {q.zoho_estimate_id && q.status === 'accepted' && (
                        <OrderLoadControls
                          estimateId={q.zoho_estimate_id}
                          estimateNumber={q.estimate_number}
                          customerName={q.customer_name}
                        />
                      )}
                    </div>
                  </div>
                  {lineItems.length > 0 && (
                    <div className="mt-2 pt-2 border-t grid grid-cols-2 md:grid-cols-3 gap-1">
                      {lineItems.map((li, i) => (
                        <div key={i} className="text-xs text-gray-600 flex justify-between bg-gray-50 rounded px-2 py-1">
                          <span className="truncate mr-2">{li.name}</span>
                          <span className="font-medium shrink-0">{Number(li.quantity).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Analytics Section
// Transport coordinator's "what to load on <date>" view — confirmed/loaded loads
// scheduled for the chosen day (plus unscheduled ones), grouped by factory with
// item-wise totals. Print-friendly for the loader.
const LoadingQueueSection = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(localISODate());

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/deliveries?date=${date}&include_unscheduled=true`);
      const rows = (r.data.deliveries || []).filter(d => d.status === 'confirmed' || d.status === 'loaded');
      setDeliveries(rows);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]);

  // Group by factory → { factory: { deliveries:[], items:{name:qty} } }
  const groups = {};
  deliveries.forEach(d => {
    const f = d.source_factory || 'Unassigned';
    groups[f] = groups[f] || { deliveries: [], items: {} };
    groups[f].deliveries.push(d);
    (d.line_items || []).forEach(li => {
      const key = li.name || li.item_id;
      groups[f].items[key] = (groups[f].items[key] || 0) + (Number(li.quantity) || 0);
    });
  });
  const factoryNames = Object.keys(groups).sort();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Loading — {fmtDate(date)}</h2>
          <p className="text-xs text-stone-500">Loads scheduled for this day (and any without a date), grouped by factory</p>
        </div>
        <div className="flex gap-2 items-center print:hidden">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value || localISODate())}
            className="h-8 px-2 text-sm border border-stone-300 rounded-md bg-white"
            data-testid="loading-date"
          />
          <Button size="sm" variant="outline" onClick={load}>↺ Refresh</Button>
          <Button size="sm" variant="outline" onClick={() => window.print()}>🖨 Print</Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-8">Loading…</p>
      ) : factoryNames.length === 0 ? (
        <p className="text-center text-stone-400 py-8 border border-dashed border-stone-200 rounded-lg">Nothing to load right now. ✓</p>
      ) : factoryNames.map(f => {
        const g = groups[f];
        const ft = factoryTag(f);
        const items = Object.entries(g.items).sort((a, b) => b[1] - a[1]);
        const grand = items.reduce((s, [, v]) => s + v, 0);
        return (
          <div key={f} className={`border rounded-lg overflow-hidden border-stone-200 border-l-4 ${ft ? ft.accent : 'border-l-stone-300'}`}>
            <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 flex items-center justify-between">
              <span className="font-semibold text-sm text-stone-800">🏭 {ft ? ft.label : f}</span>
              <span className="text-[11px] text-stone-500">{g.deliveries.length} order{g.deliveries.length !== 1 ? 's' : ''} · {grand.toLocaleString('en-IN')} pcs</span>
            </div>
            {/* Item totals to load */}
            <div className="px-4 py-2 grid grid-cols-2 sm:grid-cols-3 gap-2 border-b border-stone-100">
              {items.map(([name, qty]) => (
                <div key={name} className="bg-white border border-stone-200 rounded px-2 py-1.5">
                  <p className="text-[11px] text-stone-500 truncate leading-tight">{name}</p>
                  <p className="text-base font-bold text-stone-900">{qty.toLocaleString('en-IN')}<span className="text-[10px] text-stone-400 font-normal"> pcs</span></p>
                </div>
              ))}
            </div>
            {/* Per-order breakdown */}
            <ul className="divide-y divide-stone-100">
              {g.deliveries.map(d => (
                <li key={d.delivery_id} className="px-4 py-1.5 flex items-center justify-between text-xs">
                  <span className="text-stone-700 truncate">
                    {d.customer_name}
                    <span className="text-stone-400 ml-2">{[d.delivery_address?.city].filter(Boolean).join('')}</span>
                    <span className="text-stone-400 ml-2">{d.estimate_number} · Load {d.call_off_seq || 1}</span>
                    {!d.scheduled_date && <span className="ml-2 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">no date</span>}
                  </span>
                  <span className="shrink-0 ml-2 text-stone-500">
                    {Number(d.total_qty).toLocaleString('en-IN')} pcs · {DELIVERY_STATUS[d.status]?.label || d.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const AnalyticsSection = () => {
  const [estItems, setEstItems] = useState({});   // item name → qty from accepted estimates
  const [delItems, setDelItems] = useState({});   // item name → qty from scheduled deliveries
  const [estCount, setEstCount] = useState(0);
  const [delCount, setDelCount] = useState(0);
  const [newQuotes, setNewQuotes] = useState([]);   // recent draft estimates awaiting a rep
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [estResp, delResp, draftResp] = await Promise.all([
        axios.get(`${API}/zoho/estimates?status=accepted&limit=100`),
        axios.get(`${API}/deliveries`),
        axios.get(`${API}/zoho/estimates?status=draft&limit=8&sort_order=D`).catch(() => ({ data: { estimates: [] } })),
      ]);

      setNewQuotes(draftResp.data.estimates || []);

      // Aggregate qty per item from accepted estimates
      const eTotals = {};
      const estimates = estResp.data.estimates || [];
      estimates.forEach(est => {
        (est.line_items || []).forEach(li => {
          const key = li.name || li.item_id;
          eTotals[key] = (eTotals[key] || 0) + (Number(li.quantity) || 0);
        });
      });

      // Aggregate qty per item from scheduled (non-delivered) deliveries
      const dTotals = {};
      const deliveries = (delResp.data.deliveries || []).filter(d => d.status !== 'delivered');
      deliveries.forEach(d => {
        (d.line_items || []).forEach(li => {
          const key = li.name || li.item_id;
          dTotals[key] = (dTotals[key] || 0) + (Number(li.quantity) || 0);
        });
      });

      setEstItems(eTotals);
      setDelItems(dTotals);
      setEstCount(estimates.length);
      setDelCount(deliveries.length);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const ItemGrid = ({ totals, emptyText }) => {
    const entries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    const grandTotal = entries.reduce((s, [, v]) => s + v, 0);
    if (entries.length === 0) return <p className="text-sm text-gray-400 py-4 text-center">{emptyText}</p>;
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {entries.map(([name, qty]) => (
            <div key={name} className="bg-gray-50 border rounded px-3 py-2 flex flex-col">
              <span className="text-xs text-gray-500 truncate leading-tight">{name}</span>
              <span className="text-lg font-bold text-gray-900 mt-0.5">{qty.toLocaleString('en-IN')}</span>
              <span className="text-[10px] text-gray-400">pcs</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 text-right">
          Grand total: <span className="font-semibold text-gray-700">{grandTotal.toLocaleString('en-IN')} pcs</span>
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Analytics</h2>
        <Button size="sm" variant="outline" onClick={loadData}>↺ Refresh</Button>
      </div>

      {/* Sales-rep notification: new customer quote requests awaiting review */}
      {!loading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
          <div className="px-4 py-2.5 flex items-center gap-2 border-b border-amber-200">
            <span className="text-lg">🔔</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">New Quote Requests</p>
              <p className="text-[11px] text-amber-700">Sales rep to review, then confirm &amp; send the final quote</p>
            </div>
            {newQuotes.length > 0 && (
              <span className="shrink-0 text-xs font-bold text-white bg-amber-600 rounded-full px-2 py-0.5">
                {newQuotes.length}
              </span>
            )}
          </div>
          {newQuotes.length === 0 ? (
            <p className="px-4 py-3 text-xs text-amber-700/70">No new quote requests right now. You're all caught up. ✓</p>
          ) : (
            <ul className="divide-y divide-amber-100">
              {newQuotes.map(q => (
                <li key={q.estimate_id} className="px-4 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">
                      {q.customer_name}
                      <span className="text-[11px] font-normal text-stone-400 ml-2">{q.estimate_number}</span>
                    </p>
                    <p className="text-[11px] text-stone-500 truncate">
                      {q.salesperson_name
                        ? <>Rep: <span className="font-medium text-amber-800">{q.salesperson_name}</span></>
                        : <span className="text-red-500">No rep assigned</span>}
                      {q.date ? ` · ${q.date}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-bold text-stone-800">
                    ₹{Number(q.total || 0).toLocaleString('en-IN')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-400 py-8">Loading...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Accepted Quotes */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-green-800">Accepted Quotes</p>
                <p className="text-xs text-green-600">Qty yet to be supplied</p>
              </div>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                {estCount} quote{estCount !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="p-4">
              <ItemGrid totals={estItems} emptyText="No accepted quotes" />
            </div>
          </div>

          {/* Scheduled Deliveries */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-blue-50 border-b flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-800">Scheduled Deliveries</p>
                <p className="text-xs text-blue-600">Qty in active delivery queue</p>
              </div>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {delCount} delivery{delCount !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="p-4">
              <ItemGrid totals={delItems} emptyText="No active deliveries" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// OAuth Callback Component
const OAuthCallback = () => {
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    setProcessing(true);
    
    try {
      // Get authorization code from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (!code) {
        setResult({ error: 'No authorization code received from Zoho' });
        return;
      }

      // Send code to backend
      const response = await axios.post(`${API}/zoho/oauth-callback?authorization_code=${code}`);
      
      setResult({
        success: true,
        message: `Successfully connected to Zoho Books! Synced ${response.data.synced_items} items.`
      });
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => navigate('/'), 3000);
      
    } catch (error) {
      setResult({
        error: 'Failed to connect to Zoho Books. Please try again.',
        details: error.response?.data?.detail || error.message
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Processing Zoho Authorization</CardTitle>
          <CardDescription>Connecting your Zoho Books account...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 text-center">
          {processing && (
            <div>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Connecting to Zoho Books...</p>
            </div>
          )}

          {result && (
            <div>
              {result.success ? (
                <Alert className="border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800">
                    {result.message}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">
                    {result.error}
                    {result.details && (
                      <div className="mt-2 text-sm">{result.details}</div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {result.success && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">Redirecting to dashboard in 3 seconds...</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-center space-x-3">
            <Button variant="outline" asChild>
              <Link to="/">Back to Dashboard</Link>
            </Button>
            {result && !result.success && (
              <Button asChild>
                <Link to="/zoho-setup">Try Again</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Zoho Setup Component - Self Client Mode
const ZohoSetup = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [authUrl, setAuthUrl] = useState(null);

  useEffect(() => {
    checkZohoStatus();
    fetchAuthUrl();
  }, []);

  const checkZohoStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/zoho/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking Zoho status:', error);
      setStatus({ connected: false, message: 'Could not reach backend server.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthUrl = async () => {
    try {
      const response = await axios.get(`${API}/zoho/auth-url`);
      setAuthUrl(response.data.authorization_url);
    } catch {
      // backend may not be running — silently skip
    }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/zoho/sync-inventory`);
      alert(`Successfully synced ${response.data.synced_count} items from Zoho Books!`);
      await checkZohoStatus();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      alert(`Sync failed: ${msg}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.get(`${API}/zoho/test-connection`);
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ ok: false, message: error.response?.data?.detail || error.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Zoho Books Connection</CardTitle>
          <CardDescription>Manage your Zoho Books integration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">

          {/* Status */}
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Checking connection status...</p>
            </div>
          ) : status?.connected ? (
            <div className="space-y-3">
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-700">
                  <strong>Connected</strong> — {status.message}
                </AlertDescription>
              </Alert>
              {status.organization && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <p className="text-gray-500">Organisation ID: <span className="font-medium text-gray-800">{status.organization}</span></p>
                  {status.last_sync && (
                    <p className="text-gray-400 mt-1">Last sync: {new Date(status.last_sync).toLocaleString()}</p>
                  )}
                </div>
              )}
              <Button onClick={handleSync} className="w-full" disabled={loading}>
                Sync Inventory from Zoho
              </Button>
              <Button onClick={handleTestConnection} variant="outline" className="w-full" disabled={testing}>
                {testing ? 'Testing...' : 'Test Live Connection'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-700">
                  <strong>Not Connected</strong> — {status?.message}
                </AlertDescription>
              </Alert>

              {/* Connect button */}
              {authUrl && (
                <a href={authUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Connect to Zoho Books (Authorize)
                  </Button>
                </a>
              )}

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Required backend env vars (Self-Client setup)</h4>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li><strong>ZOHO_REFRESH_TOKEN</strong> — the durable refresh token from Zoho API Console → Generate Code</li>
                  <li>ZOHO_CLIENT_ID</li>
                  <li>ZOHO_CLIENT_SECRET</li>
                  <li>ZOHO_ORGANIZATION_ID</li>
                  <li>ZOHO_ACCOUNTS_URL = https://accounts.zoho.in</li>
                  <li>ZOHO_BASE_URL = https://www.zohoapis.in/books/v3</li>
                </ul>
                <p className="text-xs text-yellow-800 mt-2">
                  Self-Client apps don't use OAuth redirects. Set these in Railway → backend → Variables. Restart the service after saving.
                </p>
              </div>
              <Button onClick={checkZohoStatus} variant="outline" className="w-full">
                Refresh Status
              </Button>
            </div>
          )}

          {/* Test result */}
          {testResult && (
            <Alert className={testResult.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              <AlertDescription className={testResult.ok ? 'text-green-700' : 'text-red-700'}>
                {testResult.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="text-center pt-3">
            <Button variant="outline" asChild data-testid="back-dashboard-btn">
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Quote Request Component
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

// States within a sustainable delivery radius of Bangalore (~1200 km road).
// Bricks are heavy/low-value; shipping farther is neither economical nor eco-friendly,
// so far-north, north-east and island states are intentionally excluded as delivery
// destinations. Used for "Place of Supply" and "Delivery State" only — the billing /
// GST-registration dropdowns still offer the full INDIAN_STATES list, since a customer
// can be registered anywhere while taking delivery within range.
const DELIVERABLE_STATES = [
  "Andhra Pradesh", "Goa", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Odisha", "Puducherry", "Tamil Nadu", "Telangana"
];

/* The block atlas.

   It is a whole application in one file - a WebGL renderer, a bond solver and a
   drawing editor - and it is served from public/atlas/ rather than bundled. Two
   reasons, and the second is the one that matters:

   1. It stays a single self-contained document, so the same artifact can be
      handed to someone as a file or served from here.
   2. It builds its solver worker by reading its own inline script back out of
      the DOM and turning it into a Blob. That pattern does not survive a webpack
      build. Bundling it would mean rewriting the worker plumbing, on a
      deprecated react-scripts, for no gain.

   CRA copies public/ through untouched, so this costs nothing to keep current -
   drop in a new file and it ships. It is same-origin with this shell, which is
   what lets the atlas hand a take-off to /quote-request through sessionStorage.

   Full-viewport on purpose: it is a drawing board, and the app chrome would only
   take room from the canvas. */
const BlockAtlas = () => (
  <iframe
    src="/atlas/index.html"
    title="Earth Blocks wall atlas"
    style={{
      position: 'fixed', inset: 0, width: '100%', height: '100%',
      border: 0, display: 'block',
    }}
  />
);

const QuoteRequest = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [emailEstimate, setEmailEstimate] = useState(true);  // email the draft estimate to the customer on submit
  const [buildingPlans, setBuildingPlans] = useState([]);
  const planInputRef = React.useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customer_type: 'business',
    salutation: 'Mr.',
    first_name: '',
    last_name: '',
    company_name: '',
    display_name: '',
    email: '',
    mobile: '',
    gst_treatment: 'business_gst',
    gstin: '',
    place_of_supply: '',
    salesperson_name: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: '',
    billing_country: 'India',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip_code: '',
    delivery_country: 'India',
    google_maps_location: '',
    notes: '',
    line_items: []
  });

  const [selectedItems, setSelectedItems] = useState({});
  const [transportType, setTransportType] = useState('company');
  const [neededBy, setNeededBy] = useState('');   // requested first-delivery date (optional)
  const [gstValidating, setGstValidating] = useState(false);
  const [showGstModal, setShowGstModal] = useState(false);
  const [gstData, setGstData] = useState(null);
  const [editableGstData, setEditableGstData] = useState({
    company_name: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: '',
    place_of_supply: ''
  });
  const [openAccordions, setOpenAccordions] = useState({
    interlocking: true,
    octa: false,
    smart: false,
    solid: false,
    pointing: false
  });

  // Handle customer type change
  const handleCustomerTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      customer_type: type,
      // Auto-set GST treatment based on customer type
      gst_treatment: type === 'individual' ? 'business_none' : 'business_gst',
      // Clear GSTIN if switching to individual
      gstin: type === 'individual' ? '' : prev.gstin
    }));
  };

  // Categorize inventory items
  const categorizeItems = (items) => {
    const categories = {
      interlocking: { title: 'Hollow Interlocking Blocks - for 6" Walls', items: [], icon: '🔗' },
      octa: { title: 'Octa Interlocking Blocks - for 8" Walls', items: [], icon: '🔷', comingSoon: true, note: 'Will be available post Jan 2027' },
      smart: { title: 'Smart Earth Blocks - for 9" & 4.2" Walls', items: [], icon: '🧠' },
      solid: { title: 'Solid Earth Blocks - 9" or 7.5" Walls', items: [], icon: '🧱' },
      small: { title: '10 X 10 & 10x5 Bricks for 10" & 5" Walls', items: [], icon: '📏', onHold: true, note: ON_HOLD_NOTE },
      pointing: { title: 'Crushed Soil for Pointing', items: [], icon: '🪨' }
    };

    items.forEach(item => {
      // Only show active items with a SKU (backend enforces this too, but guard client-side)
      if ((item.status && item.status !== 'active') || !item.sku || item.sku.trim() === '') {
        return;
      }

      const name = item.name.toLowerCase();
      const sku = item.sku.toLowerCase();

      // Skip cement and other non-product items
      if (name.includes('cement')) {
        return;
      }

      // Add weight to item
      item.weight_kg = getBrickWeight(item.name);

      // Crushed soil for pointing
      if (name.includes('crushed') || name.includes('pointing') || name.includes('soil')) {
        categories.pointing.items.push(item);
      }
      // Octa blocks (before interlocking so "Octa" items don't fall into hollow)
      else if (name.includes('octa')) {
        categories.octa.items.push(item);
      }
      // Smart blocks - products with 'smart' in name
      else if (name.includes('smart')) {
        categories.smart.items.push(item);
      }
      // The 10x10 and its 10x5 half are on hold. The quote names the range and
      // says so - the section is declared below - but takes no items into it,
      // because nothing on a quote resolves a past order. Caught here by name
      // rather than left to fall through: see isTenByTen.
      else if (isTenByTen(name)) {
        return;
      }
      // Interlocking (hollow) blocks
      else if (name.includes('interlock') || name.includes('hollow') || name.includes('corner') || (name.includes('half') && !name.includes('solid') && !name.includes('smart'))) {
        categories.interlocking.items.push(item);
      }
      // Solid blocks (including Solid Half)
      else if (name.includes('solid')) {
        categories.solid.items.push(item);
      }
    });

    return categories;
  };

  const toggleAccordion = (category) => {
    setOpenAccordions(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Render brick item component
  const renderBrickItem = (item) => {
    const isSelected = selectedItems[item.id];
    
    return (
      <div
        key={item.id}
        className={`px-2.5 py-2 border rounded-md transition-all ${
          isSelected
            ? 'border-amber-400 bg-amber-50/50 shadow-sm'
            : 'border-stone-200 hover:border-amber-300'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h4 className="font-semibold text-sm text-stone-800">{item.name}</h4>
              {isSelected && (
                <span className="text-green-600 text-xs font-bold">✓</span>
              )}
            </div>
            <p className="text-[11px] text-stone-400 truncate">
              {item.sku} · {item.dimensions}
            </p>
          </div>
          <p className="text-sm font-bold text-amber-700 shrink-0">₹{item.rate}</p>
        </div>

        <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-stone-100">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-stone-500">Qty</Label>
            <Input
              type="number"
              min="0"
              placeholder="0"
              value={selectedItems[item.id]?.quantity || ''}
              className={`w-20 h-7 text-sm ${isSelected ? 'border-amber-400' : ''}`}
              onChange={(e) => handleItemQuantityChange(item, e.target.value)}
              data-testid={`qty-${item.id}`}
            />
          </div>
          {isSelected && (
            <p className="text-sm font-bold text-amber-800">
              ₹{(selectedItems[item.id].quantity * selectedItems[item.id].rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    document.title = "EarthBlocks | Customer Portal Blocks Estimate form";
    loadInventoryItems();
    // Salesperson is mandatory on Zoho estimates — customer picks their rep.
    axios.get(`${API}/zoho/salespersons`)
      .then(r => setSalespersons(r.data || []))
      .catch(() => setSalespersons([]));
  }, []);

  const loadInventoryItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/items`);
      setInventoryItems(response.data);
      applyTakeoffPrefill(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading inventory:', error);
      alert('Failed to load inventory items. Please check Zoho connection.');
      setLoading(false);
    }
  };

  // A quantity handed over from the block atlas, so a customer who has drawn a
  // wall does not retype what it needs. The atlas knows SKUs and counts; it does
  // not know rates, weights or item ids, so it passes only what it is sure of and
  // this page resolves the rest against the catalogue it just loaded.
  //
  // Deliberately routed through handleItemQuantityChange rather than writing
  // selectedItems directly: rate, weight and description are worked out there,
  // and a second copy of that logic would drift.
  const applyTakeoffPrefill = (catalogue) => {
    let raw;
    try { raw = sessionStorage.getItem('eb_takeoff'); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem('eb_takeoff'); } catch (e) { /* private mode */ }

    let payload;
    try { payload = JSON.parse(raw); } catch (e) { return; }
    if (!payload || !Array.isArray(payload.lines)) return;

    const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const missing = [];
    for (const line of payload.lines) {
      const item = catalogue.find(i => norm(i.sku) === norm(line.sku));
      if (!item) { missing.push(line.sku); continue; }
      handleItemQuantityChange(item, line.quantity);
    }

    if (payload.notes) {
      setFormData(prev => ({
        ...prev,
        notes: prev.notes ? prev.notes + '\n' + payload.notes : payload.notes,
      }));
    }
    // Say so rather than quietly quoting short - a missing line is worse than a
    // line the customer has to add themselves.
    if (missing.length) {
      alert('Some blocks from your drawing are not in the catalogue yet: '
            + missing.join(', ') + '.\nEverything else has been added below.');
    }
  };

  const validateGSTIN = async (gstin) => {
    if (!gstin || gstin.length !== 15) {
      alert('Please enter a valid 15-digit GSTIN');
      return;
    }

    setGstValidating(true);
    try {
      const response = await axios.get(`${API}/zoho/validate-gst/${gstin}`);
      
      if (response.data.valid && response.data.gst_details) {
        const fetchedGstData = response.data.gst_details;
        
        // Store raw GST data
        setGstData(fetchedGstData);
        
        // Populate editable fields with fetched data
        setEditableGstData({
          company_name: fetchedGstData.legal_name || fetchedGstData.trade_name || '',
          billing_address: fetchedGstData.address || '',
          billing_city: fetchedGstData.city || '',
          billing_state: fetchedGstData.state || '',
          billing_zip_code: fetchedGstData.pincode || '',
          place_of_supply: fetchedGstData.state || ''
        });
        
        // Show modal for editing
        setShowGstModal(true);
      }
    } catch (error) {
      console.error('Error validating GSTIN:', error);
      alert('Unable to fetch GST details from portal. Please check your GSTIN or fill manually.');
    } finally {
      setGstValidating(false);
    }
  };

  const confirmGstData = () => {
    // Apply edited GST data to form
    setFormData(prev => ({
      ...prev,
      company_name: editableGstData.company_name,
      billing_address: editableGstData.billing_address,
      billing_city: editableGstData.billing_city,
      billing_state: editableGstData.billing_state,
      billing_zip_code: editableGstData.billing_zip_code,
      place_of_supply: editableGstData.place_of_supply
    }));
    
    setShowGstModal(false);
    alert('✅ GST details applied to billing address!');
  };

  const cancelGstModal = () => {
    setShowGstModal(false);
    setGstData(null);
    setEditableGstData({
      company_name: '',
      billing_address: '',
      billing_city: '',
      billing_state: '',
      billing_zip_code: '',
      place_of_supply: ''
    });
  };

  const copyBillingToDelivery = () => {
    setFormData(prev => ({
      ...prev,
      delivery_address: prev.billing_address,
      delivery_city: prev.billing_city,
      delivery_state: prev.billing_state,
      delivery_zip_code: prev.billing_zip_code,
      delivery_country: prev.billing_country
    }));
    alert('✅ Billing address copied to delivery address');
  };

  const handleItemQuantityChange = (item, quantity) => {
    const qty = parseInt(quantity) || 0;
    if (qty > 0) {
      const weight = item.weight_kg || getBrickWeight(item.name);
      setSelectedItems(prev => ({
        ...prev,
        [item.id]: {
          item_id: item.zoho_item_id || item.id,
          name: item.name,
          quantity: qty,
          rate: parseFloat(item.rate),
          weight_kg: weight,
          total_weight: qty * weight,
          description: item.description
        }
      }));
    } else {
      setSelectedItems(prev => {
        const newItems = { ...prev };
        delete newItems[item.id];
        return newItems;
      });
    }
  };

  const calculateTotal = () => {
    return Object.values(selectedItems).reduce((sum, item) => {
      return sum + (item.quantity * item.rate);
    }, 0);
  };

  const calculateTotalWeight = () => {
    const totalKg = Object.values(selectedItems).reduce((sum, item) => {
      return sum + (item.total_weight || 0);
    }, 0);
    return {
      kg: totalKg,
      tons: (totalKg / 1000).toFixed(3)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const lineItems = Object.values(selectedItems);

    if (lineItems.length === 0) {
      alert('Please select at least one item with quantity');
      return;
    }

    // The loading charge as one summarized line - ₹1 per brick rounded to the
    // nearest ₹10 - added whenever our men load the customer's own vehicle,
    // which is Outstation and Porter both. See loadingChargeLine.
    const loadLine = loadingChargeLine(transportType, lineItems);
    const submitLineItems = loadLine ? [...lineItems, loadLine] : lineItems;

    setSubmitting(true);

    try {
      const quoteData = {
        ...formData,
        line_items: submitLineItems,
        transport_type: transportType,
        needed_by: neededBy || null,
        // For individuals, use billing state as place_of_supply if not set
        place_of_supply: formData.place_of_supply || formData.billing_state,
        email_estimate: emailEstimate && !!formData.email,
        ...(buildingPlans.length > 0 ? { building_plans: buildingPlans.map(bp => ({ filename: bp.name, content_type: bp.type, data_url: bp.dataUrl })) } : {}),
      };

      const response = await axios.post(`${API}/quotes/create`, quoteData);
      
      // Navigate to success page with quote data
      navigate('/quote-success', { 
        state: { 
          quoteData: response.data 
        } 
      });
      
    } catch (error) {
      console.error('Error creating quote:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create quote';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white shadow-sm border-b-2 border-amber-700 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 py-2.5">
            <div className="flex items-center space-x-3">
              <img
                src={EB_LOGO}
                alt="EarthBlocks"
                className="h-9 w-auto object-contain"
              />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-stone-800">Request a Quote</h1>
                <p className="text-[11px] text-amber-700 font-medium">Compressed Stabilized Earth Blocks</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild data-testid="reorder-btn">
                <Link to="/reorder">Reorder</Link>
              </Button>
              <Button variant="outline" size="sm" asChild data-testid="back-home-btn">
                <Link to="/">Home</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* GST Data Edit Modal */}
        {showGstModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  GST Details Retrieved - Review & Edit
                </CardTitle>
                <CardDescription>Review and edit the details fetched from GST portal before adding to form</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Alert className="border-green-200 bg-green-50">
                    <AlertDescription className="text-green-800 text-sm">
                      ✅ GST details successfully fetched! Review the information below and make any necessary edits before confirming.
                    </AlertDescription>
                  </Alert>

                  <div>
                    <Label htmlFor="modal_company_name">Company Name *</Label>
                    <Input
                      id="modal_company_name"
                      type="text"
                      value={editableGstData.company_name}
                      onChange={(e) => setEditableGstData(prev => ({...prev, company_name: e.target.value}))}
                      placeholder="Company name from GST"
                    />
                  </div>

                  <div>
                    <Label htmlFor="modal_billing_address">Billing Address *</Label>
                    <Input
                      id="modal_billing_address"
                      type="text"
                      value={editableGstData.billing_address}
                      onChange={(e) => setEditableGstData(prev => ({...prev, billing_address: e.target.value}))}
                      placeholder="Address from GST"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label htmlFor="modal_billing_city">City *</Label>
                      <Input
                        id="modal_billing_city"
                        type="text"
                        value={editableGstData.billing_city}
                        onChange={(e) => setEditableGstData(prev => ({...prev, billing_city: e.target.value}))}
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <Label htmlFor="modal_billing_state">State *</Label>
                      <select
                        id="modal_billing_state"
                        value={editableGstData.billing_state}
                        onChange={(e) => setEditableGstData(prev => ({...prev, billing_state: e.target.value}))}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="modal_billing_zip">Zip Code *</Label>
                      <Input
                        id="modal_billing_zip"
                        type="text"
                        value={editableGstData.billing_zip_code}
                        onChange={(e) => setEditableGstData(prev => ({...prev, billing_zip_code: e.target.value}))}
                        placeholder="Zip code"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="modal_place_of_supply">Place of Supply (State) *</Label>
                    <Input
                      id="modal_place_of_supply"
                      type="text"
                      value={editableGstData.place_of_supply}
                      onChange={(e) => setEditableGstData(prev => ({...prev, place_of_supply: e.target.value}))}
                      placeholder="Place of supply"
                    />
                  </div>

                  <Alert className="border-blue-200 bg-blue-50">
                    <AlertDescription className="text-blue-800 text-sm">
                      💡 <strong>Note:</strong> These details will be used to populate the billing address fields in the form. You can still edit them later if needed.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
              <CardContent className="p-6 pt-0 flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={cancelGstModal}>
                  Cancel
                </Button>
                <Button type="button" onClick={confirmGstData} className="bg-green-600 hover:bg-green-700">
                  Confirm & Add to Form
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-stone-200 shadow-sm">
          <CardHeader className="px-3 py-2.5 border-b border-stone-100">
            <CardTitle className="text-base text-stone-800">Quote Request</CardTitle>
            <CardDescription className="text-xs">Fill in your details and select bricks</CardDescription>
          </CardHeader>
          <CardContent className="px-3 py-3">
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Customer Type + Sales Representative — one row on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Customer Type */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Customer Type</h3>
                
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="customer_type"
                      value="business"
                      checked={formData.customer_type === 'business'}
                      onChange={(e) => handleCustomerTypeChange(e.target.value)}
                    />
                    <span>Business</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="customer_type"
                      value="individual"
                      checked={formData.customer_type === 'individual'}
                      onChange={(e) => handleCustomerTypeChange(e.target.value)}
                    />
                    <span>Individual</span>
                  </label>
                </div>
              </div>

              {/* Sales Representative — mandatory on Zoho estimates */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_select]:bg-white">
                <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Sales Representative</h3>
                <div>
                  <Label htmlFor="salesperson_name">Who assisted you? *</Label>
                  <select
                    id="salesperson_name"
                    value={formData.salesperson_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, salesperson_name: e.target.value }))}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                    data-testid="salesperson"
                  >
                    <option value="">Select representative</option>
                    {salespersons.map(sp => (
                      <option key={sp.salesperson_name} value={sp.salesperson_name}>{sp.salesperson_name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-stone-500 mt-1">Select the EarthBlocks representative who is helping you.</p>
                </div>
              </div>
              </div>

              {/* GST Details - Show only for Business */}
              {formData.customer_type === 'business' && (
                <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                  <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">GST Details</h3>
                  
                  <div>
                    <Label htmlFor="gst_treatment">GST Treatment *</Label>
                    <select
                      id="gst_treatment"
                      value={formData.gst_treatment}
                      onChange={(e) => setFormData(prev => ({...prev, gst_treatment: e.target.value}))}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="business_gst">Registered Business - Regular</option>
                      <option value="business_none">Unregistered Business</option>
                      <option value="consumer">Consumer</option>
                      <option value="overseas">Overseas</option>
                    </select>
                  </div>

                  {formData.gst_treatment === 'business_gst' && (
                    <div>
                      <Label htmlFor="gstin">GSTIN (15 digits) *</Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          id="gstin"
                          type="text"
                          value={formData.gstin}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                            setFormData(prev => ({...prev, gstin: value}));
                          }}
                          placeholder="22AAAAA0000A1Z5"
                          maxLength={15}
                          required={formData.gst_treatment === 'business_gst'}
                          data-testid="gstin"
                          autoCapitalize="characters"
                          autoComplete="off"
                          className="flex-1 uppercase"
                        />
                        <Button
                          type="button"
                          onClick={() => validateGSTIN(formData.gstin)}
                          disabled={gstValidating || !formData.gstin || !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(formData.gstin)}
                          className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                        >
                          {gstValidating ? 'Verifying...' : 'Verify & Update Address'}
                        </Button>
                      </div>
                      {formData.gstin && formData.gstin.length === 15 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(formData.gstin) && (
                        <p className="text-xs text-red-600 mt-1">Invalid GSTIN format. Expected: 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric</p>
                      )}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="place_of_supply">Place of Supply (State) *</Label>
                    <select
                      id="place_of_supply"
                      value={formData.place_of_supply}
                      onChange={(e) => setFormData(prev => ({...prev, place_of_supply: e.target.value}))}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md"
                      data-testid="place-of-supply"
                    >
                      <option value="">Select State</option>
                      {DELIVERABLE_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <p className="text-[11px] text-stone-500 mt-1">
                      🌱 We currently deliver to the {DELIVERABLE_STATES.length} states listed above — kept within a sustainable transport radius of our Bangalore factory.
                    </p>
                  </div>

                  {formData.gst_treatment === 'business_gst' && (
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertDescription className="text-amber-800 text-sm">
                        ℹ️ Enter your 15-digit GSTIN and click <strong>Verify & Update Address</strong> to auto-fill company name + billing address from the GST portal.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Auto GST Treatment for Individual */}
              {formData.customer_type === 'individual' && (
                <Alert className="border-stone-200 bg-stone-50">
                  <AlertDescription className="text-stone-600 text-xs">
                    GST Treatment set to: <strong>Unregistered Business</strong>
                  </AlertDescription>
                </Alert>
              )}

              {/* Primary Contact */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Primary Contact</h3>
                
                <div className="grid grid-cols-[4.5rem_1fr_1fr] gap-2">
                  <div>
                    <Label htmlFor="salutation" className="text-xs">Title</Label>
                    <select
                      id="salutation"
                      value={formData.salutation}
                      onChange={(e) => setFormData(prev => ({...prev, salutation: e.target.value}))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Dr.">Dr.</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({...prev, first_name: e.target.value}))}
                      required
                      placeholder="John"
                      data-testid="first-name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({...prev, last_name: e.target.value}))}
                      required
                      placeholder="Doe"
                      data-testid="last-name"
                    />
                  </div>
                </div>

                {formData.customer_type === 'business' && (
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({...prev, company_name: e.target.value}))}
                      required={formData.customer_type === 'business'}
                      placeholder="ABC Construction Pvt Ltd"
                      data-testid="company-name"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                      required
                      placeholder="john@example.com"
                      data-testid="customer-email"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="mobile">
                      Mobile / WhatsApp *
                    </Label>
                    <Input
                      id="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setFormData(prev => ({...prev, mobile: value}));
                        }
                      }}
                      required
                      maxLength={10}
                      pattern="[0-9]{10}"
                      placeholder="9876543210"
                      data-testid="customer-mobile"
                    />
                    {formData.mobile && formData.mobile.length < 10 && (
                      <p className="text-xs text-red-600 mt-1">Mobile number must be 10 digits</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Billing Address</h3>
                
                <div>
                  <Label htmlFor="billing_address">Address *</Label>
                  <Input
                    id="billing_address"
                    type="text"
                    value={formData.billing_address}
                    onChange={(e) => setFormData(prev => ({...prev, billing_address: e.target.value}))}
                    required
                    placeholder="Street address"
                    data-testid="billing-address"
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="billing_city">City *</Label>
                    <Input
                      id="billing_city"
                      type="text"
                      value={formData.billing_city}
                      onChange={(e) => setFormData(prev => ({...prev, billing_city: e.target.value}))}
                      required
                      placeholder="Bangalore"
                      data-testid="billing-city"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="billing_state">State *</Label>
                    <select
                      id="billing_state"
                      value={formData.billing_state}
                      onChange={(e) => setFormData(prev => ({...prev, billing_state: e.target.value}))}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md"
                      data-testid="billing-state"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="billing_zip_code">Zip Code *</Label>
                    <Input
                      id="billing_zip_code"
                      type="text"
                      value={formData.billing_zip_code}
                      onChange={(e) => setFormData(prev => ({...prev, billing_zip_code: e.target.value}))}
                      required
                      placeholder="560001"
                      data-testid="billing-zip"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                <div className="flex justify-between items-center border-b pb-2">
                  <h3 className="text-lg font-semibold">Delivery Address</h3>
                  <Button
                    type="button"
                    size="sm"
                    onClick={copyBillingToDelivery}
                    className="text-xs bg-amber-700 hover:bg-amber-800 text-white"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy from Billing
                  </Button>
                </div>
                
                <div>
                  <Label htmlFor="delivery_address">Address *</Label>
                  <Input
                    id="delivery_address"
                    type="text"
                    value={formData.delivery_address}
                    onChange={(e) => setFormData(prev => ({...prev, delivery_address: e.target.value}))}
                    required
                    placeholder="Street address"
                    data-testid="delivery-address"
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="delivery_city">City *</Label>
                    <Input
                      id="delivery_city"
                      type="text"
                      value={formData.delivery_city}
                      onChange={(e) => setFormData(prev => ({...prev, delivery_city: e.target.value}))}
                      required
                      placeholder="Bangalore"
                      data-testid="delivery-city"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="delivery_state">State *</Label>
                    <select
                      id="delivery_state"
                      value={formData.delivery_state}
                      onChange={(e) => setFormData(prev => ({...prev, delivery_state: e.target.value}))}
                      required
                      className="w-full p-2 border border-gray-300 rounded-md"
                      data-testid="delivery-state"
                    >
                      <option value="">Select State</option>
                      {DELIVERABLE_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="delivery_zip_code">Zip Code *</Label>
                    <Input
                      id="delivery_zip_code"
                      type="text"
                      value={formData.delivery_zip_code}
                      onChange={(e) => setFormData(prev => ({...prev, delivery_zip_code: e.target.value}))}
                      required
                      placeholder="560001"
                      data-testid="delivery-zip"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="google_maps_location">
                    Google Maps Location for Delivery
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="google_maps_location"
                      type="url"
                      value={formData.google_maps_location}
                      onChange={(e) => setFormData(prev => ({...prev, google_maps_location: e.target.value}))}
                      placeholder="https://maps.google.com/?q=12.9716,77.5946 or Google Maps share link"
                      data-testid="google-maps-location"
                      className="flex-1"
                    />
                    {formData.google_maps_location && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => window.open(formData.google_maps_location, '_blank')}
                        className="whitespace-nowrap"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open Map
                      </Button>
                    )}
                  </div>
                  <Alert className="mt-2 border-blue-200 bg-blue-50">
                    <AlertDescription className="text-blue-800 text-xs">
                      📍 <strong>How to get:</strong> Open Google Maps → Find delivery location → Click "Share" → Copy link and paste here. This helps the delivery team find the exact location easily.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>

              {/* Product Selection Section - Accordion Style */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Select Brick Types</h3>
                <p className="text-sm text-gray-600">Choose the brick types and quantities you need (organized by category)</p>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto mb-3"></div>
                      <p className="text-sm text-gray-500">Loading catalogue...</p>
                    </div>
                  </div>
                ) : inventoryItems.length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(categorizeItems(inventoryItems)).map(([categoryKey, category]) => {
                      /* On hold outranks everything: the section has items, and must still not
                         open. An empty one is the announced case - a flat row if we have said
                         the range is coming, nothing at all if we have not. The day the Octa
                         items go active in Books that section becomes an ordinary accordion
                         on its own, which is why comingSoon is only read when it is empty. */
                      if (category.onHold) return <NotSellingRow key={categoryKey} category={category} />;
                      if (category.items.length === 0) {
                        if (!category.comingSoon) return null;
                        return <NotSellingRow key={categoryKey} category={category} />;
                      }
                      
                      const isOpen = openAccordions[categoryKey];
                      const selectedInCategory = category.items.filter(item => selectedItems[item.id]).length;
                      
                      return (
                        <div key={categoryKey} className="border border-stone-200 rounded-lg overflow-hidden">
                          {/* Accordion Header */}
                          <button
                            type="button"
                            onClick={() => toggleAccordion(categoryKey)}
                            className="w-full flex items-center justify-between px-3 py-2.5 bg-stone-50 hover:bg-stone-100 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{category.icon}</span>
                              <div className="text-left">
                                <h4 className="font-semibold text-sm text-stone-800">{category.title}</h4>
                                <p className="text-[11px] text-stone-500">
                                  {category.items.length} product{category.items.length !== 1 ? 's' : ''}
                                  {selectedInCategory > 0 && (
                                    <span className="text-amber-700 font-semibold ml-1">
                                      · {selectedInCategory} selected
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <svg
                              className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {/* Accordion Content */}
                          {isOpen && (
                            <div className="px-2 py-1.5 bg-white space-y-1.5">
                              {category.note && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 font-medium">{category.note}</p>
                              )}
                              {category.items.map(item => renderBrickItem(item))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Alert className="border-orange-200 bg-orange-50">
                    <AlertDescription className="text-orange-800">
                      ⚠️ No inventory items found. Please sync from Zoho Books first or check your connection.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Summary Section */}
              {Object.keys(selectedItems).length > 0 && (
                <Card className="border border-stone-200 shadow-sm">
                  <CardContent className="px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Order Summary</h3>
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        {Object.keys(selectedItems).length} item(s)
                      </span>
                    </div>

                    <div className="space-y-0 divide-y divide-stone-100 mb-2">
                      {Object.values(selectedItems).map((item, idx) => (
                        <div key={idx} className="py-1.5 flex justify-between items-center">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-stone-800 truncate">{item.name}</p>
                            <p className="text-[11px] text-stone-400">
                              {item.quantity} pcs × ₹{item.rate} · {item.total_weight.toFixed(1)}kg
                            </p>
                          </div>
                          <span className="font-bold text-sm text-stone-800 shrink-0 ml-2">
                            ₹{(item.quantity * item.rate).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-amber-900 text-white rounded-lg px-3 py-2.5 space-y-2">
                      <div className="flex justify-between items-center pb-2 border-b border-amber-700">
                        <div>
                          <p className="text-amber-200 text-[11px]">Pieces</p>
                          <p className="text-base font-bold">
                            {Object.values(selectedItems).reduce((sum, item) => sum + item.quantity, 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-200 text-[11px]">Weight</p>
                          <p className="text-base font-bold">
                            {calculateTotalWeight().kg.toFixed(2)} kg
                          </p>
                          <p className="text-[10px] text-amber-300">
                            ({calculateTotalWeight().tons} T)
                          </p>
                        </div>
                      </div>

                      {/* Transport Options — Outstation and Porter both arrive as the
                          customer's own vehicle, loaded in our yard, so both
                          add a ₹1/brick loading charge to the estimate. */}
                      {calculateTotalWeight().kg > 0 && (
                        <div className="space-y-1.5 pb-2 border-b border-amber-700">
                          <p className="text-amber-200 text-[11px] font-medium uppercase tracking-wide">Transport</p>

                          {/* Option 1: EB Contracted Transport */}
                          <label className={`block rounded-md px-2.5 py-2 cursor-pointer transition-colors ${
                            transportType === 'company' ? 'bg-amber-700 ring-1 ring-amber-300' : 'bg-amber-800/60 hover:bg-amber-800'
                          }`}>
                            <div className="flex items-start gap-2">
                              <input
                                type="radio"
                                name="quote-transport"
                                checked={transportType === 'company'}
                                onChange={() => setTransportType('company')}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-xs">EB Contracted Transport (Kumar)</p>
                                <p className="text-amber-200 text-[11px]">Within 100 km · Loading + Unloading + Transport incl.</p>
                                <div className="mt-1 flex justify-between items-center text-[11px]">
                                  <span className="text-amber-200">Trips: <span className="font-bold text-white text-sm">{Math.ceil(calculateTotalWeight().kg / 12500)}</span></span>
                                  <span className="text-amber-300">Max 12.5T/trip</span>
                                </div>
                              </div>
                            </div>
                          </label>

                          {/* Option 2: Outstation Transport */}
                          <label className={`block rounded-md px-2.5 py-2 cursor-pointer transition-colors ${
                            transportType === 'outstation' ? 'bg-amber-700 ring-1 ring-amber-300' : 'bg-amber-800/60 hover:bg-amber-800'
                          }`}>
                            <div className="flex items-start gap-2">
                              <input
                                type="radio"
                                name="quote-transport"
                                checked={transportType === 'outstation'}
                                onChange={() => setTransportType('outstation')}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-xs">Outstation Transport</p>
                                <p className="text-amber-200 text-[11px]">Long distance · Loading by us · Unloading by client</p>
                                <div className="mt-1 flex justify-between items-center text-[11px]">
                                  <span className="text-amber-200">Loads: <span className="font-bold text-white text-sm">{Math.ceil(calculateTotalWeight().kg / 13000)}</span></span>
                                  <span className="text-yellow-300">+₹1/brick loading</span>
                                </div>
                              </div>
                            </div>
                          </label>

                          {/* Option 3: Porter — for very small quantities */}
                          <label className={`block rounded-md px-2.5 py-2 cursor-pointer transition-colors ${
                            transportType === 'porter' ? 'bg-amber-700 ring-1 ring-amber-300' : 'bg-amber-800/60 hover:bg-amber-800'
                          }`}>
                            <div className="flex items-start gap-2">
                              <input
                                type="radio"
                                name="quote-transport"
                                checked={transportType === 'porter'}
                                onChange={() => setTransportType('porter')}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-xs">Porter</p>
                                <p className="text-amber-200 text-[11px]">Small quantities · 100 kg – 4 T · Loading by us · Unloading by client</p>
                                <div className="mt-1 flex justify-between items-center text-[11px]">
                                  <span className="text-amber-200">Trips: <span className="font-bold text-white text-sm">{Math.ceil(calculateTotalWeight().kg / 4000)}</span></span>
                                  <span className="text-yellow-300">+₹1/brick loading</span>
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      )}

                      {/* Requested first-delivery date — printed on the estimate, becomes the order's needed-by */}
                      <div className="mt-2 rounded-md bg-amber-800/60 px-2.5 py-2">
                        <label className="block text-[11px] text-amber-200 mb-1" htmlFor="quote-needed-by">
                          When do you need the first load? <span className="text-amber-300">(optional)</span>
                        </label>
                        <input
                          id="quote-needed-by"
                          type="date"
                          min={localISODate()}
                          value={neededBy}
                          onChange={(e) => setNeededBy(e.target.value)}
                          className="w-full rounded px-2 py-1 text-sm text-stone-800 bg-white border border-amber-300"
                          data-testid="quote-needed-by"
                        />
                        <p className="text-[10px] text-amber-200 mt-1">Large orders go out as several loads — this is the date for the first one.</p>
                      </div>

                      {/* Loading charge preview whenever we load the vehicle — ₹1/brick rounded to ₹10 */}
                      {(() => {
                        const load = loadingChargeFor(transportType, Object.values(selectedItems));
                        if (load.amount <= 0) return null;
                        const totalBricks = load.bricks, chargeAmt = load.amount;
                        return (
                          <div className="flex justify-between items-center bg-amber-800/60 rounded-md px-2.5 py-1.5 text-xs">
                            <div>
                              <p className="text-white text-xs">Loading Charges</p>
                              <p className="text-[10px] text-amber-200">₹1 × {totalBricks.toLocaleString('en-IN')} bricks · rounded to ₹10</p>
                            </div>
                            <p className="font-semibold text-sm">₹{chargeAmt.toLocaleString('en-IN')}</p>
                          </div>
                        );
                      })()}

                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-amber-200 text-[11px]">Total Amount</p>
                          <p className="text-[10px] text-amber-300">Excl. GST</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold" data-testid="quote-total">
                            ₹{(() => {
                              const base = calculateTotal();
                              const load = loadingChargeFor(transportType, Object.values(selectedItems));
                              return (base + load.amount).toFixed(2);
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {Object.keys(selectedItems).length === 0 && (
                <div className="text-center py-3 text-xs text-stone-400 border border-dashed border-stone-200 rounded-lg">
                    Select brick types above to see order summary
                </div>
              )}

              {/* Notes Section */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100 [&_input]:bg-white [&_select]:bg-white">
                <Label htmlFor="notes">Additional Notes</Label>
                <Input
                  id="notes"
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                  placeholder="Any special requirements or instructions"
                  data-testid="quote-notes"
                />
              </div>

              {/* Building Plan Upload */}
              <div className="space-y-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white shadow-sm transition-all duration-200 focus-within:border-amber-300 focus-within:shadow-amber-100">
                <Label>Building Plan</Label>
                <p className="text-xs text-stone-500">Upload your floor plan or building drawing — helps us plan block requirements accurately. Preferred format: Architect's DXF or PDF.</p>
                <input
                  ref={planInputRef}
                  type="file"
                  accept="image/*,.pdf,.dxf"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (buildingPlans.length + files.length > 10) {
                      alert('Maximum 10 files allowed');
                      e.target.value = '';
                      return;
                    }
                    files.forEach(file => {
                      if (file.size > 5 * 1024 * 1024) { alert(`${file.name} exceeds 5 MB limit`); return; }
                      const reader = new FileReader();
                      reader.onload = () => setBuildingPlans(prev => [...prev, { name: file.name, type: file.type, dataUrl: reader.result }]);
                      reader.readAsDataURL(file);
                    });
                    e.target.value = '';
                  }}
                />
                {buildingPlans.length > 0 && (
                  <div className="space-y-1">
                    {buildingPlans.map((bp, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-stone-50 rounded border border-stone-200">
                        <span className="text-sm">📎</span>
                        <span className="text-xs text-stone-700 truncate flex-1">{bp.name}</span>
                        <button type="button" onClick={() => setBuildingPlans(prev => prev.filter((_, j) => j !== i))} className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                {buildingPlans.length < 10 && (
                  <Button type="button" variant="outline" size="sm" className="w-full text-xs" onClick={() => planInputRef.current?.click()}>
                    📄 {buildingPlans.length === 0 ? 'Upload Building Plan' : 'Add More Files'}
                  </Button>
                )}
              </div>

              {/* Email-the-estimate option (only when an email is entered) */}
              {formData.email && (
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={emailEstimate}
                    onChange={(e) => setEmailEstimate(e.target.checked)}
                    className="h-4 w-4 mt-0.5 rounded border-stone-300 text-amber-700 focus:ring-amber-500"
                    data-testid="email-estimate"
                  />
                  <span className="text-xs text-stone-600 leading-tight">
                    Email me this quote at <span className="font-medium text-stone-800">{formData.email}</span>
                    <span className="block text-[10px] text-stone-400">Draft copy — our sales rep will review &amp; confirm final pricing.</span>
                  </span>
                </label>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/')}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || Object.keys(selectedItems).length === 0}
                  data-testid="submit-quote-btn"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Quote...
                    </>
                  ) : 'Submit Quote Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ─── OTP flag — set to true to activate OTP gating ────────────────────────
const OTP_ENABLED = true;

// ─── Customer session token ───────────────────────────────────────────────
// The backend now scopes /customers/{id}/balance, /orders and /orders/reorder to
// a token bound to ONE contact_id. It travels in its own header rather than
// Authorization, because the global axios interceptor (App.js:42) owns that one
// for the STAFF jwt — a logged-in employee opening /reorder would otherwise send
// the wrong token type and get locked out of a public page.
const CUSTOMER_TOKEN_KEY = 'eb_customer_token';

const customerAuth = {
  save(contactId, token, expiresIn) {
    if (!token) return;
    try {
      localStorage.setItem(CUSTOMER_TOKEN_KEY, JSON.stringify({
        contact_id: contactId,
        token,
        // Expire ours a minute early so we re-verify rather than eat a 401.
        expires_at: Date.now() + ((expiresIn || 1800) - 60) * 1000,
      }));
    } catch {}
  },
  // Returns the token only if it is still valid AND belongs to this customer.
  get(contactId) {
    try {
      const s = JSON.parse(localStorage.getItem(CUSTOMER_TOKEN_KEY) || 'null');
      if (!s?.token || s.contact_id !== contactId) return null;
      if (Date.now() >= s.expires_at) return null;
      return s.token;
    } catch { return null; }
  },
  clear() {
    try { localStorage.removeItem(CUSTOMER_TOKEN_KEY); } catch {}
  },
  headers(contactId) {
    const t = this.get(contactId);
    return t ? { 'X-Customer-Token': t } : {};
  },
};

// The token outlived the tab: 401 (none/expired) or 403 (valid, wrong customer).
// Drop it so a stale one can't linger and re-fail on every subsequent screen.
const isCustomerAuthError = (err) => {
  const s = err?.response?.status;
  if (s !== 401 && s !== 403) return false;
  customerAuth.clear();
  return true;
};

// Reorder Form Component for Existing Customers
const ReorderForm = () => {
  // ── Search fields (tab-based: one active at a time) ──
  const [searchType, setSearchType] = useState('phone'); // 'phone' | 'email' | 'gstin'
  const [searchPhone, setSearchPhone] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchGstin, setSearchGstin] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // ── OTP states ──
  const [pendingCustomer, setPendingCustomer] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpDestinations, setOtpDestinations] = useState(null); // { mobile?: string, email?: string }

  // ── Customer / balance / orders ──
  const [customerData, setCustomerData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [balanceError, setBalanceError] = useState('');
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [ordersError, setOrdersError] = useState('');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // ── Order builder ──
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [customerRates, setCustomerRates] = useState({});
  // Last price this customer was given per item (from their full order history) —
  // any item they pick auto-uses this so negotiated pricing carries over.
  const [historicalRates, setHistoricalRates] = useState({});
  const [referenceEstimateId, setReferenceEstimateId] = useState(null);
  const [emailEstimate, setEmailEstimate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  // Reorder-Same-Products preview: shows loading-charge breakdown + Confirm Order button.
  const [reorderPreviewOrderId, setReorderPreviewOrderId] = useState(null);
  const [confirmingReorder, setConfirmingReorder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transportType, setTransportType] = useState('company');
  const [neededBy, setNeededBy] = useState('');   // requested first-delivery date (optional)
  const [openAccordions, setOpenAccordions] = useState({
    interlocking: true, octa: false, smart: false, solid: false, small: false, pointing: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "EarthBlocks | Customer Portal Blocks Reorder form";
    loadInventoryItems();
  }, []);

  // Restore saved customer session (persistent login) — the customer stays "logged in"
  // across visits and after installing as a PWA. To sign out, use the button in the header.
  //
  // The saved record outlives the token (30 min), so a returning visitor almost
  // always needs a fresh one. With OTP off we re-mint silently; with OTP on there
  // is nothing to re-mint from without a code, so the session is dropped and they
  // identify again — which is exactly what "OTP on" is supposed to mean.
  useEffect(() => {
    (async () => {
      let c = null;
      try {
        c = JSON.parse(localStorage.getItem('eb_customer') || 'null');
      } catch {}
      if (!c?.contact_id) return;

      if (!customerAuth.get(c.contact_id)) {
        if (OTP_ENABLED) {
          customerAuth.clear();
          try { localStorage.removeItem('eb_customer'); } catch {}
          return;
        }
        try {
          await mintCustomerToken(c);
        } catch {
          // Backend unreachable or refused — leave them at the search step
          // rather than rendering a page whose every fetch will 401.
          customerAuth.clear();
          return;
        }
      }
      confirmCustomer(c);
    })();
  }, []);

  // When customer is found, load their balance and order history
  useEffect(() => {
    if (customerData?.contact_id) {
      loadCustomerBalance(customerData.contact_id);
      loadPreviousOrders(customerData.contact_id, customerData.contact_name);
    }
  }, [customerData?.contact_id]);

  // Build a "last price per item" map from the customer's order history (newest first),
  // so any item they pick reuses the most recent price they were given. Items with no
  // history fall back to the catalog rate and get flagged for the rep to reconfirm.
  useEffect(() => {
    if (!previousOrders.length || !inventoryItems.length) { setHistoricalRates({}); return; }
    const allInv = Object.values(categorizeItems(inventoryItems)).flatMap(c => c.items);
    const rates = {};
    previousOrders.forEach(order => {
      (order.line_items || []).forEach(li => {
        const inv = allInv.find(i =>
          (li.item_id && i.zoho_item_id === li.item_id) ||
          i.name.toLowerCase().trim() === (li.name || '').toLowerCase().trim()
        );
        if (inv && rates[inv.id] == null && li.rate != null && Number(li.rate) > 0) {
          rates[inv.id] = Number(li.rate);
        }
      });
    });
    setHistoricalRates(rates);
  }, [previousOrders, inventoryItems]);

  const categorizeItems = (items) => {
    const categories = {
      interlocking: { title: 'Hollow Interlocking Blocks - for 6" Walls', items: [], icon: '🔗' },
      octa: { title: 'Octa Interlocking Blocks - for 8" Walls', items: [], icon: '🔷', comingSoon: true, note: 'Will be available post Jan 2027' },
      smart: { title: 'Smart Earth Blocks - for 9" & 4.2" Walls', items: [], icon: '🧠' },
      solid: { title: 'Solid Earth Blocks - 9" or 7.5" Walls', items: [], icon: '🧱' },
      small: { title: '10 X 10 & 10x5 Bricks for 10" & 5" Walls', items: [], icon: '📏', onHold: true, note: ON_HOLD_NOTE },
      pointing: { title: 'Crushed Soil for Pointing', items: [], icon: '🪨' }
    };
    items.forEach(item => {
      if ((item.status && item.status !== 'active') || !item.sku || item.sku.trim() === '') return;
      const name = item.name.toLowerCase();
      if (name.includes('cement')) return;
      item.weight_kg = getBrickWeight(item.name);
      if (name.includes('crushed') || name.includes('pointing') || name.includes('soil')) categories.pointing.items.push(item);
      else if (name.includes('octa')) categories.octa.items.push(item);
      else if (name.includes('smart')) categories.smart.items.push(item);
      else if (isTenByTen(name)) categories.small.items.push(item);
      else if (name.includes('interlock') || name.includes('hollow') || name.includes('corner') || (name.includes('half') && !name.includes('solid') && !name.includes('smart'))) categories.interlocking.items.push(item);
      else if (name.includes('solid')) categories.solid.items.push(item);
    });
    return categories;
  };

  const loadInventoryItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/items`);
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerBalance = async (contactId) => {
    if (!contactId) {
      setBalanceError('Account not linked to Zoho Books. Balance unavailable.');
      return;
    }
    setLoadingBalance(true);
    setBalanceError('');
    try {
      const resp = await axios.get(`${API}/customers/${contactId}/balance`,
                                   { headers: customerAuth.headers(contactId) });
      // Always set data — endpoint never throws, may include a warning
      setBalanceData(resp.data);
      if (resp.data.error) {
        console.warn('Balance warning:', resp.data.error);
        setBalanceError(resp.data.error);
      }
    } catch (err) {
      // Should not happen with the new resilient endpoint, but guard anyway
      console.error('Balance fetch failed:', err);
      if (isCustomerAuthError(err)) {
        setBalanceError(err.response?.data?.detail || 'Session expired. Please search again.');
      } else {
        setBalanceData({ credit_balance: 0, outstanding: 0, currency_symbol: '₹' });
      }
    } finally {
      setLoadingBalance(false);
    }
  };

  const loadPreviousOrders = async (contactId, customerName) => {
    if (!contactId) {
      setOrdersError('Account not linked to Zoho Books. Order history unavailable.');
      return;
    }
    setLoadingOrders(true);
    setOrdersError('');
    try {
      const params = new URLSearchParams({ limit: 8 });
      if (customerName) params.append('customer_name', customerName);
      const resp = await axios.get(`${API}/customers/${contactId}/orders?${params.toString()}`,
                                   { headers: customerAuth.headers(contactId) });
      setPreviousOrders(resp.data.orders || []);
      if (resp.data.error) {
        console.warn('Orders warning:', resp.data.error);
        if ((resp.data.orders || []).length === 0) setOrdersError(resp.data.error);
      }
    } catch (err) {
      console.error('Orders fetch failed:', err);
      setPreviousOrders([]);
      if (isCustomerAuthError(err)) {
        setOrdersError(err.response?.data?.detail || 'Session expired. Please search again.');
      }
    } finally {
      setLoadingOrders(false);
    }
  };

  const searchCustomer = async () => {
    const phone = searchPhone.trim();
    const email = searchEmail.trim();
    const gstin = searchGstin.trim();
    if (!phone && !email && !gstin) {
      setSearchError('Please enter at least one of: Mobile Number, Email, or GSTIN.');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const params = new URLSearchParams();
      if (phone) params.append('phone', phone);
      if (email) params.append('email', email);
      if (gstin) params.append('gstin', gstin);

      const response = await axios.get(`${API}/customers/search?${params.toString()}`);
      if (response.data.found) {
        const customer = response.data.customer;
        if (OTP_ENABLED && customer.mobile) {
          // Store customer but gate behind OTP
          setPendingCustomer(customer);
          await doSendOtp(customer.mobile, customer.contact_id);
        } else {
          // OTP disabled — still mint the token, because the endpoints require
          // one either way. It is a binding, not a proof: it stops one request
          // walking every contact_id, and flipping OTP_ENABLED on later turns
          // the same call into a real check with no further code change.
          await mintCustomerToken(customer);
          confirmCustomer(customer);
        }
      } else {
        setSearchError('No account found with these details. First-time customer? Use New Quote Request.');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const confirmCustomer = (customer) => {
    setCustomerData(customer);
    setSelectedItems({});
    setCustomerRates({});
    setReferenceEstimateId(null);
    setOrderResult(null);
    setPendingCustomer(null);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpValue('');
    setOtpError('');
    setOtpDestinations(null);
    // Persist for future visits (PWA "saved access")
    try {
      localStorage.setItem('eb_customer', JSON.stringify({
        contact_id: customer.contact_id,
        contact_name: customer.contact_name,
        contact_number: customer.contact_number || null,
        mobile: customer.mobile || null,
        email: customer.email || null,
        gst_treatment: customer.gst_treatment || null,
        gstin: customer.gstin || null,
      }));
    } catch {}
  };

  const signOutCustomer = () => {
    customerAuth.clear();
    try { localStorage.removeItem('eb_customer'); } catch {}
    setCustomerData(null);
    setBalanceData(null);
    setBalanceError('');
    setPreviousOrders([]);
    setSelectedItems({});
    setCustomerRates({});
    setReferenceEstimateId(null);
    setOrderResult(null);
    setSearchPhone('');
    setSearchEmail('');
    setSearchGstin('');
    setSearchError('');
  };

  // ── OTP functions ────────────────────────────────────────────────────────
  const doSendOtp = async (mobile, contactId) => {
    setSendingOtp(true);
    setOtpError('');
    try {
      const resp = await axios.post(`${API}/customers/send-otp`, { mobile, contact_id: contactId });
      if (resp.data?.sent === false) {
        // Backend accepted request but couldn't deliver (SMS/Email/WhatsApp send failed)
        setOtpError(resp.data.message || 'Could not send OTP. Please try again.');
        setOtpSent(false);
        setOtpDestinations(null);
        return;
      }
      setOtpDestinations(resp.data?.destinations || null);
      setOtpSent(true);
    } catch (err) {
      setOtpError('Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  // Exchange proof-of-mobile for the contact-scoped token the read/write
  // endpoints now require. With OTP off the backend ignores the code and trusts
  // contact_id; with OTP on it only answers to a code it actually sent.
  const mintCustomerToken = async (customer, otpCode = '') => {
    const resp = await axios.post(`${API}/customers/verify-otp`, {
      mobile: customer.mobile || '',
      otp_code: otpCode,
      contact_id: customer.contact_id,
    });
    if (resp.data?.verified && resp.data?.customer_token) {
      customerAuth.save(customer.contact_id, resp.data.customer_token, resp.data.expires_in);
    }
    return resp.data;
  };

  const doVerifyOtp = async () => {
    if (!otpValue.trim() || otpValue.trim().length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      return;
    }
    setVerifyingOtp(true);
    setOtpError('');
    try {
      const data = await mintCustomerToken(pendingCustomer, otpValue.trim());
      if (data.verified) {
        setOtpVerified(true);
        confirmCustomer(pendingCustomer);
      } else {
        setOtpError(data.message || 'Incorrect OTP. Please try again.');
      }
    } catch (err) {
      // 429 = attempt cap. Show what the server said; a generic retry message
      // here would have people hammering a door that is already bolted.
      setOtpError(err.response?.status === 429
        ? (err.response?.data?.detail || 'Too many attempts. Please wait a few minutes.')
        : 'Verification failed. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Pre-fill the order builder from any previous invoice or estimate, using its rates
  // Loading labor charge: ₹1 per brick when the order has more than 30 bricks,
  // rounded to the nearest ₹10. This is the Reorder-Same-Products quick flow's
  // own rule, 30-brick floor and all; everything that goes through the transport
  // picker is charged by loadingChargeFor instead.
  const computeLoadingCharge = (bricks) => {
    if (!bricks || bricks <= 30) return 0;
    return Math.round(bricks / 10) * 10;
  };
  // Historical line items may include their own "Loading Charges" row — strip
  // those before recomputing (so we don't stack an old charge on top of the new one).
  const isLoadingChargeLine = (li) => /loading\s+charge/i.test(li?.name || '');

  // Reorder-Same-Products confirm: submit directly (skips the transport picker;
  // defaults to Company transport). Adds a loading-charge line if applicable.
  const confirmReorder = async (order) => {
    if (!customerData?.contact_id) { alert('Customer is not linked to a Zoho Books account.'); return; }
    const orderable = (order.line_items || [])
      .filter(li => !isLoadingChargeLine(li) && Number(li.quantity) > 0);
    // The hold has to bite here, not only on the product list. This button
    // submits the old record's lines straight through and never consults the
    // catalogue, so a range taken off the shelf would still sell from it.
    const held = [...new Set(orderable.filter(li => isTenByTen(li.name)).map(li => li.name))];
    const items = orderable
      .filter(li => !isTenByTen(li.name))
      .map(li => ({
        item_id: li.item_id || '',
        name: li.name,
        quantity: Number(li.quantity),
        rate: Number(li.rate),
        unit: 'Nos',
      }));
    if (held.length && items.length === 0) {
      alert(`${held.join('\n')}\n\nSales are on hold, so there is nothing left on this order to repeat.`);
      return;
    }
    if (items.length === 0) { alert('No orderable items on this record.'); return; }
    if (held.length && !window.confirm(
      `On hold and will not be included:\n\n${held.join('\n')}\n\nPlace the order without ${held.length === 1 ? 'it' : 'them'}?`
    )) return;
    const totalBricks = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
    const loadingAmount = computeLoadingCharge(totalBricks);
    if (loadingAmount > 0) {
      items.push({
        item_id: '',
        name: 'Loading Charges',
        quantity: 1,
        rate: loadingAmount,
        unit: 'Nos',
        description: `Loading @ ₹1 per brick × ${totalBricks.toLocaleString('en-IN')} bricks (rounded to nearest ₹10)`,
      });
    }
    setConfirmingReorder(true);
    try {
      const spOrder = previousOrders.find(o => o.salesperson_id || o.salesperson_name);
      const ref = order.record_id || order.estimate_id || order.invoice_number || order.estimate_number || '';
      const resp = await axios.post(`${API}/orders/reorder`, {
        contact_id: customerData.contact_id,
        contact_name: customerData.contact_name,
        contact_number: customerData.contact_number || '',
        salesperson_id: spOrder?.salesperson_id || '',
        salesperson_name: spOrder?.salesperson_name || '',
        reference_estimate_id: ref,
        line_items: items,
        notes: `Reorder via EarthBlocks Portal (based on ${order.invoice_number || order.estimate_number || ref})`,
        transport_type: 'company',
        needed_by: null,
        email_estimate: !!customerData.email,
        customer_email: customerData.email || '',
      }, { headers: customerAuth.headers(customerData.contact_id) });
      setOrderResult(resp.data);
      setReorderPreviewOrderId(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to place order');
    } finally {
      setConfirmingReorder(false);
    }
  };

  const repeatOrder = (order) => {
    const newSelected = {};
    const newRates = {};
    const held = [];
    const cats = categorizeItems(inventoryItems);

    order.line_items.forEach(li => {
      if (!li.quantity || li.quantity <= 0) return;
      // A line we have taken off sale is named, not dropped. Rebuilding the
      // order one item short without saying so is how a customer finds out
      // about the hold at delivery.
      if (isTenByTen(li.name)) { held.push(li.name); return; }
      // Match live inventory item by zoho_item_id or name (case-insensitive)
      let invItem = null;
      for (const cat of Object.values(cats)) {
        invItem = cat.items.find(i =>
          (li.item_id && i.zoho_item_id === li.item_id) ||
          i.name.toLowerCase().trim() === li.name.toLowerCase().trim()
        );
        if (invItem) break;
      }
      if (invItem) {
        const weight = invItem.weight_kg || getBrickWeight(invItem.name);
        newSelected[invItem.id] = {
          item_id: invItem.zoho_item_id || invItem.id,
          name: invItem.name,
          quantity: li.quantity,
          rate: li.rate,       // use customer's negotiated rate from this record
          weight_kg: weight,
          total_weight: li.quantity * weight,
          description: invItem.description,
        };
        newRates[invItem.id] = li.rate;
      }
    });

    setSelectedItems(newSelected);
    setCustomerRates(newRates);
    // Use record_id (new field) or fallback to legacy estimate_id
    setReferenceEstimateId(order.record_id || order.estimate_id);

    // Auto-open categories that have items
    const openState = { interlocking: false, octa: false, smart: false, solid: false, small: false, pointing: false };
    const categorized = categorizeItems(inventoryItems);
    Object.entries(categorized).forEach(([key, cat]) => {
      if (cat.items.some(i => newSelected[i.id])) openState[key] = true;
    });
    setOpenAccordions(openState);
    if (held.length) {
      alert(`On hold, so not added to this order:\n\n${[...new Set(held)].join('\n')}\n\nEverything else from that order is ready to send.`);
    }
    // Scroll to product section
    setTimeout(() => document.getElementById('product-builder')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleItemQuantityChange = (item, quantity) => {
    const qty = parseInt(quantity) || 0;
    // Price priority: explicit "reorder same" rate → last price from history → catalog rate
    const rate = customerRates[item.id] != null ? customerRates[item.id]
               : historicalRates[item.id] != null ? historicalRates[item.id]
               : parseFloat(item.rate);
    if (qty > 0) {
      const weight = item.weight_kg || getBrickWeight(item.name);
      setSelectedItems(prev => ({
        ...prev,
        [item.id]: {
          item_id: item.zoho_item_id || item.id,
          name: item.name,
          quantity: qty,
          rate,
          weight_kg: weight,
          total_weight: qty * weight,
          description: item.description,
        }
      }));
    } else {
      setSelectedItems(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    }
  };

  // The bar and the submitted estimate now read the same helper, so they
  // cannot round the charge differently from each other.
  const calculateLoadingCharge = () => loadingChargeFor(transportType, Object.values(selectedItems)).amount;
  const calculateTotal = () =>
    Object.values(selectedItems).reduce((s, i) => s + i.quantity * i.rate, 0)
    + calculateLoadingCharge();
  // Transport options — capacity + label per option.
  //   company    = own trucks used for < 100 km deliveries (max 12.5 T per trip)
  //   outstation = larger trucks used for longer hauls (30–40 T, we plan on 35)
  //   porter     = the customer's own hired vehicle, 100 kg to 4 T
  // Outstation and Porter are loaded by us and unloaded by the client, so both
  // carry the ₹1/brick loading charge. On a company truck the trip rate has it.
  const TRANSPORT_OPTIONS = {
    company:    { label: 'EB Contracted Transport (Kumar)', sub: 'up to 100 km · max 12.5 T per trip', capacityTons: 12.5, short: 'EB Transport' },
    outstation: { label: 'Outstation transport', sub: '13 – 40 T per truck · long hauls',  capacityTons: 13, short: 'Outstation', fee: '+₹1/brick loading' },
    porter:     { label: 'Porter',               sub: 'small quantities · 100 kg – 4 T',  capacityTons: 4, short: 'Porter', fee: '+₹1/brick loading' },
  };
  const truckCapacityTons = TRANSPORT_OPTIONS[transportType]?.capacityTons || 20;
  const calculateTotalWeight = () => {
    const kg = Object.values(selectedItems).reduce((s, i) => s + (i.total_weight || 0), 0);
    const tonsNum = kg / 1000;
    return {
      kg,
      tons: tonsNum.toFixed(3),
      tonsNum,
      trucks: tonsNum > 0 ? Math.ceil(tonsNum / truckCapacityTons) : 0,
    };
  };
  const calculateAmountToDeposit = () => {
    const credit = balanceData?.credit_balance || 0;
    return Math.max(0, calculateTotal() - credit);
  };

  const toggleAccordion = (k) => setOpenAccordions(prev => ({ ...prev, [k]: !prev[k] }));

  const handleSubmit = async () => {
    const lineItems = Object.values(selectedItems);
    if (lineItems.length === 0) { alert('Please select at least one product'); return; }
    if (!customerData?.contact_id) { alert('Customer is not linked to a Zoho Books account. Cannot create the quote.'); return; }

    // The loading charge as one summarized line - ₹1 per brick rounded to the
    // nearest ₹10 - added whenever our men load the customer's own vehicle,
    // which is Outstation and Porter both. See loadingChargeLine.
    const loadLine = loadingChargeLine(transportType, lineItems);
    const submitLineItems = loadLine ? [...lineItems, loadLine] : lineItems;

    setSubmitting(true);
    try {
      const spOrder = previousOrders.find(o => o.salesperson_id || o.salesperson_name);
      // Items with no prior price on file (using catalog rate) → rep must reconfirm.
      const reconfirmItems = Object.keys(selectedItems)
        .filter(id => customerRates[id] == null && historicalRates[id] == null)
        .map(id => selectedItems[id].name);
      const resp = await axios.post(`${API}/orders/reorder`, {
        contact_id: customerData.contact_id,
        contact_name: customerData.contact_name,
        contact_number: customerData.contact_number || '',
        salesperson_id: spOrder?.salesperson_id || '',
        salesperson_name: spOrder?.salesperson_name || '',
        reference_estimate_id: referenceEstimateId,
        line_items: submitLineItems,
        notes: referenceEstimateId
          ? `Reorder via EarthBlocks Portal (based on ${referenceEstimateId})`
          : 'Reorder via EarthBlocks Customer Portal',
        transport_type: transportType,
        needed_by: neededBy || null,
        price_reconfirm_items: reconfirmItems,
        email_estimate: emailEstimate && !!customerData.email,
        customer_email: customerData.email || '',
      }, { headers: customerAuth.headers(customerData.contact_id) });
      setOrderResult(resp.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit reorder. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render: brick item row ─────────────────────────────────────────────
  const renderBrickItem = (item) => {
    const sel = selectedItems[item.id];
    // Price this customer gets: explicit "reorder same" rate → last price from history → catalog
    const priorRate = customerRates[item.id] != null ? customerRates[item.id] : historicalRates[item.id];
    const displayRate = priorRate != null ? priorRate : parseFloat(item.rate);
    const fromHistory = priorRate != null;                  // reusing a price they were given before
    const needsReconfirm = !fromHistory;                    // no prior price → catalog rate, rep to confirm
    return (
      <div key={item.id} className={`px-2.5 py-2 border rounded-md transition-all ${sel ? 'border-amber-400 bg-amber-50/50 shadow-sm' : 'border-stone-200 hover:border-amber-300'}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h4 className="font-semibold text-sm text-stone-800">{item.name}</h4>
              {sel && <span className="text-green-600 text-xs font-bold">✓</span>}
              {fromHistory && <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded font-medium border border-amber-200">Your price</span>}
              {needsReconfirm && <span className="text-[10px] text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded font-medium border border-orange-200">New price · rep to confirm</span>}
            </div>
            <p className="text-[11px] text-stone-400">{item.sku} · {item.dimensions} · {item.weight_kg ?? getBrickWeight(item.name)}kg/pc</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm font-bold text-amber-700">₹{displayRate.toFixed(2)}</p>
            {fromHistory && Number(priorRate) !== parseFloat(item.rate) && <p className="text-[10px] text-stone-400 line-through">₹{parseFloat(item.rate).toFixed(2)}</p>}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-stone-100">
          <div className="flex items-center gap-1">
            <button type="button" className="w-6 h-6 rounded-full border border-stone-300 text-stone-500 hover:bg-stone-100 font-bold text-xs flex items-center justify-center"
              onClick={() => handleItemQuantityChange(item, Math.max(0, (sel?.quantity || 0) - 10))}>−</button>
            <Input type="number" min="0" step="10" placeholder="0"
              value={sel?.quantity || ''}
              className={`w-16 h-7 text-center text-sm ${sel ? 'border-amber-400' : ''}`}
              onChange={(e) => handleItemQuantityChange(item, e.target.value)} />
            <button type="button" className="w-6 h-6 rounded-full border border-stone-300 text-stone-500 hover:bg-stone-100 font-bold text-xs flex items-center justify-center"
              onClick={() => handleItemQuantityChange(item, (sel?.quantity || 0) + 10)}>+</button>
          </div>
          {sel && (
            <p className="text-sm font-bold text-amber-800">
              ₹{(sel.quantity * sel.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              <span className="text-[10px] text-stone-400 font-normal ml-1">{(sel.total_weight || 0).toFixed(1)}kg</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  // ─── Render: order success ───────────────────────────────────────────────
  if (orderResult) return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl shadow-2xl">
        <CardContent className="p-8 text-center">
          <div className="bg-green-500 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">Reorder Placed!</h2>
          <p className="text-gray-600 mb-6">Your order has been submitted and a quote has been created in Zoho Books.</p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Quote Number</span>
              <span className="font-bold text-green-800">{orderResult.estimate_number || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Customer</span>
              <span className="font-semibold">{customerData?.contact_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Order Total</span>
              <span className="font-bold text-lg text-green-800">₹{orderResult.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Status</span>
              <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">{orderResult.status || 'draft'}</Badge>
            </div>
            {orderResult.email_sent && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Emailed to you</span>
                <span className="font-medium text-green-700">✓ Sent</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-6">Our team will review and confirm your order. You'll be contacted shortly.</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setOrderResult(null); setSelectedItems({}); setCustomerRates({}); setReferenceEstimateId(null); setEmailEstimate(false); }}>
              Place Another Order
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const orderTotal = calculateTotal();
  const credit = balanceData?.credit_balance || 0;
  const outstanding = balanceData?.outstanding || 0;
  const amountToDeposit = calculateAmountToDeposit();
  const weight = calculateTotalWeight();
  const selectedCount = Object.keys(selectedItems).length;

  // ─── Render: main page ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-stone-50 pb-48">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-2 border-amber-700 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 py-2.5">
            <div className="flex items-center space-x-3">
              <img src={EB_LOGO} alt="EarthBlocks" className="h-9 w-auto object-contain shrink-0" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-stone-800">Reorder</h1>
                <p className="text-[11px] text-amber-700 font-medium">Quick reorder for existing customers</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild><Link to="/quote-request">New Quote</Link></Button>
              <Button variant="outline" size="sm" asChild><Link to="/">Home</Link></Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-4 space-y-3">

        {/* ── Step 1: Find Your Account ── */}
        <Card className="border border-stone-200 shadow-sm">
          <CardHeader className="px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="bg-amber-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">1</span>
              <CardTitle className="text-sm text-stone-800">Find Your Account</CardTitle>
              {!customerData && !pendingCustomer && (
                <span className="text-[11px] text-stone-400 ml-1">— search by phone, email, or GST</span>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            {/* ── Customer confirmed ── */}
            {customerData ? (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 rounded-full p-2.5 mt-0.5 shrink-0">
                    <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-900">{customerData.contact_name}</p>
                      <Badge className="bg-green-100 text-green-700 border border-green-300 text-xs">✓ Verified</Badge>
                    </div>
                    {customerData.company_name && <p className="text-sm text-gray-600">{customerData.company_name}</p>}
                    <p className="text-xs text-gray-500 mt-0.5">
                      {customerData.mobile && <span>📱 {customerData.mobile}</span>}
                      {customerData.email && <span className="ml-2">✉ {customerData.email}</span>}
                    </p>
                    {customerData.gstin && <p className="text-xs text-gray-500">GST: {customerData.gstin}</p>}
                    <p className="text-xs text-gray-400">
                      {customerData.contact_number && <span className="font-medium text-gray-600">{customerData.contact_number}</span>}
                      {customerData.contact_number && customerData.contact_id && <span className="mx-1">·</span>}
                      {customerData.contact_id
                        ? <span>ID: {customerData.contact_id}</span>
                        : <span className="text-red-500 font-medium">⚠ Zoho account not linked — reorder unavailable</span>
                      }
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => {
                    setSelectedItems({}); setCustomerRates({}); setReferenceEstimateId(null); setOrderResult(null);
                  }}>
                    Reset Order
                  </Button>
                  <Button variant="outline" size="sm" onClick={signOutCustomer}>
                    Not you? Sign out
                  </Button>
                </div>
              </div>

            ) : pendingCustomer && OTP_ENABLED ? (
              /* ── OTP Step (only when OTP_ENABLED = true) ── */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900">Account found — verify your identity</p>
                  {(() => {
                    const dests = otpDestinations || {};
                    const mobileMask = dests.mobile || (pendingCustomer.mobile ? `+91-XXXXXX${(pendingCustomer.mobile || '').slice(-4)}` : '');
                    const emailMask = dests.email || '';
                    const parts = [];
                    if (mobileMask) parts.push(<strong key="m">{mobileMask}</strong>);
                    if (emailMask) parts.push(<strong key="e">{emailMask}</strong>);
                    if (parts.length === 0) {
                      return <p className="text-xs text-blue-700 mt-0.5">An OTP has been sent.</p>;
                    }
                    return (
                      <p className="text-xs text-blue-700 mt-0.5">
                        An OTP has been sent to {parts.reduce((acc, node, i) => (
                          i === 0 ? [node] : [...acc, ' and ', node]
                        ), [])}
                      </p>
                    );
                  })()}
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Enter 6-digit OTP</Label>
                    <Input className="mt-1 text-center text-lg tracking-[0.5em] font-mono" type="text"
                      inputMode="numeric" maxLength={6}
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={(e) => e.key === 'Enter' && doVerifyOtp()}
                      placeholder="••••••"
                    />
                  </div>
                  <Button onClick={doVerifyOtp} disabled={verifyingOtp || otpValue.length !== 6}
                    className="bg-green-600 hover:bg-green-700 h-9 shrink-0">
                    {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                </div>
                {otpError && <p className="text-xs text-red-600">{otpError}</p>}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>Didn't receive it?</span>
                  <button type="button" disabled={sendingOtp}
                    className="text-blue-600 underline disabled:opacity-50"
                    onClick={() => doSendOtp(pendingCustomer.mobile, pendingCustomer.contact_id)}>
                    {sendingOtp ? 'Sending...' : 'Resend OTP'}
                  </button>
                  <span>·</span>
                  <button type="button" className="text-gray-500 underline"
                    onClick={() => { setPendingCustomer(null); setOtpSent(false); setOtpValue(''); setOtpError(''); setOtpDestinations(null); }}>
                    Search again
                  </button>
                </div>
              </div>

            ) : (
              /* ── Search form: tab selector + single input ── */
              <div className="space-y-4">
                {/* Tab buttons */}
                <div className="flex gap-2">
                  {[
                    { key: 'phone', label: '📱 Mobile' },
                    { key: 'email', label: '✉ Email' },
                    { key: 'gstin', label: '🏢 GSTIN' },
                  ].map(({ key, label }) => (
                    <button key={key} type="button"
                      onClick={() => { setSearchType(key); setSearchError(''); }}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        searchType === key
                          ? 'bg-amber-700 text-white border-amber-700'
                          : 'bg-white text-stone-600 border-stone-300 hover:border-amber-400'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Active input */}
                <div>
                  {searchType === 'phone' && (
                    <>
                      <Label className="text-xs font-medium text-gray-700">Mobile / WhatsApp Number</Label>
                      <Input className="mt-1" type="tel" inputMode="numeric" autoFocus
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        onKeyDown={(e) => e.key === 'Enter' && searchCustomer()}
                        placeholder="9876543210" maxLength={10}
                      />
                      <p className="text-xs text-gray-400 mt-0.5">10-digit number</p>
                    </>
                  )}
                  {searchType === 'email' && (
                    <>
                      <Label className="text-xs font-medium text-gray-700">Email Address</Label>
                      <Input className="mt-1" type="email" autoFocus
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchCustomer()}
                        placeholder="you@company.com"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Registered email</p>
                    </>
                  )}
                  {searchType === 'gstin' && (
                    <>
                      <Label className="text-xs font-medium text-gray-700">GSTIN</Label>
                      <Input className="mt-1 uppercase" type="text" autoFocus
                        value={searchGstin}
                        onChange={(e) => setSearchGstin(e.target.value.toUpperCase().slice(0, 15))}
                        onKeyDown={(e) => e.key === 'Enter' && searchCustomer()}
                        placeholder="22AAAAA0000A1Z5" maxLength={15}
                      />
                      <p className="text-xs text-gray-400 mt-0.5">15-character GST number</p>
                    </>
                  )}
                </div>

                {searchError && (
                  <Alert className="border-red-200 bg-red-50 py-2">
                    <AlertDescription className="text-red-700 text-xs">{searchError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Alert className="border-blue-100 bg-blue-50 py-2 flex-1">
                    <AlertDescription className="text-blue-700 text-xs">
                      💡 First time ordering? Use <Link to="/quote-request" className="underline font-medium">New Quote Request</Link> instead.
                    </AlertDescription>
                  </Alert>
                  <Button onClick={searchCustomer} disabled={searching}
                    className="bg-amber-700 hover:bg-amber-800 text-white px-6 shrink-0">
                    {searching ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Searching...
                      </span>
                    ) : '🔍 Find My Account'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Step 2: Account Balance (shown after customer found) ── */}
        {customerData && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {balanceError && (
            <div className="col-span-3">
              <Alert className="border-yellow-200 bg-yellow-50 py-2">
                <AlertDescription className="text-yellow-800 text-xs">⚠️ Balance: {balanceError}</AlertDescription>
              </Alert>
            </div>
          )}
            {/* Credit Balance */}
            <Card className={`border-2 ${credit > 0 ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Available Credit</p>
                {loadingBalance ? (
                  <div className="animate-pulse h-7 bg-gray-200 rounded mt-1"></div>
                ) : !customerData.contact_id ? (
                  <p className="text-sm text-gray-400 mt-1">—</p>
                ) : (
                  <p className={`text-2xl font-bold mt-1 ${credit > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                    ₹{credit.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">Advance / credit notes</p>
              </CardContent>
            </Card>
            {/* Outstanding */}
            <Card className={`border-2 ${outstanding > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Outstanding</p>
                {loadingBalance ? (
                  <div className="animate-pulse h-7 bg-gray-200 rounded mt-1"></div>
                ) : !customerData.contact_id ? (
                  <p className="text-sm text-gray-400 mt-1">—</p>
                ) : (
                  <p className={`text-2xl font-bold mt-1 ${outstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    ₹{outstanding.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">Pending invoices</p>
              </CardContent>
            </Card>
            {/* This Order */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">This Order</p>
                <p className="text-2xl font-bold mt-1 text-blue-700">
                  ₹{orderTotal.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{selectedCount > 0 ? `${selectedCount} product type${selectedCount > 1 ? 's' : ''}` : 'Nothing selected yet'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Step 3: Order History (invoices + estimates) ── */}
        {customerData && (
          <Card className="border border-stone-200 shadow-sm">
            <CardHeader className="px-3 py-2.5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="bg-amber-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">2</span>
                  <CardTitle className="text-sm text-stone-800">Order History</CardTitle>
                  {!loadingOrders && previousOrders.length > 0 && (
                    <span className="text-[11px] text-stone-400">— invoices & quotes</span>
                  )}
                </div>
                {referenceEstimateId && (
                  <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
                    Pre-filled below
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0">
              {loadingOrders ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="animate-pulse flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                      <div className="h-9 w-9 bg-gray-200 rounded-lg shrink-0"></div>
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                        <div className="h-2.5 bg-gray-100 rounded w-48"></div>
                      </div>
                      <div className="h-5 bg-gray-200 rounded w-20 shrink-0"></div>
                    </div>
                  ))}
                </div>
              ) : ordersError ? (
                <Alert className="border-yellow-200 bg-yellow-50 py-2">
                  <AlertDescription className="text-yellow-800 text-xs">⚠️ {ordersError}</AlertDescription>
                </Alert>
              ) : previousOrders.length === 0 ? (
                <div className="py-4 text-center">
                  <p className="text-sm text-gray-500">No invoices or quotes found for this customer.</p>
                  <p className="text-xs text-gray-400 mt-1">Build a fresh order in Step 3 below.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {previousOrders.map(order => {
                    const isInvoice = order.type === 'invoice';
                    const uid = order.record_id || order.estimate_id;
                    const displayNum = order.record_number || order.estimate_number || uid;
                    const isExpanded = expandedOrder === uid;

                    // Status colour
                    const statusColor = {
                      paid: 'bg-green-100 text-green-800',
                      partially_paid: 'bg-teal-100 text-teal-800',
                      sent: 'bg-blue-100 text-blue-800',
                      accepted: 'bg-green-100 text-green-800',
                      invoiced: 'bg-blue-100 text-blue-800',
                      overdue: 'bg-red-100 text-red-700',
                      draft: 'bg-gray-100 text-gray-600',
                      declined: 'bg-red-100 text-red-600',
                    }[order.status] || 'bg-yellow-100 text-yellow-700';

                    return (
                      <div key={uid}
                        className={`border-2 rounded-lg overflow-hidden transition-all ${isExpanded ? (isInvoice ? 'border-blue-400' : 'border-green-400') : 'border-gray-200 hover:border-gray-300'}`}>
                        {/* Row header */}
                        <button type="button"
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-all text-left"
                          onClick={() => setExpandedOrder(isExpanded ? null : uid)}>
                          <div className="flex items-center gap-3">
                            {/* Type icon */}
                            <div className={`rounded-lg p-2 shrink-0 ${isInvoice ? 'bg-blue-50' : 'bg-green-50'}`}>
                              {isInvoice ? (
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-sm text-gray-900">{displayNum}</p>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${isInvoice ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                                  {isInvoice ? 'Invoice' : 'Quote'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">
                                {order.date ? fmtDate(order.date) : '—'}
                                {order.due_date && ` · Due ${fmtDate(order.due_date)}`}
                                {' · '}{order.line_items.length} item{order.line_items.length !== 1 ? 's' : ''}
                              </p>
                              {order.order && (
                                <p className="text-xs text-stone-600 mt-0.5" data-testid={`order-progress-${order.estimate_id || order.id}`}>
                                  {order.order.status === 'closed' ? 'Order closed' : 'Order open'}
                                  {' · supplied '}{Number(order.order.supplied_qty || 0).toLocaleString('en-IN')} / {Number(order.order.ordered_qty || 0).toLocaleString('en-IN')}
                                  {' · '}{order.order.loads || 0} load{order.order.loads !== 1 ? 's' : ''}
                                  {order.order.needed_by && ` · requested ${fmtDate(order.order.needed_by)}`}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 shrink-0">
                            <div className="text-right">
                              <p className="font-bold text-gray-900 text-sm">
                                ₹{order.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </p>
                              <div className="flex items-center gap-1 justify-end flex-wrap">
                                <Badge className={`text-xs ${statusColor}`}>{order.status || 'draft'}</Badge>
                                {isInvoice && order.balance > 0 && (
                                  <span className="text-xs text-red-600 font-medium">
                                    Due ₹{order.balance.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className={`border-t p-3 ${isInvoice ? 'bg-blue-50 border-blue-100' : 'bg-green-50 border-green-100'}`}>
                            {order.line_items.length === 0 ? (
                              <p className="text-xs text-gray-500 py-1">No line item details available.</p>
                            ) : (
                              <div className="bg-white rounded-lg divide-y divide-gray-100 mb-3 overflow-hidden">
                                {/* Table header */}
                                <div className="grid grid-cols-12 gap-1 px-3 py-1.5 bg-gray-50 text-xs text-gray-400 font-medium">
                                  <span className="col-span-5">Product</span>
                                  <span className="col-span-2 text-right">Qty</span>
                                  <span className="col-span-2 text-right">Rate</span>
                                  <span className="col-span-3 text-right">Amount</span>
                                </div>
                                {order.line_items.map((li, idx) => (
                                  <div key={idx} className="grid grid-cols-12 gap-1 px-3 py-2 text-xs">
                                    <span className="col-span-5 text-gray-800 font-medium leading-tight">{li.name}</span>
                                    <span className="col-span-2 text-right text-gray-600">{li.quantity}</span>
                                    <span className="col-span-2 text-right text-gray-600">₹{li.rate.toLocaleString('en-IN')}</span>
                                    <span className="col-span-3 text-right font-semibold text-gray-800">
                                      ₹{li.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                ))}
                                <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-gray-50">
                                  <span className="col-span-9 text-right text-xs font-bold text-gray-700">Total</span>
                                  <span className="col-span-3 text-right text-sm font-bold text-gray-900">
                                    ₹{order.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                                {isInvoice && order.balance > 0 && (
                                  <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-red-50">
                                    <span className="col-span-9 text-right text-xs font-bold text-red-600">Balance Due</span>
                                    <span className="col-span-3 text-right text-sm font-bold text-red-700">
                                      ₹{order.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Only rows the tracker can actually answer: `order` is
                                attached by the backend for estimates that have a local
                                order behind them, which is exactly what /track reads.
                                Linking an old invoice would dead-end on "no order found".
                                New tab so a half-built reorder above is not thrown away. */}
                            {order.order && order.record_number && (
                              <a href={`/track?ref=${encodeURIComponent(order.record_number)}`}
                                target="_blank" rel="noopener noreferrer"
                                data-testid={`track-order-${order.record_id || ''}`}
                                className="block mb-2 text-center text-xs font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2">
                                📍 Track this order
                              </a>
                            )}
                            {order.line_items.length > 0 && (() => {
                              const orderKey = order.record_id || order.invoice_number || order.estimate_number || '';
                              const isPreview = reorderPreviewOrderId === orderKey;
                              // Total bricks for the loading calc — historical "Loading Charges" rows excluded.
                              const totalBricks = order.line_items
                                .filter(li => !isLoadingChargeLine(li))
                                .reduce((s, li) => s + Number(li.quantity || 0), 0);
                              const loadingAmount = computeLoadingCharge(totalBricks);
                              const orderTotalWithoutOldLoading = order.line_items
                                .filter(li => !isLoadingChargeLine(li))
                                .reduce((s, li) => s + Number(li.amount || (li.quantity * li.rate) || 0), 0);
                              const grandTotal = orderTotalWithoutOldLoading + loadingAmount;
                              if (!isPreview) {
                                return (
                                  <Button size="sm"
                                    className={`w-full text-white ${isInvoice ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                                    onClick={() => setReorderPreviewOrderId(orderKey)}>
                                    🔄 Reorder
                                  </Button>
                                );
                              }
                              return (
                                <div className="space-y-2">
                                  <div className="bg-white border border-amber-200 rounded-md p-3 text-xs space-y-1">
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Confirm this order</p>
                                    <div className="flex justify-between text-stone-700">
                                      <span>Sub-total ({totalBricks.toLocaleString('en-IN')} bricks · same pricing)</span>
                                      <span>₹{orderTotalWithoutOldLoading.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    {loadingAmount > 0 ? (
                                      <div className="flex justify-between text-amber-800">
                                        <span>Loading Charges (₹1/brick, rounded to ₹10)</span>
                                        <span>₹{loadingAmount.toLocaleString('en-IN')}</span>
                                      </div>
                                    ) : (
                                      <p className="text-[11px] text-stone-500 italic">No loading charge (order is 30 bricks or fewer).</p>
                                    )}
                                    <div className="flex justify-between font-bold text-stone-900 pt-1 border-t border-amber-200">
                                      <span>Total (excl. GST)</span>
                                      <span>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <p className="text-[10px] text-stone-500 pt-1">
                                      Transport: EB Contracted (Kumar) · same delivery address as before.
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <Button size="sm" variant="outline"
                                      className="w-full text-xs"
                                      onClick={() => setReorderPreviewOrderId(null)}
                                      disabled={confirmingReorder}>
                                      Cancel
                                    </Button>
                                    <Button size="sm"
                                      className="w-full text-white bg-green-600 hover:bg-green-700"
                                      onClick={() => confirmReorder(order)}
                                      disabled={confirmingReorder}>
                                      {confirmingReorder ? 'Placing…' : '✓ Confirm Order'}
                                    </Button>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Transport option — determines truck capacity used in the totals ── */}
        {customerData && (
          <Card className="border border-stone-200 shadow-sm">
            <CardHeader className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                <span className="bg-amber-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">3</span>
                <CardTitle className="text-sm text-stone-800">Transport</CardTitle>
                <span className="text-[11px] text-stone-400 ml-1">— pick for this order</span>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.entries(TRANSPORT_OPTIONS).map(([key, opt]) => {
                  const active = transportType === key;
                  return (
                    <label
                      key={key}
                      className={`flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer transition-all ${
                        active
                          ? 'border-amber-600 bg-amber-50/60 shadow-sm'
                          : 'border-stone-200 bg-white hover:border-amber-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="transport"
                        checked={active}
                        onChange={() => setTransportType(key)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-stone-800">
                          🚛 {opt.label}
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">{opt.sub}</div>
                        {opt.fee && <div className="text-xs font-medium text-amber-700 mt-0.5">{opt.fee}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-stone-500 mt-2">
                Truck count in the summary bar is calculated using this option's per-truck capacity.
              </p>
              <div className="mt-3 pt-3 border-t border-stone-100">
                <label className="block text-xs font-medium text-stone-700 mb-1" htmlFor="reorder-needed-by">
                  When do you need the first load? <span className="text-stone-400 font-normal">(optional)</span>
                </label>
                <input
                  id="reorder-needed-by"
                  type="date"
                  min={localISODate()}
                  value={neededBy}
                  onChange={(e) => setNeededBy(e.target.value)}
                  className="w-full sm:w-56 rounded-md border border-stone-300 px-2 py-1.5 text-sm bg-white"
                  data-testid="reorder-needed-by"
                />
                <p className="text-[11px] text-stone-500 mt-1">Large orders go out as several loads — this is the date for the first one.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Step 4: Product Builder ── */}
        {customerData && (
          <Card id="product-builder" className="border border-stone-200 shadow-sm">
            <CardHeader className="px-3 py-2.5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="bg-amber-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">4</span>
                  <CardTitle className="text-sm text-stone-800">Build Your Order</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  {referenceEstimateId && (
                    <p className="text-xs text-orange-600 font-medium">Showing rates from previous order · Edit quantities as needed</p>
                  )}
                  {selectedCount > 0 && (
                    <Button variant="outline" size="sm" onClick={() => { setSelectedItems({}); setCustomerRates({}); setReferenceEstimateId(null); }}>
                      Clear All
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-700 mx-auto mb-2"></div>
                    <p className="text-xs text-stone-500">Loading catalogue...</p>
                  </div>
                </div>
              ) : (
              <div className="space-y-2">
                {Object.entries(categorizeItems(inventoryItems)).map(([categoryKey, category]) => {
                  /* On hold outranks everything: the section has items, and must still not
                     open. An empty one is the announced case - a flat row if we have said
                     the range is coming, nothing at all if we have not. The day the Octa
                     items go active in Books that section becomes an ordinary accordion
                     on its own, which is why comingSoon is only read when it is empty. */
                  if (category.onHold) return <NotSellingRow key={categoryKey} category={category} />;
                  if (category.items.length === 0) {
                    if (!category.comingSoon) return null;
                    return <NotSellingRow key={categoryKey} category={category} />;
                  }
                  const isOpen = openAccordions[categoryKey];
                  const selectedInCategory = category.items.filter(item => selectedItems[item.id]).length;
                  return (
                    <div key={categoryKey} className="border border-stone-200 rounded-lg overflow-hidden">
                      <button type="button" onClick={() => toggleAccordion(categoryKey)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-stone-50 hover:bg-stone-100 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{category.icon}</span>
                          <div className="text-left">
                            <h4 className="font-semibold text-sm text-stone-800">{category.title}</h4>
                            <p className="text-[11px] text-stone-500">
                              {category.items.length} product{category.items.length !== 1 ? 's' : ''}
                              {selectedInCategory > 0 && <span className="text-amber-700 font-semibold ml-1">· {selectedInCategory} selected</span>}
                            </p>
                          </div>
                        </div>
                        <svg className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="px-2 py-1.5 bg-white space-y-1.5">
                          {category.note && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 font-medium">{category.note}</p>
                          )}
                          {category.items.map(item => renderBrickItem(item))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>)}
            </CardContent>
          </Card>
        )}
      </main>

      {/* ── Sticky Footer: Order Summary + Submit ── */}
      {customerData && selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t-2 border-amber-700 shadow-2xl">
          <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-2">
            {/* Line items summary (collapsible mini list) */}
            <div className="hidden sm:grid grid-cols-12 gap-2 mb-3 max-h-24 overflow-y-auto pr-1">
              {Object.values(selectedItems).map((item, idx) => (
                <div key={idx} className="col-span-12 flex justify-between text-xs py-0.5 border-b border-gray-100">
                  <span className="text-gray-700 truncate">{item.name} × {item.quantity}</span>
                  <span className="font-semibold text-gray-800 shrink-0 ml-2">₹{(item.quantity * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>

            {/* Email-the-estimate option (only if the account has an email on file) */}
            {customerData?.email && (
              <label className="flex items-start gap-2 mb-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={emailEstimate}
                  onChange={(e) => setEmailEstimate(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-stone-300 text-amber-700 focus:ring-amber-500"
                  data-testid="email-estimate"
                />
                <span className="text-xs text-stone-600 leading-tight">
                  Email me this quote now at <span className="font-medium text-stone-800">{customerData.email}</span>
                  <span className="block text-[10px] text-stone-400">Draft copy — your sales rep will review &amp; confirm final pricing.</span>
                </span>
              </label>
            )}

            <div className="flex items-center justify-between gap-4 flex-wrap">
              {/* Totals */}
              <div className="flex items-center gap-4 flex-wrap text-sm">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Total Weight</p>
                  <p className="font-bold text-gray-800">{weight.tons} T <span className="text-xs text-gray-500 font-normal">({weight.kg.toFixed(0)} kg)</span></p>
                </div>
                <Separator orientation="vertical" className="h-8 hidden sm:block" />
                <div className="text-center" title={`Assumes ${truckCapacityTons}T per truck · ${TRANSPORT_OPTIONS[transportType]?.label}`}>
                  <p className="text-xs text-gray-500">Trucks</p>
                  <p className={`font-bold ${weight.trucks > 1 ? 'text-amber-700' : 'text-gray-800'}`}>
                    🚛 {weight.trucks}
                    {weight.trucks > 1 && (
                      <span className="text-[10px] text-amber-700 font-normal ml-1">(split delivery)</span>
                    )}
                  </p>
                  <p className="text-[10px] text-gray-400">{TRANSPORT_OPTIONS[transportType]?.short || 'Transport'}</p>
                </div>
                <Separator orientation="vertical" className="h-8 hidden sm:block" />
                <div className="text-center">
                  <p className="text-xs text-gray-500">Order Total</p>
                  <p className="font-bold text-lg text-gray-900">₹{orderTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </div>
                <Separator orientation="vertical" className="h-8 hidden sm:block" />
                <div className="text-center">
                  <p className="text-xs text-gray-500">Available Credit</p>
                  <p className={`font-bold text-sm ${credit > 0 ? 'text-green-600' : 'text-gray-400'}`}>−₹{credit.toLocaleString('en-IN', { minimumFractionDigits: 0 })}</p>
                </div>
                <Separator orientation="vertical" className="h-8 hidden sm:block" />
                <div className="text-center">
                  <p className="text-xs text-gray-500">Amount to Deposit</p>
                  <p className={`font-bold text-lg ${amountToDeposit > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {amountToDeposit > 0 ? `₹${amountToDeposit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '✓ Covered'}
                  </p>
                </div>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={submitting || !customerData?.contact_id}
                title={!customerData?.contact_id ? 'Customer not linked to Zoho Books — cannot create the quote' : ''}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-base font-bold h-auto shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  '✅ Submit Reorder →'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Quote Success Component
const QuoteSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quoteData = location.state?.quoteData;

  if (!quoteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 mb-4">No quote data found</p>
            <Button onClick={() => navigate('/quote-request')}>
              Create New Quote
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center bg-green-50 border-b-2 border-green-200">
          <div className="flex justify-center mb-4">
            <div className="bg-green-500 rounded-full p-4">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-3xl text-green-800">Quote Created Successfully!</CardTitle>
          <CardDescription className="text-lg">Your quote has been saved in Zoho Books as a draft</CardDescription>
        </CardHeader>
        <CardContent className="p-8">
          <div className="space-y-6">
            <Alert className="border-blue-200 bg-blue-50">
              <AlertDescription className="text-blue-800">
                <strong>Quote Number:</strong> {quoteData.estimate_number}
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Customer:</span>
                <span className="font-semibold">{quoteData.customer_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Status:</span>
                <Badge className="bg-yellow-100 text-yellow-800">{quoteData.status}</Badge>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-bold text-lg text-blue-600">₹{(quoteData.total ?? 0).toFixed(2)}</span>
              </div>
              {quoteData.email_sent && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Emailed to you:</span>
                  <span className="font-medium text-green-700">✓ Sent</span>
                </div>
              )}
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Zoho Estimate ID:</span>
                <span className="font-mono text-sm">{quoteData.zoho_estimate_id}</span>
              </div>
            </div>

            <Alert className="border-orange-200 bg-orange-50">
              <AlertDescription className="text-orange-800">
                <strong>What happens next:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Our sales representative will review your request.</li>
                  <li>They'll confirm pricing, transport and availability.</li>
                  <li>You'll receive your final quote from them shortly.</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="flex space-x-3 pt-4">
              <Button 
                className="flex-1" 
                onClick={() => navigate('/quote-request')}
                data-testid="create-another-quote-btn"
              >
                Create Another Quote
              </Button>
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => navigate('/')}
                data-testid="back-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── DELIVERY MODULE ────────────────────────────────────────────────────────

const DELIVERY_STATUS = {
  confirmed:  { label: 'Confirmed',   color: 'bg-blue-100 text-blue-800',   dot: 'bg-blue-500' },
  loaded:     { label: 'Loaded',      color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  in_transit: { label: 'Departed',    color: 'bg-purple-100 text-purple-800', dot: 'bg-purple-500' },
  delivered:  { label: 'Delivered',   color: 'bg-green-100 text-green-800',  dot: 'bg-green-500' },
  cancelled:  { label: 'Cancelled',   color: 'bg-stone-200 text-stone-600',  dot: 'bg-stone-400' },
};

const DeliveryBadge = ({ status }) => {
  const s = DELIVERY_STATUS[status] || { label: status, color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// Staff portals and the roles that grant access — drives the multi-role portal switcher.
const STAFF_PORTALS = [
  { to: '/dashboard', label: 'Dashboard', roles: ['admin', 'manager', 'staff'] },
  { to: '/factory',   label: 'Factory',   roles: ['admin', 'factory'] },
  { to: '/delivery',  label: 'Delivery',  roles: ['admin', 'manager', 'transport', 'factory'] },
  // Inbox intentionally omitted — customer WhatsApp is handled by Zoho Books
  // (documents + inbox live there). Route + backend are kept dormant so the
  // in-app inbox can be re-enabled without a rebuild if the strategy changes.
  // { to: '/inbox',  label: 'Inbox',    roles: ['admin', 'manager', 'transport', 'factory'] },
];
const userPortals = (u) => {
  if (!u) return [];
  const rs = [u.role, ...(u.extra_roles || [])];
  return STAFF_PORTALS.filter(p => p.roles.some(r => rs.includes(r)));
};

// WhatsApp 2-way inbox — read customer messages and reply (free-form within 24h).
const WA_POLL_MS = 10000;
const WhatsAppInbox = () => {
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);      // contact_wa_id
  const [activeName, setActiveName] = useState('');
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const threadEndRef = React.useRef(null);

  useEffect(() => { document.title = 'EarthBlocks | WhatsApp Inbox'; }, []);

  const loadConvos = async () => {
    try { const r = await axios.get(`${API}/whatsapp/conversations`); setConvos(r.data.conversations || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  const loadThread = async (contact) => {
    if (!contact) return;
    try { const r = await axios.get(`${API}/whatsapp/thread?contact=${encodeURIComponent(contact)}`); setThread(r.data.messages || []); }
    catch (e) { /* ignore */ }
  };

  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, WA_POLL_MS); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), WA_POLL_MS);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ''; } };

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, { to: active, body });
      setReply('');
      await loadThread(active);
      await loadConvos();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          {active
            ? <Button variant="ghost" size="sm" onClick={() => setActive(null)}>← Chats</Button>
            : <Button variant="ghost" size="sm" onClick={() => navigate('/delivery')}>← Back</Button>}
          <div className="flex-1 text-center min-w-0">
            <h1 className="text-sm font-bold text-stone-800 truncate">{active ? activeName : '💬 WhatsApp Inbox'}</h1>
            {active && <p className="text-[11px] text-stone-400">+{active}</p>}
          </div>
          <div className="w-14 shrink-0" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto">
        {!active ? (
          loading ? (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          ) : convos.length === 0 ? (
            <div className="text-center py-16 text-gray-400 px-6">
              <p className="text-4xl mb-2">💬</p>
              <p className="font-medium">No conversations yet</p>
              <p className="text-sm">Customer WhatsApp messages appear here once the webhook is connected in Meta.</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {convos.map(c => (
                <button key={c.contact_wa_id} onClick={() => { setActive(c.contact_wa_id); setActiveName(c.contact_name || `+${c.contact_wa_id}`); setThread([]); }}
                  className="w-full text-left px-4 py-3 hover:bg-stone-100 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold shrink-0">
                    {(c.contact_name || c.contact_wa_id || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="font-medium text-sm text-stone-800 truncate">{c.contact_name || `+${c.contact_wa_id}`}</span>
                      <span className="text-[10px] text-stone-400 shrink-0">{fmtTime(c.last_at)}</span>
                    </div>
                    <p className="text-xs text-stone-500 truncate">{c.last_direction === 'out' ? 'You: ' : ''}{c.last_body || `[${c.last_msg_type}]`}</p>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 49px)' }}>
            <div className="flex-1 px-3 py-3 space-y-2">
              {thread.map((m, i) => (
                <div key={i} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${m.direction === 'out' ? 'bg-green-600 text-white' : 'bg-white border border-stone-200 text-stone-800'}`}>
                    <p className="whitespace-pre-wrap break-words">{m.body || `[${m.msg_type}]`}</p>
                    <p className={`text-[9px] mt-0.5 ${m.direction === 'out' ? 'text-green-100' : 'text-stone-400'}`}>
                      {fmtTime(m.created_at)}{m.direction === 'out' && m.status ? ` · ${m.status}` : ''}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={threadEndRef} />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-stone-200 px-2 py-2 flex items-center gap-2">
              <Input value={reply} onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="Type a reply…" className="flex-1" />
              <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-green-600 hover:bg-green-700 text-white">
                {sending ? '…' : 'Send'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Source-factory differentiator for deliveries — distinct colour + short label per factory.
const factoryTag = (name) => {
  if (!name) return null;
  const n = name.toLowerCase();
  // "marasandra" is the local name for M Annapoorneshwari's site — same physical factory.
  if (n.includes('annapoorn') || n.includes('marasandra')) return { label: 'Marasandra', pill: 'bg-violet-100 text-violet-800 border-violet-200', accent: 'border-l-violet-400' };
  if (n.includes('virgonagar')) return { label: 'Virgonagar', pill: 'bg-sky-100 text-sky-800 border-sky-200', accent: 'border-l-sky-400' };
  return { label: name, pill: 'bg-stone-100 text-stone-700 border-stone-200', accent: 'border-l-stone-300' };
};

// Transport staff delivery list screen
const DeliveryPortal = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('confirmed');
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  // Loaded trucks still waiting for Accounts' invoice — the hand-off worklist.
  const [awaitingCount, setAwaitingCount] = useState(0);

  useEffect(() => {
    document.title = "EarthBlocks | Customer Portal Blocks Transport Portal";
    axios.get(`${API}/auth/me`).then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadDeliveries(); }, [statusFilter]);

  const loadDeliveries = async () => {
    setLoading(true);
    try {
      // '__pending__' is the "photos not yet on the Zoho invoice" reminder filter;
      // '__awaiting__' is the "loaded, waiting for documents" worklist.
      let url;
      if (statusFilter === '__awaiting__') url = `${API}/deliveries/awaiting-documents`;
      else if (statusFilter === '__pending__') url = `${API}/deliveries?photos_pending=true`;
      else url = `${API}/deliveries${statusFilter ? `?status=${statusFilter}` : ''}`;
      const resp = await axios.get(url);
      setDeliveries(resp.data.deliveries || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
    axios.get(`${API}/deliveries/photos-pending/count`)
      .then(r => setPendingCount(r.data.count || 0)).catch(() => {});
    axios.get(`${API}/deliveries/awaiting-documents?count_only=true`)
      .then(r => setAwaitingCount(r.data.count || 0)).catch(() => {});
  };

  // Live-refresh the list when the SSE stream reports a new/changed delivery,
  // so a new "Confirmed" appears instantly without the user hitting refresh.
  // Ref keeps the latest loadDeliveries closure without re-subscribing per render.
  const loadRef = React.useRef();
  loadRef.current = loadDeliveries;
  useEffect(() => {
    if (!EB_EVENTS) return;
    const refresh = () => loadRef.current?.();
    EB_EVENTS.addEventListener('delivery_confirmed', refresh);
    EB_EVENTS.addEventListener('delivery_status_changed', refresh);
    EB_EVENTS.addEventListener('delivery_documents_ready', refresh);
    EB_EVENTS.addEventListener('delivery_funds_updated', refresh);
    return () => {
      EB_EVENTS.removeEventListener('delivery_confirmed', refresh);
      EB_EVENTS.removeEventListener('delivery_status_changed', refresh);
      EB_EVENTS.removeEventListener('delivery_documents_ready', refresh);
      EB_EVENTS.removeEventListener('delivery_funds_updated', refresh);
    };
  }, []);

  const statusCounts = deliveries.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header — mirrors FactoryPortal for a consistent toolbar across portals:
          logo + name on the left, portal nav + notifications on the right. */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={EB_LOGO} alt="EarthBlocks" className="h-7 w-auto shrink-0" />
            <h1 className="text-sm font-bold text-stone-800 truncate">Delivery Portal</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {userPortals(currentUser).filter(p => p.to !== '/delivery').map(p => (
              <Button key={p.to} variant="outline" size="sm" onClick={() => navigate(p.to)}>{p.label} →</Button>
            ))}
            <NotificationsBell />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-3 py-3 space-y-3">
        {/* Status filter pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {[['confirmed', 'Confirmed'], ['loaded', 'Loaded'], ['in_transit', 'Departed'], ['delivered', 'Delivered'], ['', 'All']].map(([val, lbl]) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${statusFilter === val ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-amber-400'}`}>
              {lbl}{val && statusCounts[val] ? ` (${statusCounts[val]})` : ''}
            </button>
          ))}
          {(awaitingCount > 0 || statusFilter === '__awaiting__') && (
            <button onClick={() => setStatusFilter('__awaiting__')}
              title="Loaded trucks waiting for Accounts to raise the invoice in Zoho"
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${statusFilter === '__awaiting__' ? 'bg-orange-600 text-white border-orange-600' : 'bg-orange-50 text-orange-800 border-orange-300 hover:border-orange-500'}`}
              data-testid="awaiting-documents-pill">
              📄 Waiting for invoice{awaitingCount ? ` (${awaitingCount})` : ''}
            </button>
          )}
          {(pendingCount > 0 || statusFilter === '__pending__') && (
            <button onClick={() => setStatusFilter('__pending__')}
              title="Photos not yet pushed to the Zoho invoice — they upload automatically once the invoice is raised"
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${statusFilter === '__pending__' ? 'bg-amber-600 text-white border-amber-600' : 'bg-amber-50 text-amber-800 border-amber-300 hover:border-amber-500'}`}>
              📎 Photos pending{pendingCount ? ` (${pendingCount})` : ''}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading deliveries...</div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-4xl mb-2">🚛</p>
            <p className="font-medium">No deliveries found</p>
            <p className="text-sm">New confirmed orders will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {deliveries.map(d => {
              const ft = factoryTag(d.source_factory);
              return (
              <Card key={d.delivery_id} className={`cursor-pointer hover:shadow-md transition-all border-stone-200 border-l-4 ${ft ? ft.accent : 'border-l-stone-200'}`}
                onClick={() => navigate(`/delivery/${d.delivery_id}`)}>
                <CardContent className="px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-stone-800">{d.customer_name}</span>
                        {d.estimate_number && <span className="text-[11px] text-stone-400 font-mono">{d.estimate_number}</span>}
                        {d.call_off_seq > 0 && (
                          <span className="text-[10px] bg-stone-100 text-stone-700 border border-stone-200 rounded px-1" title={d.salesorder_number ? `Order ${d.salesorder_number}` : undefined}>Load {d.call_off_seq}</span>
                        )}
                        {d.photos_pending && (
                          <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 rounded px-1" title="Photos not yet on the Zoho invoice">📎 photos</span>
                        )}
                        {d.status === 'confirmed' && !d.is_stock_transfer && d.funded === false && (
                          <span className="text-[10px] bg-red-50 text-red-700 border border-red-200 rounded px-1" title={d.shortfall ? `₹${Number(d.shortfall).toLocaleString('en-IN', { maximumFractionDigits: 0 })} short` : 'Payment not OK'}>⏳ payment</span>
                        )}
                        {d.status === 'loaded' && !d.cleared && (
                          <span className="text-[10px] bg-orange-50 text-orange-800 border border-orange-200 rounded px-1" title="Waiting for Accounts to raise the invoice">📄 waiting for invoice</span>
                        )}
                        {d.status === 'loaded' && d.cleared && (
                          <span className="text-[10px] bg-green-50 text-green-800 border border-green-200 rounded px-1">✓ invoice OK</span>
                        )}
                      </div>
                      {ft && (
                        <span className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${ft.pill}`}>
                          🏭 {ft.label}
                        </span>
                      )}
                      {d.delivery_address?.city && (
                        <p className="text-xs text-gray-500 mt-0.5">📍 {[d.delivery_address.address, d.delivery_address.city, d.delivery_address.state].filter(Boolean).join(', ')}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {Number(d.total_qty || 0).toLocaleString('en-IN')} pcs · ₹{Number(d.total).toLocaleString('en-IN')}
                        {d.scheduled_date ? ` · 📅 ${fmtDate(d.scheduled_date)}` : (d.status === 'confirmed' ? ' · no date' : '')}
                        {d.age_minutes != null && ` · waiting ${d.age_minutes >= 60 ? `${Math.floor(d.age_minutes / 60)} h ${d.age_minutes % 60} m` : `${d.age_minutes} m`}`}
                      </p>
                    </div>
                    <DeliveryBadge status={d.status} />
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Individual delivery detail — load list + POD
// Share invoice + delivery location with the driver on WhatsApp.
// - Location: prefers google_maps_location from the estimate address (a plain URL);
//   falls back to a Google Maps search on the delivery address string.
// - Invoice: links directly to Zoho Books when we have zoho_invoice_id.
const DriverShareBar = ({ delivery }) => {
  const digits = String(delivery.driver_mobile || '').replace(/\D/g, '');
  const wa = digits ? (digits.length === 10 ? `91${digits}` : digits) : '';

  const addressParts = (() => {
    const a = delivery.delivery_address || {};
    return [a.address, a.city, a.state, a.zip, a.zip_code].filter(Boolean).join(', ');
  })();
  const mapsUrl = delivery.delivery_address?.google_maps_location
    || (addressParts ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}` : '');

  const invoiceUrl = delivery.zoho_invoice_id
    ? `https://books.zoho.in/app/60024798345#/invoices/${delivery.zoho_invoice_id}`
    : '';

  const share = (text) => {
    const enc = encodeURIComponent(text);
    return wa ? `https://wa.me/${wa}?text=${enc}` : `https://wa.me/?text=${enc}`;
  };

  const locationText =
    `EarthBlocks delivery ${delivery.delivery_id || ''}\n\n` +
    `Customer: ${delivery.customer_name || '—'}\n` +
    (addressParts ? `Address: ${addressParts}\n` : '') +
    (mapsUrl ? `Map: ${mapsUrl}\n` : '') +
    (delivery.customer_mobile ? `\nCustomer contact: ${delivery.customer_mobile}` : '');

  const invoiceText =
    `EarthBlocks invoice ${delivery.invoice_number || ''}\n\n` +
    `Customer: ${delivery.customer_name || '—'}\n` +
    (invoiceUrl ? `View invoice: ${invoiceUrl}\n` : '') +
    `Total: ₹${Number(delivery.total || 0).toLocaleString('en-IN')}\n` +
    (delivery.total_qty ? `Qty: ${delivery.total_qty}\n` : '');

  return (
    <div className="pt-2 border-t border-stone-100 flex gap-2 flex-wrap">
      <a
        href={share(locationText)}
        target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-green-50 hover:bg-green-100 text-green-800 px-3 py-1.5 text-xs font-medium"
      >
        📍 Share location on WhatsApp
      </a>
      <a
        href={share(invoiceText)}
        target="_blank" rel="noreferrer"
        className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium ${
          invoiceUrl
            ? 'border-green-300 bg-green-50 hover:bg-green-100 text-green-800'
            : 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed pointer-events-none'
        }`}
        title={invoiceUrl ? '' : 'Invoice will appear here once the truck is dispatched.'}
      >
        📄 Share invoice on WhatsApp
      </a>
    </div>
  );
};

const DeliveryDetail = () => {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showTruckForm, setShowTruckForm] = useState(false);
  const [truckNumber, setTruckNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverMobile, setDriverMobile] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [zohoActions, setZohoActions] = useState(null); // last-fired zoho_actions response
  const [uploadingPOD, setUploadingPOD] = useState(false);
  const [syncingPhotos, setSyncingPhotos] = useState(false);
  const [messages, setMessages] = useState([]);         // Zoho WhatsApp/email history for this delivery
  const [refreshingMsgs, setRefreshingMsgs] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  const roles = currentUser ? [currentUser.role, ...(currentUser.extra_roles || [])] : [];
  const isAdmin = roles.includes('admin');
  const isManager = isAdmin || roles.includes('manager');           // cancel a load, link an invoice
  const canSchedule = isManager || roles.includes('transport');      // move the delivery date

  useEffect(() => { loadDelivery(); }, [deliveryId]);

  // Live-refresh when Accounts' documents land (fast probe hit) or the status
  // changes elsewhere — so "Depart" enables without a manual reload.
  useEffect(() => {
    if (!EB_EVENTS) return;
    const refresh = (ev) => {
      const id = ev?.detail?.delivery_id;
      if (!id || String(id) === String(deliveryId)) loadDelivery();
    };
    EB_EVENTS.addEventListener('delivery_documents_ready', refresh);
    EB_EVENTS.addEventListener('delivery_status_changed', refresh);
    EB_EVENTS.addEventListener('delivery_funds_updated', refresh);
    return () => {
      EB_EVENTS.removeEventListener('delivery_documents_ready', refresh);
      EB_EVENTS.removeEventListener('delivery_status_changed', refresh);
      EB_EVENTS.removeEventListener('delivery_funds_updated', refresh);
    };
  }, [deliveryId]);

  const loadDelivery = async () => {
    try {
      const resp = await axios.get(`${API}/deliveries/${deliveryId}`);
      setDelivery(resp.data);
      setTruckNumber(resp.data.truck_number || '');
      setDriverName(resp.data.driver_name || '');
      setDriverMobile(resp.data.driver_mobile || '');
      if (resp.data.delivered_from_warehouse) setWarehouse(resp.data.delivered_from_warehouse);
    } catch (e) {
      alert('Delivery not found');
      navigate('/delivery');
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp/email history from Zoho (Comments & History for this delivery's estimate + invoice).
  useEffect(() => {
    if (!deliveryId) return;
    axios.get(`${API}/deliveries/${deliveryId}/messages`)
      .then(r => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [deliveryId]);
  const refreshMessages = async () => {
    setRefreshingMsgs(true);
    try {
      const { data } = await axios.get(`${API}/deliveries/${deliveryId}/messages?refresh=true`);
      setMessages(data.messages || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to refresh');
    } finally { setRefreshingMsgs(false); }
  };

  // Push the DC + delivery photos to the Zoho invoice and free them from the app.
  const syncPhotos = async () => {
    setSyncingPhotos(true);
    try {
      const { data } = await axios.post(`${API}/deliveries/${deliveryId}/sync-photos`);
      if (data.success && data.result?.resolved) {
        const n = data.result.attach?.uploaded ?? 0;
        alert(`${n} photo${n !== 1 ? 's' : ''} moved to invoice ${data.result.invoice_number || ''} and freed from the app.`);
      } else if (data.success && data.result?.nothing_pending) {
        alert('All photos are already in Zoho — nothing pending.');
      } else {
        alert(data.reason || 'Could not move the photos yet.');
      }
      await loadDelivery();
    } catch (e) {
      alert(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingPhotos(false);
    }
  };

  const updateStatus = async (newStatus, opts = {}) => {
    // The loading warehouse is fixed at call-off for new loads. Legacy rows may
    // have none — a Stock Transfer must pick one before "Loading Completed" because
    // its inventory adjustment (posted at Depart) debits that warehouse. Normal
    // loads invoice via Accounts in Zoho, so the warehouse is informational.
    if (newStatus === 'loaded' && delivery.is_stock_transfer && !warehouse) {
      alert('Please select the warehouse the truck loaded at before completing loading for this Free Delivery truck.');
      return;
    }
    setUpdating(true);
    try {
      const resp = await axios.put(`${API}/deliveries/${deliveryId}/status`, {
        status: newStatus,
        truck_number: truckNumber || undefined,
        driver_name: driverName || undefined,
        driver_mobile: driverMobile || undefined,
        delivered_from_warehouse: newStatus === 'loaded' && warehouse ? warehouse : undefined,
        override: opts.override || undefined,
        override_reason: opts.override_reason || undefined,
      });
      if (resp.data?.zoho_actions) setZohoActions(resp.data.zoho_actions);
      await loadDelivery();
      setShowTruckForm(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : (d?.message || 'Failed to update status'));
    } finally {
      setUpdating(false);
    }
  };

  // Admin-only: load a truck before the payment is recorded. Reason is logged.
  const overrideLoading = async () => {
    const short = Number(delivery.shortfall || 0) > 0 ? ` · ${fmtMoney(delivery.shortfall)} short` : '';
    const reason = window.prompt(
      `Payment not OK${short}.\n\nAdmin override — enter the reason (recorded against your name):`
    );
    if (reason == null) return;
    if (reason.trim().length < 5) { alert('Override reason must be at least 5 characters'); return; }
    await updateStatus('loaded', { override: true, override_reason: reason.trim() });
  };

  // Admin/manager: cancel a load that has not been loaded yet. The challan is
  // removed in Zoho (best effort); the sales order is never touched.
  const cancelLoad = async () => {
    const ok = window.confirm(
      `Cancel load ${delivery.call_off_seq || ''} of ${delivery.estimate_number}?\n\n` +
      'The truck slip is removed in Zoho and the quantity goes back to the order\'s remaining. ' +
      'The order itself is not changed.'
    );
    if (!ok) return;
    await updateStatus('cancelled');
  };

  // Admin: truck was unloaded — back to Confirmed.
  const undoLoading = async () => {
    if (!window.confirm('Move this load back to Confirmed (truck unloaded)?')) return;
    await updateStatus('confirmed');
  };

  // Admin/manager: the probe found several candidate invoices on the sales order
  // (or none) — paste the invoice id from Zoho Books to link it by hand.
  const linkInvoice = async () => {
    const id = window.prompt(
      'Zoho invoice id for this load.\n\nOpen the invoice in Zoho Books — the id is the long number at the end of the URL.'
    );
    if (!id || !id.trim()) return;
    setUpdating(true);
    try {
      await axios.put(`${API}/deliveries/${deliveryId}/invoice`, { invoice_id: id.trim() });
      await loadDelivery();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Could not link the invoice');
    } finally {
      setUpdating(false);
    }
  };

  const reschedule = async (newDate) => {
    if (!newDate || newDate === delivery.scheduled_date) return;
    try {
      await axios.put(`${API}/deliveries/${deliveryId}/schedule`, { scheduled_date: newDate });
      await loadDelivery();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Could not change the date');
    }
  };

  const handleFileCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPOD(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const dataUrl = ev.target.result;
        const mediaType = file.type.startsWith('video') ? 'video' : 'image';
        await axios.post(`${API}/deliveries/${deliveryId}/pod`, { media_type: mediaType, data_url: dataUrl });
        await loadDelivery();
        setUploadingPOD(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert('Upload failed');
      setUploadingPOD(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>;
  if (!delivery) return null;

  const addr = delivery.delivery_address || {};
  const addrStr = [addr.address, addr.street2, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
  const mapsUrl = addrStr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrStr)}` : null;
  // Per-load state machine: confirmed → loaded → in_transit → delivered (+ cancelled).
  // "Loading Completed" waits for payment (funded); "Depart" waits for Accounts'
  // documents (cleared). Both flags are computed by the backend on the row.
  const nextStatus = { confirmed: 'loaded', loaded: 'in_transit', in_transit: 'delivered' }[delivery.status];
  const nextLabel = { confirmed: 'Loading Completed', loaded: 'Depart', in_transit: 'Mark Delivered' }[delivery.status];
  const dispatched = delivery.status === 'in_transit' || delivery.status === 'delivered';
  // The Invoice card: shown from "loaded" onwards. Its wording is the same for
  // every kind of load; only the Zoho links underneath differ.
  const showDocs = delivery.status === 'loaded' || dispatched;
  const docsPending = showDocs && !delivery.cleared;
  const fmtWhen = (ts) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  // Pre-filled WhatsApp hand-off the transport user can fire to Accounts from their own
  // phone (wa.me). Includes every detail we have; empty lines are dropped. If an Accounts
  // number is configured it deep-links straight to them, else WhatsApp asks who to send to.
  const waLines = [
    '*EarthBlocks — Ready to Invoice*',
    'Please raise the Invoice & e-Way Bill in Zoho for:',
    '',
    delivery.customer_name && `Client: ${delivery.customer_name}`,
    delivery.estimate_number && `Estimate: ${delivery.estimate_number}${delivery.call_off_seq ? ` · Load ${delivery.call_off_seq}` : ''}`,
    delivery.salesorder_number && `Sales Order: ${delivery.salesorder_number} (invoice this load's quantities from it)`,
    delivery.challan_number && `Delivery Challan: ${delivery.challan_number} — put this as the invoice reference`,
    delivery.truck_number && `Truck: ${delivery.truck_number}`,
    delivery.driver_name && `Driver: ${delivery.driver_name}${delivery.driver_mobile ? ` (${delivery.driver_mobile})` : ''}`,
    (delivery.delivered_from_warehouse || delivery.source_factory) && `Loaded at: ${delivery.delivered_from_warehouse || delivery.source_factory}`,
    delivery.total_qty ? `Qty: ${Number(delivery.total_qty).toLocaleString('en-IN')} pcs` : null,
    delivery.total ? `Value: ₹${Number(delivery.total).toLocaleString('en-IN')}` : null,
    addrStr && `Deliver to: ${addrStr}`,
    addr.google_maps_location && `Map: ${addr.google_maps_location}`,
  ].filter(Boolean);
  // Always open WhatsApp's chat picker (no number) so the user can select the Accounts
  // GROUP — wa.me/<number> can only open a 1:1 chat, never a group.
  const waHref = `https://wa.me/?text=${encodeURIComponent(waLines.join('\n'))}`;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-3 py-2 space-y-1">
          {/* Row 1: nav + actions + status badges */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/delivery')}>← Back</Button>
            <div className="flex-1" />
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(true)}
                title="Edit delivery (admin)"
                className="text-stone-600 hover:text-stone-900"
              >
                ✎ Edit
              </Button>
            )}
            {delivery.is_stock_transfer && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                title={delivery.stock_transfer_reason || undefined}
              >
                STOCK TRANSFER · {STOCK_TRANSFER_LABEL(delivery.stock_transfer_category)}
              </span>
            )}
            {(() => { const ft = factoryTag(delivery.source_factory); return ft ? (
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${ft.pill}`}>🏭 {ft.label}</span>
            ) : null; })()}
            <DeliveryBadge status={delivery.status} />
          </div>
          {/* Row 2: customer + estimate number, side-by-side */}
          <div className="flex items-baseline gap-2 min-w-0">
            <h1 className="text-sm font-bold text-stone-800 truncate">{delivery.customer_name}</h1>
            <p className="text-[11px] text-stone-500 shrink-0">{delivery.estimate_number}</p>
          </div>
          {/* Row 3: which load of the order, its papers and its date */}
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] text-stone-500">
            {delivery.call_off_seq > 0 && <span className="font-medium text-stone-700">Load {delivery.call_off_seq}</span>}
            {delivery.salesorder_number && <span>· SO {delivery.salesorder_number}</span>}
            {delivery.challan_number && <span>· DC {delivery.challan_number}</span>}
            {canSchedule && (delivery.status === 'confirmed' || delivery.status === 'loaded') ? (
              <label className="flex items-center gap-1">
                <span>· 📅</span>
                <input type="date" value={delivery.scheduled_date || ''} min={localISODate()}
                  onChange={(e) => reschedule(e.target.value)}
                  className="border border-stone-300 rounded px-1 py-0.5 text-[11px] bg-white"
                  title="Delivery date — change it to move this load to another day" />
                {!delivery.scheduled_date && <span className="text-amber-700">no date</span>}
              </label>
            ) : delivery.scheduled_date ? (
              <span>· 📅 {fmtDate(delivery.scheduled_date)}</span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-3 py-3 space-y-3">
        {delivery.status === 'cancelled' && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            This load was cancelled{delivery.cancelled_at ? ` on ${fmtDate(delivery.cancelled_at)}` : ''}. Its quantity is back on the order's remaining; the order itself was not changed.
          </div>
        )}
        {/* Customer + Address */}
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="px-3 py-2.5 space-y-1.5">
            {delivery.customer_mobile && (
              <a href={`tel:${delivery.customer_mobile}`} className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                📞 {delivery.customer_mobile}
              </a>
            )}
            {addrStr && (
              <div className="flex items-start gap-2">
                <span className="text-sm text-gray-600 flex-1">📍 {addrStr}</span>
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                    className="shrink-0 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded px-2 py-1">
                    Maps ↗
                  </a>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {delivery.salesorder_number && (
                <p className="text-xs text-gray-400">SO: <span className="font-medium text-gray-600">{delivery.salesorder_number}</span></p>
              )}
              {delivery.challan_number && (
                <p className="text-xs text-gray-400">DC: <span className="font-medium text-blue-600">{delivery.challan_number}</span></p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Load list */}
        <Card className="border-stone-200 shadow-sm">
          <div className="px-3 py-2 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Items to Load</span>
            <span className="text-[11px] text-stone-400">{delivery.total_qty.toLocaleString()} pcs</span>
          </div>
          <div className="divide-y divide-stone-100">
            {delivery.line_items.map((li, i) => (
              <div key={i} className="px-3 py-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-stone-800">{li.name}</p>
                  {li.description && <p className="text-[11px] text-stone-400">{li.description}</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-stone-900">{Number(li.quantity).toLocaleString()}</p>
                  <p className="text-[10px] text-stone-400">{li.unit || 'Nos'}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Truck details (editable until the truck departs) */}
        {(delivery.status === 'confirmed' || delivery.status === 'loaded') && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Truck & Driver</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Truck Number</label>
                  <Input value={truckNumber} onChange={e => setTruckNumber(e.target.value)} placeholder="KA-01-AB-1234" className="mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Driver Name</label>
                  <Input value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="Driver name" className="mt-1 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500">Driver Mobile (WhatsApp)</label>
                  <Input
                    value={driverMobile}
                    onChange={e => setDriverMobile(e.target.value)}
                    placeholder="9876543210"
                    inputMode="numeric"
                    className="mt-1 text-sm"
                  />
                  <p className="text-[10px] text-stone-400 mt-1">
                    Used to share invoice + delivery location with the driver on WhatsApp.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing truck info (in_transit or delivered) */}
        {(delivery.status === 'in_transit' || delivery.status === 'delivered') && (delivery.truck_number || delivery.driver_name || delivery.driver_mobile) && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-6 flex-wrap">
                {delivery.truck_number && <div><p className="text-xs text-gray-500">Truck</p><p className="text-sm font-semibold">{delivery.truck_number}</p></div>}
                {delivery.driver_name && <div><p className="text-xs text-gray-500">Driver</p><p className="text-sm font-semibold">{delivery.driver_name}</p></div>}
                {delivery.driver_mobile && <div><p className="text-xs text-gray-500">Mobile</p><p className="text-sm font-semibold font-mono">{delivery.driver_mobile}</p></div>}
              </div>
              {delivery.driver_mobile && <DriverShareBar delivery={delivery} />}
            </CardContent>
          </Card>
        )}

        {/* Delivery photos — one section, title + guidance switches with status.
            All images captured here are pushed to the Zoho invoice at the
            corresponding transition (loaded→in_transit uploads pre-dispatch
            photos, in_transit→delivered uploads post-dispatch photos). */}
        {delivery.status !== 'confirmed' && delivery.status !== 'cancelled' && (() => {
          const isLoading = delivery.status === 'loaded';
          const isInTransit = delivery.status === 'in_transit';
          const isDelivered = delivery.status === 'delivered';
          const sectionTitle = isDelivered
            ? 'Proof of Delivery'
            : isInTransit
              ? 'Delivered load picture'
              : 'Truck loaded picture & DC copy';
          const helper = isInTransit
            ? 'Capture the delivered load at the site — kept as proof of delivery with this record.'
            : isLoading
              ? 'Capture 2 photos — one of the loaded truck, one of the paper DC — kept with this delivery record.'
              : '';
          // Consider only post-dispatch photos as "delivery photos" so we can
          // require one before Mark Delivered enables.
          const postDispatchPhotos = (delivery.pod_media || []).filter(
            m => !m.zoho_invoice_attached && m.media_type !== 'video'
          );
          // Stock Transfer has no invoice to attach to → don't force a delivered-load photo
          const needDeliveryPhoto = isInTransit && postDispatchPhotos.length === 0 && !delivery.is_stock_transfer;
          return (
            <Card className="border-stone-200 shadow-sm">
              <div className="px-3 py-2 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
                <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">{sectionTitle}</span>
                <span className="text-[11px] text-stone-400">{delivery.pod_media?.length || 0} photo{delivery.pod_media?.length !== 1 ? 's' : ''}</span>
              </div>
              <CardContent className="px-3 py-2.5 space-y-2">
                {helper && <p className="text-xs text-stone-500">{helper}</p>}
                {delivery.pod_media?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {delivery.pod_media.map((m, i) => {
                      // Local copy freed after upload — the file now lives on the Zoho invoice.
                      const inZoho = !m.data_url && (m.zoho_invoice_attached || m.storage === 'zoho_invoice');
                      const invHref = delivery.zoho_invoice_id
                        ? `https://books.zoho.in/app/#/invoices/${delivery.zoho_invoice_id}`
                        : null;
                      return (
                        <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                          {inZoho ? (
                            <a
                              href={invHref || undefined}
                              target="_blank" rel="noopener noreferrer"
                              onClick={(e) => { if (!invHref) e.preventDefault(); }}
                              className="w-full h-full flex flex-col items-center justify-center gap-0.5 text-center p-1 text-stone-500 hover:bg-stone-50 no-underline"
                              title="Stored on the Zoho invoice"
                            >
                              <span className="text-lg">📎</span>
                              <span className="text-[10px] leading-tight">In Zoho invoice</span>
                              {invHref && <span className="text-[10px] text-amber-800 underline">View ↗</span>}
                            </a>
                          ) : m.media_type === 'video' ? (
                            <video src={m.data_url} className="w-full h-full object-cover" controls />
                          ) : (
                            <img src={m.data_url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                          )}
                          {m.zoho_invoice_attached && !inZoho && (
                            <span className="absolute bottom-1 right-1 text-[10px] bg-green-600 text-white rounded px-1">✓ Zoho</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {!isDelivered && (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*,video/*" capture="environment"
                      className="hidden" onChange={handleFileCapture} />
                    <Button variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()} disabled={uploadingPOD}>
                      {uploadingPOD ? 'Uploading...' : '📷 Capture Photo / Video'}
                    </Button>
                    {needDeliveryPhoto && (
                      <p className="text-xs text-amber-700">
                        📸 Capture a delivered-load photo — it's required to mark delivered.
                      </p>
                    )}
                  </>
                )}
                {(delivery.pod_media || []).some(m => m.media_type !== 'video' && !m.zoho_invoice_attached && m.data_url) && !delivery.is_stock_transfer && (
                  <>
                    <p className="text-[11px] text-amber-700">
                      📎 {(delivery.pod_media || []).filter(m => m.media_type !== 'video' && !m.zoho_invoice_attached && m.data_url).length} photo(s) not yet on the Zoho invoice — they'll upload automatically once the invoice is raised.
                    </p>
                    <Button variant="outline" className="w-full text-xs h-8" onClick={syncPhotos} disabled={syncingPhotos}>
                      {syncingPhotos ? 'Moving to Zoho…' : '☁️ Move photos to Zoho now'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Loading factory — fixed at call-off for new loads (the stock check ran
            against it); still editable here for legacy rows. Mandatory for a stock
            transfer (its stock is adjusted in Zoho at Depart), informational otherwise. */}
        {nextStatus === 'loaded' && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 space-y-2">
              <Label className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
                Loading warehouse{delivery.is_stock_transfer ? '' : ' (optional)'}
              </Label>
              <select
                value={warehouse}
                onChange={(e) => setWarehouse(e.target.value)}
                className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">Select factory…</option>
                <option value="Virgonagar Factory">Virgonagar Factory</option>
                <option value="M Annapoorneshwari Enterprise">Marasandra Factory (M Annapoorneshwari)</option>
              </select>
              <p className="text-xs text-amber-800">
                {delivery.is_stock_transfer
                  ? "Free Delivery: this factory's stock is adjusted in Zoho when the truck departs."
                  : 'On "Loading Completed", the Accounts team is notified to raise the invoice in Zoho. The app records this factory but does not change Zoho stock.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Invoice — from "loaded" onwards. One card, one wording for every kind of
            load: waiting until the papers for this truck exist in Zoho, OK after. */}
        {(showDocs || zohoActions) && (
          <Card className={
            (zohoActions?.invoice?.error || zohoActions?.inventory?.error)
              ? 'border-red-300 bg-red-50'
              : docsPending
                ? 'border-orange-300 bg-orange-50'
                : 'border-green-300 bg-green-50'
          }>
            <CardContent className="p-4 space-y-2 text-sm">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Invoice</p>
              {showDocs && (docsPending ? (
                <div data-testid="docs-pending">
                  <p className="text-orange-800 font-medium">⏳ Waiting for invoice from Accounts</p>
                  <p className="text-xs text-stone-600 mt-0.5">
                    {delivery.accounts_notified_at ? `Notified ${fmtWhen(delivery.accounts_notified_at)}` : 'Accounts not yet notified'}
                    {delivery.accounts_reminder_count > 0 ? ` · reminded ${delivery.accounts_reminder_count}×` : ''}
                    {delivery.loaded_at ? ` · loaded ${fmtWhen(delivery.loaded_at)}` : ''}
                  </p>
                  {zohoActions?.accounts_notified?.sent === true && (
                    <p className="text-xs text-green-700 mt-0.5">WhatsApp alert sent to Accounts {zohoActions.accounts_notified.to || ''}.</p>
                  )}
                  {zohoActions?.accounts_notified && zohoActions.accounts_notified.sent === false && (
                    <p className="text-xs text-amber-700 mt-0.5">
                      WhatsApp alert not sent ({zohoActions.accounts_notified.error || zohoActions.accounts_notified.reason || 'notifications disabled'}) — the hand-off is still logged in the activity feed.
                    </p>
                  )}
                  {delivery.needs_manual_link && (
                    <p className="text-xs text-amber-800 mt-1">
                      More than one invoice matches this load on the sales order — {isManager ? 'link the right one below.' : 'ask an admin or manager to link the right one.'}
                    </p>
                  )}
                  <a
                    href={waHref}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2.5 flex items-center justify-center gap-2 w-full h-11 rounded-md bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold text-sm no-underline"
                  >
                    <span aria-hidden="true">📲</span> Message Accounts on WhatsApp
                  </a>
                  <p className="text-[11px] text-stone-500 mt-1">
                    Opens WhatsApp with the load, truck, client &amp; challan details pre-filled — pick your Accounts group, then send.
                  </p>
                  {isManager && (
                    <Button variant="outline" onClick={linkInvoice} disabled={updating} className="mt-2 w-full text-xs h-8" data-testid="link-invoice">
                      🔗 Link invoice…
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-green-700 font-medium" data-testid="docs-ready">
                  ✓ Invoice OK{delivery.status === 'loaded' ? ' — truck may depart' : ''}
                  {delivery.cleared_at ? <span className="text-xs font-normal text-stone-600"> · {fmtWhen(delivery.cleared_at)}</span> : null}
                </p>
              ))}
              {showDocs && delivery.delivered_from_warehouse && (
                <p className="text-xs text-stone-600">Loaded at {delivery.delivered_from_warehouse}</p>
              )}
              {/* Free Delivery — category + reason (the adjustment line is below) */}
              {delivery.is_stock_transfer ? (
                <div>
                  <p className="text-stone-700 text-xs">
                    Free Delivery · <span className="font-semibold">{STOCK_TRANSFER_LABEL(delivery.stock_transfer_category)}</span>
                  </p>
                  {delivery.stock_transfer_reason && (
                    <p className="text-xs text-stone-600 italic mt-0.5">"{delivery.stock_transfer_reason}"</p>
                  )}
                </div>
              ) : (delivery.zoho_invoice_id || zohoActions?.invoice) && (
                <div>
                  {delivery.zoho_invoice_id || zohoActions?.invoice?.id ? (
                    <p>
                      <span className="text-green-700">✓ Invoice</span>{' '}
                      <span className="font-semibold">{delivery.invoice_number || zohoActions?.invoice?.number || '—'}</span>{' '}
                      <a
                        href={`https://books.zoho.in/app/#/invoices/${delivery.zoho_invoice_id || zohoActions?.invoice?.id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs text-amber-800 underline underline-offset-2"
                      >View in Zoho ↗</a>
                    </p>
                  ) : (
                    <p className="text-red-700">✗ Invoice: {zohoActions?.invoice?.error}</p>
                  )}
                </div>
              )}
              {/* Inventory */}
              {(delivery.zoho_outbound_adj_id || zohoActions?.inventory) && (
                <div>
                  {delivery.zoho_outbound_adj_id || zohoActions?.inventory?.adj_id ? (
                    delivery.is_stock_transfer ? (
                      <p>
                        <span className="text-green-700">✓ Stock decremented</span>{' '}
                        {(delivery.delivered_from_warehouse || zohoActions?.inventory?.warehouse) && (
                          <span className="text-stone-600">from {delivery.delivered_from_warehouse || zohoActions?.inventory?.warehouse}</span>
                        )}
                      </p>
                    ) : (
                      // Rule: for invoice-eligible deliveries, ONLY the Zoho invoice
                      // reduces stock. This delivery has an app-side adjustment (pre-fix
                      // legacy), so an invoice on top would decrement twice — flag for
                      // Accounts to reverse the pre-existing adjustment in Zoho before invoicing.
                      <div className="text-xs bg-red-50 border border-red-300 rounded px-2 py-1.5 text-red-800">
                        <p className="font-semibold">⚠ Stock adjustment recorded on dispatch (legacy)</p>
                        <p className="mt-0.5">Stock was already reduced{(delivery.delivered_from_warehouse || zohoActions?.inventory?.warehouse) ? ` at ${delivery.delivered_from_warehouse || zohoActions?.inventory?.warehouse}` : ''} when this delivery was dispatched under the old flow. Reverse that adjustment in Zoho before raising the invoice, so stock isn't reduced twice.</p>
                      </div>
                    )
                  ) : (
                    <p className="text-red-700">✗ Stock: {zohoActions?.inventory?.error}</p>
                  )}
                </div>
              )}
              {/* Truck-loaded + DC copy attachments */}
              {zohoActions?.invoice_attachments && (
                <div>
                  {zohoActions.invoice_attachments.uploaded === zohoActions.invoice_attachments.total ? (
                    <p className="text-green-700">
                      ✓ {zohoActions.invoice_attachments.uploaded} photo{zohoActions.invoice_attachments.uploaded !== 1 ? 's' : ''} attached to invoice
                    </p>
                  ) : (
                    <>
                      <p className="text-amber-700">
                        ⚠ {zohoActions.invoice_attachments.uploaded}/{zohoActions.invoice_attachments.total} photos attached
                      </p>
                      {(zohoActions.invoice_attachments.errors || []).slice(0, 3).map((e, i) => (
                        <p key={i} className="text-xs text-amber-600">• {e}</p>
                      ))}
                    </>
                  )}
                </div>
              )}
              {/* Reminder that the e-Way Bill is raised by hand in Zoho — only while the truck is still in the yard */}
              {delivery.zoho_invoice_id && !delivery.is_stock_transfer && delivery.status === 'loaded' && (
                <p className="text-xs text-stone-500 italic border-t border-stone-200 pt-2 mt-2">
                  💡 Make sure the e-Way Bill is done in Zoho Books before the truck leaves.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer messaging history — mirrored from Zoho Books' "Comments & History"
             for this delivery's estimate + invoice. Includes WhatsApp sends, emails,
             and Zoho's own email-viewed confirmations. */}
        {(delivery.zoho_estimate_id || delivery.zoho_invoice_id) && (
          <Card>
            <div className="px-3 py-2 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">💬 Customer messages</span>
              <button onClick={refreshMessages} disabled={refreshingMsgs}
                      className="text-[11px] text-amber-800 underline underline-offset-2 disabled:opacity-50">
                {refreshingMsgs ? 'Syncing…' : 'Refresh'}
              </button>
            </div>
            <CardContent className="px-3 py-2.5">
              {messages.length === 0 ? (
                <p className="text-xs text-stone-400 py-2">
                  No messages yet. Zoho auto-sends estimates &amp; invoices on WhatsApp/email when created — tap Refresh to sync.
                </p>
              ) : (
                <ul className="space-y-2">
                  {messages.map((m) => {
                    const isWA = m.msg_type === 'zoho_whatsapp';
                    const isEmail = m.msg_type === 'zoho_email';
                    const isEstimate = (m.doc_ref || '').includes(':estimates:');
                    const chip = isWA ? { label: 'WhatsApp', cls: 'bg-green-100 text-green-800 border-green-200' }
                              : isEmail ? { label: 'Email', cls: 'bg-blue-100 text-blue-800 border-blue-200' }
                              : { label: m.msg_type, cls: 'bg-stone-100 text-stone-700 border-stone-200' };
                    return (
                      <li key={m.id} className="flex items-start gap-2 py-1 border-b border-stone-100 last:border-0">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${chip.cls}`}>
                          {chip.label}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-stone-800 break-words">{m.body}</p>
                          <p className="text-[10px] text-stone-400 mt-0.5">
                            {new Date(m.created_at).toLocaleString()}
                            {' · '}{isEstimate ? 'Estimate' : 'Invoice'}
                            {m.commented_by ? ` · by ${m.commented_by}` : ''}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action button — three gates:
             confirmed → loaded   : payment cleared (funded)  — admin override with a reason
             loaded → in_transit  : invoice OK (cleared) — no override
             in_transit → delivered: at least one delivered-load photo (pushed to the invoice) */}
        {nextStatus && (() => {
          const postDispatchPhotos = (delivery.pod_media || []).filter(
            m => !m.zoho_invoice_attached && m.media_type !== 'video'
          );
          const blockDelivered = nextStatus === 'delivered' && postDispatchPhotos.length === 0 && !delivery.is_stock_transfer;
          const paymentBlocked = nextStatus === 'loaded' && !delivery.is_stock_transfer && delivery.funded === false;
          const docsBlocked = nextStatus === 'in_transit' && !delivery.cleared;
          const blocked = blockDelivered || paymentBlocked || docsBlocked;
          const shortTxt = Number(delivery.shortfall || 0) > 0 ? ` · ${fmtMoney(delivery.shortfall)} short` : '';
          const label = updating ? 'Updating...'
            : paymentBlocked ? `🔒 Payment not OK${shortTxt}`
            : docsBlocked ? '🔒 Waiting for invoice'
            : nextLabel;
          return (
            <div className="space-y-2">
              <Button
                onClick={() => updateStatus(nextStatus)}
                disabled={updating || blocked}
                className={`w-full h-12 text-base font-bold ${nextStatus === 'delivered' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                title={blockDelivered ? 'Capture a delivered-load photo first' : paymentBlocked ? 'Loading starts once the payment is recorded in Zoho Books' : docsBlocked ? 'The truck departs once Accounts raise the invoice in Zoho Books' : undefined}
                data-testid="delivery-action"
              >
                {label}
              </Button>
              {paymentBlocked && (
                <p className="text-[11px] text-stone-500 text-center">
                  Loading starts once the payment is recorded in Zoho Books — the balance is re-checked each time this page opens.
                  {Number(delivery.pending_other_loads || 0) > 0 && ` Includes ${fmtMoney(delivery.pending_other_loads)} for this customer's other loaded trucks still waiting for the invoice.`}
                </p>
              )}
              {paymentBlocked && isAdmin && (
                <Button variant="outline" onClick={overrideLoading} disabled={updating}
                  className="w-full text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid="override-loading">
                  Override payment gate… (admin, logged)
                </Button>
              )}
              {docsBlocked && (
                <p className="text-[11px] text-stone-500 text-center">
                  The truck departs once Accounts raise the invoice in Zoho Books — checked automatically every few minutes.
                </p>
              )}
            </div>
          );
        })()}

        {delivery.status === 'confirmed' && isManager && (
          <Button variant="ghost" onClick={cancelLoad} disabled={updating} className="w-full text-xs text-stone-500 hover:text-red-700" data-testid="cancel-load">
            Cancel this load
          </Button>
        )}
        {delivery.status === 'loaded' && isAdmin && (
          <Button variant="ghost" onClick={undoLoading} disabled={updating} className="w-full text-xs text-stone-500 hover:text-stone-800">
            Truck unloaded — back to Confirmed
          </Button>
        )}

        {delivery.status === 'delivered' && (
          <div className="text-center py-4">
            <p className="text-green-600 font-bold text-lg">✓ Delivered</p>
            {delivery.delivered_time && <p className="text-xs text-gray-400">{fmtDate(delivery.delivered_time)}</p>}
          </div>
        )}
      </div>

      {isAdmin && (
        <AdminEditDeliveryModal
          open={showEditModal}
          delivery={delivery}
          onClose={() => setShowEditModal(false)}
          onSaved={loadDelivery}
        />
      )}
    </div>
  );
};

// Admin-only edit modal for a delivery. Lets you fix truck/driver/warehouse/notes
// plus advanced Zoho ids (salesorder_id, invoice_id) when Zoho got a bad link.
const AdminEditDeliveryModal = ({ open, delivery, onClose, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({
    truck_number: '',
    driver_name: '',
    driver_mobile: '',
    delivered_from_warehouse: '',
    notes: '',
    zoho_estimate_id: '',
    zoho_salesorder_id: '',
    salesorder_number: '',
    zoho_invoice_id: '',
    invoice_number: '',
  });

  useEffect(() => {
    if (!open || !delivery) return;
    setErr('');
    setBusy(false);
    setShowAdvanced(false);
    setForm({
      truck_number: delivery.truck_number || '',
      driver_name: delivery.driver_name || '',
      driver_mobile: delivery.driver_mobile || '',
      delivered_from_warehouse: delivery.delivered_from_warehouse || '',
      notes: delivery.notes || '',
      zoho_estimate_id: delivery.zoho_estimate_id || '',
      zoho_salesorder_id: delivery.zoho_salesorder_id || '',
      salesorder_number: delivery.salesorder_number || '',
      zoho_invoice_id: delivery.zoho_invoice_id || '',
      invoice_number: delivery.invoice_number || '',
    });
    axios.get(`${API}/production/warehouses`)
      .then(r => setWarehouses(r.data?.warehouses || []))
      .catch(() => setWarehouses([]));
  }, [open, delivery]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      // Send only fields the admin actually changed (compared to loaded delivery),
      // so unrelated fields stay untouched.
      const payload = {};
      const orig = {
        truck_number: delivery.truck_number || '',
        driver_name: delivery.driver_name || '',
        driver_mobile: delivery.driver_mobile || '',
        delivered_from_warehouse: delivery.delivered_from_warehouse || '',
        notes: delivery.notes || '',
        zoho_estimate_id: delivery.zoho_estimate_id || '',
        zoho_salesorder_id: delivery.zoho_salesorder_id || '',
        salesorder_number: delivery.salesorder_number || '',
        zoho_invoice_id: delivery.zoho_invoice_id || '',
        invoice_number: delivery.invoice_number || '',
      };
      for (const k of Object.keys(form)) {
        if ((form[k] || '') !== (orig[k] || '')) payload[k] = form[k];
      }
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      await axios.put(`${API}/deliveries/${delivery.delivery_id}`, payload);
      await onSaved();
      onClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  if (!delivery) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit delivery</DialogTitle>
          <DialogDescription className="text-xs">
            Admin-only. Empty a field to clear it. Line items and address come from Zoho and aren't editable here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Truck number</Label>
              <Input value={form.truck_number} onChange={set('truck_number')} placeholder="KA-01-AB-1234" />
            </div>
            <div>
              <Label className="text-xs">Driver name</Label>
              <Input value={form.driver_name} onChange={set('driver_name')} placeholder="Driver name" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Driver mobile (WhatsApp)</Label>
            <Input value={form.driver_mobile} onChange={set('driver_mobile')} placeholder="9876543210" inputMode="numeric" />
          </div>
          <div>
            <Label className="text-xs">Warehouse (loaded from)</Label>
            <select
              value={form.delivered_from_warehouse}
              onChange={set('delivered_from_warehouse')}
              className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">— Not set —</option>
              {warehouses.map(w => (
                <option key={w.warehouse_id} value={w.warehouse_name}>{warehouseDisplayName(w.warehouse_name)}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={set('notes')} placeholder="Free-form notes" />
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="text-xs text-amber-700 hover:text-amber-900 underline"
          >
            {showAdvanced ? '− Hide Zoho ids' : '+ Advanced: Zoho ids'}
          </button>

          {showAdvanced && (
            <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">
                ⚠ Only touch these to fix a broken Zoho link. Wrong ids will break invoice + stock sync on the next dispatch.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Estimate ID</Label>
                  <Input value={form.zoho_estimate_id} onChange={set('zoho_estimate_id')} className="font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Sales Order ID</Label>
                  <Input value={form.zoho_salesorder_id} onChange={set('zoho_salesorder_id')} className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Sales Order Number</Label>
                <Input value={form.salesorder_number} onChange={set('salesorder_number')} placeholder="SO-019" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Invoice ID</Label>
                  <Input value={form.zoho_invoice_id} onChange={set('zoho_invoice_id')} className="font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Invoice Number</Label>
                  <Input value={form.invoice_number} onChange={set('invoice_number')} placeholder="INV-000123" />
                </div>
              </div>
            </div>
          )}

          {err && <p className="text-xs text-red-600">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-amber-800 hover:bg-amber-900 text-white">
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Legally-defensible stock transfer categories — labels shown in the UI.
const STOCK_TRANSFER_CATEGORIES = [
  { key: 'sample',       label: 'Sample / demo' },
  { key: 'internal_use', label: 'Internal use' },
  { key: 'wastage',      label: 'Wastage' },
  { key: 'damage',       label: 'Damaged in handling' },
  { key: 'adjustment',   label: 'Stock adjustment' },
  { key: 'other',        label: 'Other (see reason)' },
];
const STOCK_TRANSFER_LABEL = (key) => (STOCK_TRANSFER_CATEGORIES.find(c => c.key === key) || {}).label || key;

// Shared /auth/me cache so we don't fire one request per estimate row.
// Cleared by the axios 401 interceptor via invalidateCachedMe so a switched-user
// session doesn't briefly see the previous user's role in the UI.
// Also mirrored to localStorage as 'eb_me' so static pages outside the React
// bundle (e.g. public/atlas/index.html) can read the current user's role
// without making an API call.
let _cachedMe = null;
let _cachedMePromise = null;
const getCachedMe = () => {
  if (_cachedMe) return Promise.resolve(_cachedMe);
  if (_cachedMePromise) return _cachedMePromise;
  _cachedMePromise = axios.get(`${API}/auth/me`)
    .then(r => {
      _cachedMe = r.data;
      try { localStorage.setItem('eb_me', JSON.stringify(r.data)); } catch (e) {}
      return r.data;
    })
    .catch(() => null)
    .finally(() => { _cachedMePromise = null; });
  return _cachedMePromise;
};
invalidateCachedMe = () => {
  _cachedMe = null;
  _cachedMePromise = null;
  try { localStorage.removeItem('eb_me'); } catch (e) {}
};

// Zoho location ids of the two loading warehouses — the same ids the backend
// gates stock against. Labels are the names staff use.
const CALL_OFF_WAREHOUSES = [
  { id: '1535795000007435074', label: 'Virgonagar Factory' },
  { id: '1535795000007435298', label: 'Marasandra Factory (M Annapoorneshwari)' },
];
const fmtQty = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Who owns this customer. The owner is the one salesperson who may quote, book
// trucks and move deliveries for them; the backend refuses everyone else with a
// 403. Renders straight from the endpoint's own flags (is_mine / can_claim /
// can_reassign), so it needs no role prop and no second request to work them out.
const CustomerOwnerBar = ({ customerId, customerName, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [people, setPeople] = useState(null);   // staff list, fetched only when the picker opens
  const [picking, setPicking] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/customers/${customerId}/owner`);
      setInfo(r.data);
      setFailed(false);
    } catch (e) {
      setFailed(true);
    }
  };
  useEffect(() => { if (customerId) load(); }, [customerId]);

  const claim = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/customers/${customerId}/claim`, { customer_name: customerName || '' });
      setInfo(i => ({ ...(i || {}), owner: r.data.owner, is_mine: true, can_claim: false, can_act: true }));
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not claim this customer');
      load();
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    setPicking(true); setErr('');
    if (people) return;
    try {
      const r = await axios.get(`${API}/users`);
      const rows = (Array.isArray(r.data) ? r.data : [])
        .filter(u => !u.is_disabled && ['admin', 'manager', 'staff'].includes(u.role));
      setPeople(rows);
    } catch (e) {
      setPeople([]);
      setErr('Could not load the staff list');
    }
  };

  const reassign = async (salespersonId) => {
    setBusy(true); setErr('');
    try {
      await axios.put(`${API}/customers/${customerId}/salesperson`, {
        salesperson_id: salespersonId || null,
        customer_name: customerName || '',
      });
      setPicking(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not reassign this customer');
    } finally {
      setBusy(false);
    }
  };

  if (!customerId || failed) return null;
  if (!info) return <span className="text-[11px] text-stone-400">Checking owner…</span>;

  const owner = info.owner;
  const badge = owner
    ? (info.is_mine
        ? { text: '👤 Your customer', cls: 'bg-blue-50 text-blue-800 border-blue-200' }
        : { text: `👤 ${owner.salesperson_name}'s customer`, cls: info.can_act
              ? 'bg-stone-100 text-stone-700 border-stone-200'
              : 'bg-amber-50 text-amber-800 border-amber-200' })
    : { text: '👤 Unclaimed', cls: 'bg-stone-100 text-stone-600 border-stone-200 border-dashed' };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`customer-owner-${customerId}`}>
      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${badge.cls}`}
        title={owner?.claimed_at ? `Claimed ${fmtDate(owner.claimed_at)}` : 'Nobody has claimed this customer yet'}>
        {badge.text}
      </span>
      {info.can_claim && (
        <Button size="sm" variant="outline" onClick={claim} disabled={busy}
          className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
          data-testid={`claim-customer-${customerId}`}>
          {busy ? 'Claiming…' : 'Claim'}
        </Button>
      )}
      {info.can_reassign && !picking && (
        <Button size="sm" variant="ghost" onClick={openPicker} disabled={busy}
          className="text-xs text-stone-500 hover:text-blue-700">
          Reassign…
        </Button>
      )}
      {info.can_reassign && picking && (
        <span className="flex items-center gap-1">
          <select
            className="text-xs border rounded px-1 py-0.5 bg-white"
            defaultValue={owner?.salesperson_id || ''}
            disabled={busy || people === null}
            onChange={(e) => reassign(e.target.value)}
            data-testid={`reassign-customer-${customerId}`}
          >
            <option value="">{people === null ? 'Loading…' : '— unclaimed —'}</option>
            {(people || []).map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
            ))}
          </select>
          <button type="button" onClick={() => { setPicking(false); setErr(''); }}
            className="text-[11px] text-stone-400 hover:text-stone-700">cancel</button>
        </span>
      )}
      {err && <span className="text-[11px] text-red-600">{err}</span>}
    </div>
  );
};

// Controls on an accepted quote row: the order line ("Order open · SO-n ·
// supplied x / y"), "Book truck" (raises a load) and "Close order".
const OrderLoadControls = ({ estimateId, estimateNumber, customerName, onCreated, onClosed }) => {
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  const [me, setMe] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/orders/${estimateId}`);
      setOrder(r.data.order || null);
      setFailed(false);
    } catch (e) {
      setFailed(true);
    }
  };
  useEffect(() => { getCachedMe().then(setMe); load(); }, [estimateId]);

  const roles = me ? [me.role, ...(me.extra_roles || [])] : [];
  const canLoad = roles.some(r => ['admin', 'manager', 'staff'].includes(r));
  const canClose = roles.some(r => ['admin', 'manager'].includes(r));

  const closeOrder = async () => {
    const ok = window.confirm(
      `Close order ${estimateNumber || order?.estimate_number || ''}?\n\n` +
      'The unsupplied quantity is released from the supply plan (the sales order is closed in Zoho: ' +
      'voided if nothing was invoiced yet, otherwise trimmed to what was invoiced). ' +
      'Loads already delivered are not affected.'
    );
    if (!ok) return;
    setClosing(true); setErr('');
    try {
      await axios.post(`${API}/orders/${estimateId}/close`);
      await load();
      if (onClosed) onClosed();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not close the order');
    } finally {
      setClosing(false);
    }
  };

  if (failed) return <span className="text-[11px] text-stone-400">Order details unavailable</span>;
  if (!order) return <span className="text-[11px] text-stone-400">Loading order…</span>;
  if (!order.salesorder_id) {
    return <span className="text-[11px] text-amber-700">Not opened yet — approve the quote to open the order</span>;
  }

  const closed = order.status === 'closed';
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`order-controls-${estimateId}`}>
      <span
        className={`text-[11px] px-2 py-0.5 rounded-full border ${closed ? 'bg-stone-100 text-stone-600 border-stone-200' : 'bg-green-50 text-green-800 border-green-200'}`}
        title={order.needed_by ? `Requested by ${fmtDate(order.needed_by)}` : undefined}
      >
        {closed ? 'Order closed' : 'Order open'}
        {order.salesorder_number ? ` · ${order.salesorder_number}` : ''}
        {' · supplied '}{fmtQty(order.supplied_qty)} / {fmtQty(order.ordered_qty)}
        {order.loads > 0 ? ` · ${order.loads} load${order.loads !== 1 ? 's' : ''}` : ''}
        {order.open_loads > 0 ? ` (${order.open_loads} open)` : ''}
      </span>
      {!closed && canLoad && (
        <Button size="sm" variant="outline" onClick={() => setShowModal(true)}
          className="text-xs border-green-300 text-green-700 hover:bg-green-50" data-testid={`new-load-${estimateId}`}>
          🚛 Book truck
        </Button>
      )}
      {!closed && canClose && (
        <Button size="sm" variant="ghost" onClick={closeOrder} disabled={closing}
          className="text-xs text-stone-500 hover:text-red-700">
          {closing ? 'Closing…' : 'Close order'}
        </Button>
      )}
      {err && <span className="text-[11px] text-red-600">{err}</span>}
      {showModal && (
        <CallOffModal
          estimateId={estimateId}
          order={order}
          customerName={customerName || order.customer_name}
          onCancel={() => setShowModal(false)}
          onCreated={(resp) => {
            setShowModal(false);
            if (resp.order) setOrder(resp.order); else load();
            const warn = (resp.warnings || []).length ? `\n\n${resp.warnings.join('\n')}` : '';
            alert(`Truck ${resp.call_off_seq} booked for ${order.estimate_number}` +
              (resp.challan_number ? `\nTruck slip: ${resp.challan_number}` : '') + warn);
            if (onCreated) onCreated(resp.delivery_id);
          }}
        />
      )}
    </div>
  );
};

// Book one truck against an open order: partial quantities, loading
// warehouse and date. The backend refuses the load when physical on-hand at that
// warehouse is short (409 stock_short); admin/manager may override with a reason.
const CallOffModal = ({ estimateId, order, customerName, onCancel, onCreated }) => {
  const today = localISODate();
  const lines = order.lines || [];
  const dominantLocation = (() => {
    const counts = {};
    lines.forEach(l => { if (l.location_id) counts[l.location_id] = (counts[l.location_id] || 0) + 1; });
    const best = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
    return CALL_OFF_WAREHOUSES.some(w => w.id === best) ? best : CALL_OFF_WAREHOUSES[0].id;
  })();

  const [qty, setQty] = useState(() => Object.fromEntries(lines.map(l => [l.line_item_id, String(Math.max(0, Number(l.remaining) || 0))])));
  const [locationId, setLocationId] = useState(dominantLocation);
  const [date, setDate] = useState(order.needed_by && order.needed_by >= today ? order.needed_by : today);
  const [isST, setIsST] = useState(false);
  const [category, setCategory] = useState('sample');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [preflightErr, setPreflightErr] = useState('');
  const [stockShort, setStockShort] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { getCachedMe().then(setMe); }, []);
  // Live on-hand per line at the chosen warehouse + the customer's funds.
  useEffect(() => {
    let alive = true;
    setPreflight(null); setPreflightErr('');
    axios.get(`${API}/orders/${estimateId}/preflight?location_id=${encodeURIComponent(locationId)}`)
      .then(r => { if (alive) setPreflight(r.data); })
      .catch(e => {
        if (!alive) return;
        const d = e.response?.data?.detail;
        setPreflightErr(typeof d === 'string' ? d : 'Could not read stock for this warehouse');
      });
    return () => { alive = false; };
  }, [estimateId, locationId]);

  const roles = me ? [me.role, ...(me.extra_roles || [])] : [];
  const canOverride = roles.some(r => ['admin', 'manager'].includes(r));
  const warehouse = CALL_OFF_WAREHOUSES.find(w => w.id === locationId);
  const onHandOf = (l) => {
    const pl = (preflight?.order?.lines || []).find(x => x.line_item_id === l.line_item_id);
    return pl && pl.on_hand != null ? Number(pl.on_hand) : null;
  };
  const q = (l) => Number(qty[l.line_item_id]) || 0;
  const totalQty = lines.reduce((s, l) => s + q(l), 0);
  const approxTotal = lines.reduce((s, l) => s + q(l) * (Number(l.rate) || 0), 0);
  const funds = preflight?.funds || null;
  const pendingOther = Number(funds?.pending_other_loads || 0);
  const prepaid = funds ? Number(funds.prepaid_amount || 0) : null;
  const fundsShort = funds && !funds.credit_terms && !isST ? Math.max(0, approxTotal + pendingOther - prepaid) : 0;

  const setLineQty = (l, v) => { setStockShort(null); setQty(prev => ({ ...prev, [l.line_item_id]: v })); };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    const bodyLines = lines.filter(l => q(l) > 0).map(l => ({ line_item_id: l.line_item_id, quantity: q(l) }));
    if (!bodyLines.length) { setErr('Enter a quantity for at least one line'); return; }
    if (!date || date < today) { setErr('Pick a delivery date (today or later)'); return; }
    if (isST && (!reason || reason.trim().length < 5)) { setErr('Free Delivery reason is required (min 5 characters)'); return; }
    const overriding = !!stockShort;
    if (overriding && !canOverride) { setErr('Only an admin or manager can override the stock check'); return; }
    if (overriding && overrideReason.trim().length < 5) { setErr('Override reason must be at least 5 characters'); return; }
    setBusy(true);
    try {
      const resp = await axios.post(`${API}/orders/${estimateId}/call-offs`, {
        lines: bodyLines,
        scheduled_date: date,
        location_id: locationId,
        is_stock_transfer: isST,
        stock_transfer_category: isST ? category : null,
        stock_transfer_reason: isST ? reason.trim() : null,
        notes: notes.trim() || null,
        override_stock: overriding,
        override_reason: overriding ? overrideReason.trim() : null,
      });
      onCreated(resp.data);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      if (e2.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'stock_short') {
        setStockShort(detail);
      } else {
        setErr(typeof detail === 'string' ? detail : (detail?.message || 'Could not book the truck'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Book truck — {order.estimate_number}</DialogTitle>
          <DialogDescription className="text-xs">
            {customerName || '—'}{order.salesorder_number ? ` · ${order.salesorder_number}` : ''} · load {(order.loads || 0) + 1}
            {order.needed_by ? ` · requested by ${fmtDate(order.needed_by)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Loading warehouse</Label>
              <select value={locationId} onChange={(e) => { setStockShort(null); setLocationId(e.target.value); }}
                className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="calloff-warehouse">
                {CALL_OFF_WAREHOUSES.map(w => <option key={w.id} value={w.id}>{w.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Delivery date</Label>
              <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)}
                className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="calloff-date" />
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border border-stone-200">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 text-stone-500">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">Item</th>
                  <th className="text-right px-2 py-1.5 font-medium">Ordered</th>
                  <th className="text-right px-2 py-1.5 font-medium">Supplied</th>
                  <th className="text-right px-2 py-1.5 font-medium">Remaining</th>
                  <th className="text-right px-2 py-1.5 font-medium">On hand</th>
                  <th className="text-right px-2 py-1.5 font-medium w-28">This load</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => {
                  const onHand = onHandOf(l);
                  const v = q(l);
                  const over = v > (Number(l.remaining) || 0);
                  const short = onHand != null && v > 0 && v > onHand;
                  return (
                    <React.Fragment key={l.line_item_id}>
                      <tr className="border-t border-stone-100">
                        <td className="px-2 py-1.5 text-stone-800">{l.name}<span className="text-stone-400"> · {l.unit || 'Nos'}</span></td>
                        <td className="px-2 py-1.5 text-right text-stone-500">{fmtQty(l.ordered)}</td>
                        <td className="px-2 py-1.5 text-right text-stone-500">{fmtQty(l.supplied)}</td>
                        <td className="px-2 py-1.5 text-right font-medium text-stone-700">{fmtQty(l.remaining)}</td>
                        <td className={`px-2 py-1.5 text-right ${onHand == null ? 'text-stone-300' : onHand <= 0 ? 'text-red-600' : 'text-stone-700'}`}>
                          {preflight ? (onHand == null ? '—' : fmtQty(onHand)) : (preflightErr ? '?' : '…')}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <input type="number" min="0" step="1" value={qty[l.line_item_id] ?? ''}
                            onChange={(e) => setLineQty(l, e.target.value)}
                            className={`w-24 border rounded-md px-2 py-1 text-right text-sm ${short ? 'border-red-400' : over ? 'border-amber-400' : 'border-stone-300'}`}
                            data-testid={`calloff-qty-${l.line_item_id}`} />
                        </td>
                      </tr>
                      {(over || short) && (
                        <tr>
                          <td colSpan={6} className="px-2 pb-1.5 text-[11px]">
                            {short && <span className="text-red-600 mr-2">Only {fmtQty(onHand)} on hand at {warehouse?.label || 'this warehouse'} — the load will be refused unless overridden.</span>}
                            {over && <span className="text-amber-700">More than the remaining {fmtQty(l.remaining)} on the order — allowed, noted as a warning.</span>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {preflightErr && <p className="text-[11px] text-amber-700">{preflightErr} — the stock check still runs when you submit.</p>}

          <div className="flex flex-wrap justify-between gap-2 text-xs text-stone-600">
            <span>This load: <b>{fmtQty(totalQty)}</b> pcs · ≈ {fmtMoney(approxTotal)} before tax</span>
            {isST ? (
              <span className="text-stone-500">Free Delivery — no payment hold</span>
            ) : funds ? (
              funds.credit_terms
                ? <span className="text-green-700">Credit account — loading is not held for payment</span>
                : <span className={fundsShort > 0 ? 'text-amber-700' : 'text-green-700'}>
                    Prepaid {fmtMoney(prepaid)}
                    {pendingOther > 0 ? ` · other loads waiting for invoice ${fmtMoney(pendingOther)}` : ''}
                    {fundsShort > 0 ? ` · short ≈ ${fmtMoney(fundsShort)} — loading waits for payment` : ' · covers this load'}
                  </span>
            ) : (
              <span className="text-stone-400">{preflight ? 'Balance not available' : 'Checking balance…'}</span>
            )}
          </div>

          <label className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 p-3 text-sm cursor-pointer">
            <input type="checkbox" checked={isST} onChange={(e) => setIsST(e.target.checked)} className="mt-0.5" />
            <div>
              <div className="font-medium text-stone-800">Free Delivery</div>
              <div className="text-xs text-stone-500 mt-0.5">
                Samples, internal use, wastage, damage and one-off adjustments. Stock still moves in Zoho with a categorised reason. This action is logged.
              </div>
            </div>
          </label>
          {isST && (
            <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <div>
                <Label className="text-xs">Category</Label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                  {STOCK_TRANSFER_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Reason (required)</Label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  placeholder="Sample for site visit, broken during loading, owner testing new mix, ..."
                  className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm" />
                <p className="text-[11px] text-stone-500 mt-1">
                  Included on the Zoho stock adjustment as: <span className="font-mono">Stock Transfer · {category} · {reason.trim().slice(0, 40) || '…'}</span>
                </p>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Notes for the loading team (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate timing, site contact, unloading help…" className="text-sm" />
          </div>

          {stockShort && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs space-y-2" data-testid="calloff-stock-short">
              <p className="font-medium text-red-800">Not enough stock at {stockShort.location || warehouse?.label || 'the warehouse'}</p>
              <ul className="list-disc pl-4 text-red-700 space-y-0.5">
                {(stockShort.lines || []).map(s => (
                  <li key={s.item_id || s.name}>{s.name}: requested {fmtQty(s.requested)}, on hand {fmtQty(s.on_hand)} (short {fmtQty(s.short)})</li>
                ))}
              </ul>
              {canOverride ? (
                <div>
                  <Label className="text-xs text-red-800">Override reason (admin/manager, logged)</Label>
                  <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={2}
                    placeholder="Stock counted physically this morning; Zoho adjustment pending…"
                    className="w-full border border-red-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="calloff-override-reason" />
                </div>
              ) : (
                <p className="text-red-700">Reduce the quantity, pick another warehouse, or ask an admin or manager to override.</p>
              )}
            </div>
          )}

          {err && <p className="text-xs text-red-600">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy || (!!stockShort && !canOverride)}
              className={stockShort ? 'bg-red-700 hover:bg-red-800 text-white' : isST ? 'bg-amber-800 hover:bg-amber-900 text-white' : 'bg-green-700 hover:bg-green-800 text-white'}
              data-testid="calloff-submit">
              {busy ? 'Booking…' : stockShort ? 'Override and book truck' : isST ? 'Book Free Delivery truck' : 'Book truck'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── FACTORY STAFF PORTAL ────────────────────────────────────────────────────

// Which categories are produced at which location
// `label` is what the user sees; `warehouse` is the canonical Zoho branch name that
// gets sent to the backend. `aliases` are alternative local names accepted from a
// user's `factory_warehouse` field, so records saved with older names still resolve.
const FACTORY_LOCATIONS = [
  {
    id: 'virgonagar', label: 'Virgonagar Factory',
    warehouse: 'Virgonagar Factory',
    aliases: ['virgonagar', 'virgonagar factory'],
    categories: ['interlocking', 'small', 'pointing'],
  },
  {
    id: 'annapoorneshwari', label: 'Marasandra Factory',
    subtitle: 'M Annapoorneshwari Enterprise',
    warehouse: 'M Annapoorneshwari Enterprise',
    aliases: ['marasandra', 'marasandra factory', 'annapoorneshwari', 'm annapoorneshwari', 'm annapoorneshwari enterprise'],
    categories: ['smart', 'solid', 'pointing'],
  },
];

// Match a possibly-aliased warehouse name (from a user's factory_warehouse field or
// a stored delivery record) to a FACTORY_LOCATIONS entry.
const findFactoryLocation = (name) => {
  if (!name) return null;
  const n = String(name).toLowerCase().trim();
  return FACTORY_LOCATIONS.find(l =>
    l.warehouse.toLowerCase() === n ||
    l.label.toLowerCase() === n ||
    (l.aliases || []).includes(n)
  ) || null;
};

// Friendly label for a canonical Zoho branch name — used in dropdowns/badges so users
// see the local name they're used to ("Marasandra") next to the Zoho name for clarity.
const warehouseDisplayName = (canonicalName) => {
  const loc = findFactoryLocation(canonicalName);
  if (!loc) return canonicalName || '';
  return loc.subtitle ? `${loc.label} (${loc.subtitle})` : loc.label;
};

// Categories that should start collapsed on the Factory Portal — pointing / crushed
// soil is a rare line, and having it always open pushed the primary block categories
// off the visible area on smaller screens.
const _COLLAPSED_BY_DEFAULT = new Set(['pointing']);
const _defaultOpenCategories = (loc) => {
  const open = {};
  (loc?.categories || []).forEach(k => { open[k] = !_COLLAPSED_BY_DEFAULT.has(k); });
  return open;
};

const FactoryPortal = () => {
  const navigate = useNavigate();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [produced, setProduced] = useState({});
  const [notes, setNotes] = useState('');
  const [cementBags, setCementBags] = useState('');   // string in state so empty input clears cleanly
  const [labourCount, setLabourCount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null); // null = not yet chosen
  const [openCategories, setOpenCategories] = useState({});       // category key → bool
  const [capturedImages, setCapturedImages] = useState([]);       // array of base64 data URLs
  const [syncResult, setSyncResult] = useState(null);             // structured server response after submit
  const [errorMsg, setErrorMsg] = useState('');                   // e.g. unknown warehouse (400)
  const [currentUser, setCurrentUser] = useState(null);
  const [retryingLog, setRetryingLog] = useState(null);           // log_id currently being retried
  const [editingLog, setEditingLog] = useState(null);             // the log object currently open in the edit modal
  const imageInputRef = React.useRef(null);

  const today = fmtDate(new Date());

  useEffect(() => {
    document.title = "EarthBlocks | Customer Portal Blocks Factory Portal";
    axios.get(`${API}/auth/me`).then(r => {
      setCurrentUser(r.data);
      // Per-factory login: auto-select this user's factory and hide the switcher.
      // findFactoryLocation accepts aliases ("Marasandra Factory" resolves to
      // M Annapoorneshwari Enterprise) so records saved with the local name still work.
      const loc = findFactoryLocation(r.data?.factory_warehouse);
      if (loc) {
        setSelectedLocation(loc);
        setOpenCategories(_defaultOpenCategories(loc));
      }
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('eb_token');
    localStorage.removeItem('eb_refresh');
    invalidateCachedMe();
    navigate('/login');
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inventory/items`).then(r => setInventoryItems(r.data)).catch(() => {}),
      axios.get(`${API}/production/logs?limit=7`).then(r => setRecentLogs(r.data.logs || [])).catch(() => {}),
    ]).finally(() => setLoadingItems(false));
  }, []);

  const selectLocation = (loc) => {
    setSelectedLocation(loc);
    setProduced({});   // clear entries when switching location
    setOpenCategories(_defaultOpenCategories(loc));
  };

  const toggleCategory = (key) => setOpenCategories(o => ({ ...o, [key]: !o[key] }));

  const compressImage = (file) => new Promise(resolve => {
    // Previous settings (1200 px / 0.72 quality) produced ~80 KB photos — too
    // soft to read block SKUs on the shift photo. 1920 px / 0.88 quality lands
    // around 300-600 KB per photo which is still well under Zoho's 10 MB
    // attachment cap and uploads fine on 4G in the field.
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1920;
        const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.88));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleImageCapture = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - capturedImages.length;
    const toProcess = files.slice(0, remaining);
    const compressed = await Promise.all(toProcess.map(compressImage));
    setCapturedImages(imgs => [...imgs, ...compressed]);
    e.target.value = '';   // reset so same file can be re-selected
  };

  const removeImage = (idx) => setCapturedImages(imgs => imgs.filter((_, i) => i !== idx));

  const handleQty = (item, val) => {
    if (!val || parseFloat(val) <= 0) {
      setProduced(p => { const n = { ...p }; delete n[item.id]; return n; });
    } else {
      setProduced(p => ({ ...p, [item.id]: { item, qty: parseFloat(val) } }));
    }
  };

  const handleSubmit = async () => {
    const entries = Object.values(produced);
    if (!entries.length) { alert('Enter at least one item quantity'); return; }
    if (!selectedLocation) { alert('Select a factory location first'); return; }
    // Photo is mandatory — every production log must have a shift photo attached
    // to the Zoho inventory adjustment for audit / dispute resolution.
    if (!capturedImages.length) { alert('Take at least one production photo before logging — this attaches to the Zoho adjustment as proof.'); return; }
    // Cement bags + labour count are shift-audit inputs and mandatory — a shift log
    // without them isn't a complete record for costing / dispute resolution.
    if (cementBags.trim() === '') { alert('Enter the number of Cement Bags used this shift.'); return; }
    if (labourCount.trim() === '') { alert('Enter the number of Labour who worked this shift.'); return; }
    setSubmitting(true);
    try {
      // Empty inputs stay undefined so the backend saves NULL rather than 0.
      const cbNum = cementBags.trim() === '' ? undefined : Math.max(0, parseInt(cementBags, 10) || 0);
      const lcNum = labourCount.trim() === '' ? undefined : Math.max(0, parseInt(labourCount, 10) || 0);
      const resp = await axios.post(`${API}/production/log`, {
        items: entries.map(({ item, qty }) => ({
          item_id: item.zoho_item_id || item.id,
          name: item.name,
          qty_produced: qty,
          unit: item.unit || 'Nos',
          warehouse_name: selectedLocation.warehouse,
        })),
        notes: notes || undefined,
        cement_bags: cbNum,
        labour_count: lcNum,
        images: capturedImages.length ? capturedImages : undefined,
      });
      setProduced({});
      setNotes('');
      setCementBags('');
      setLabourCount('');
      setCapturedImages([]);
      const logsResp = await axios.get(`${API}/production/logs?limit=7`);
      setRecentLogs(logsResp.data.logs || []);
      setSyncResult(resp.data);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || err.message || 'Failed to log production');
    } finally {
      setSubmitting(false);
    }
  };

  // Retry pushing a locally-saved entry to Zoho — for logs whose initial submit
  // failed the network / Zoho call and are stuck as "Local only".
  const retrySync = async (logId) => {
    setRetryingLog(logId);
    try {
      const resp = await axios.post(`${API}/production/logs/${logId}/retry-sync`);
      const logsResp = await axios.get(`${API}/production/logs?limit=7`);
      setRecentLogs(logsResp.data.logs || []);
      setSyncResult(resp.data);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || err.message || 'Retry failed');
    } finally {
      setRetryingLog(null);
    }
  };

  const closeResultModal = () => {
    setSyncResult(null);
    setShowHistory(true);
  };

  const allCategories = inventoryCategorize(inventoryItems);
  // Filter categories to those belonging to the selected location
  const visibleCategories = selectedLocation
    ? Object.entries(allCategories).filter(([key, cat]) =>
        cat.items.length > 0 && selectedLocation.categories.includes(key))
    : [];
  const entryCount = Object.keys(produced).length;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10 shadow-sm">
        {/* Left-aligned brand + portal name, right-aligned nav + bell + logout —
            same layout across Factory / Delivery portals so the toolbar reads
            consistently regardless of which one you're on. */}
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={EB_LOGO} alt="EarthBlocks" className="h-7 w-auto shrink-0" />
            <h1 className="text-sm font-bold text-stone-800 truncate">Factory Portal</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {userPortals(currentUser).filter(p => p.to !== '/factory').map(p => (
              <Button key={p.to} variant="outline" size="sm" onClick={() => navigate(p.to)}>{p.label} →</Button>
            ))}
            <NotificationsBell />
            <Button variant="ghost" size="sm" onClick={handleLogout} title="Logout" className="text-stone-500 hover:text-stone-800">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-3 py-3 space-y-3">
        <Card className="border-stone-200 shadow-sm">
          <CardHeader className="px-3 py-2.5">
            <CardTitle className="text-sm text-stone-800">Day's Production Entry</CardTitle>
            <CardDescription className="text-[11px]">Select factory, enter today's quantities</CardDescription>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0 space-y-3">

            {/* Location selector — hidden when user is locked to a specific factory */}
            {currentUser?.factory_warehouse ? (
              <div className="rounded-lg border-2 border-orange-500 bg-orange-50 text-orange-900 px-3 py-3 text-sm font-semibold">
                <div className="text-xs uppercase tracking-wide text-orange-700 mb-1">Factory</div>
                <div>📍 {warehouseDisplayName(currentUser.factory_warehouse)}</div>
                <div className="text-[10px] font-normal text-orange-600 mt-0.5">
                  Locked to your assigned factory
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">Factory Location</Label>
                <div className="grid grid-cols-2 gap-2">
                  {FACTORY_LOCATIONS.map(loc => (
                    <button key={loc.id} onClick={() => selectLocation(loc)}
                      className={`px-2.5 py-2 rounded-md border text-sm font-semibold transition-all text-left ${
                        selectedLocation?.id === loc.id
                          ? 'border-amber-600 bg-amber-700 text-white shadow-sm'
                          : 'border-stone-200 bg-white text-stone-700 hover:border-amber-300'
                      }`}>
                      <div className="text-sm mb-0.5">📍</div>
                      <div className="text-xs leading-tight">{loc.label}</div>
                      {loc.subtitle && (
                        <div className={`text-[10px] mt-0.5 leading-tight ${selectedLocation?.id === loc.id ? 'text-amber-100' : 'text-stone-400'}`}>
                          {loc.subtitle}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Items — shown only after location selected */}
            {!selectedLocation ? (
              <p className="text-center text-gray-400 text-sm py-4">Select a location above to enter production quantities</p>
            ) : loadingItems ? (
              <p className="text-center text-gray-400 py-6">Loading items...</p>
            ) : (
              <div className="space-y-2">
                {visibleCategories.map(([key, cat]) => {
                  const isOpen = openCategories[key] !== false; // default open
                  const catEntries = cat.items.filter(i => produced[i.id]);
                  return (
                    <div key={key} className="border border-stone-200 rounded-lg overflow-hidden">
                      {/* Accordion header */}
                      <button
                        onClick={() => toggleCategory(key)}
                        className="w-full flex items-center gap-2 px-3 py-2 bg-stone-50 hover:bg-stone-100 text-left transition-colors">
                        <span className="text-sm">{cat.icon}</span>
                        <span className="text-[11px] font-semibold text-stone-700 uppercase tracking-wide flex-1">{cat.title}</span>
                        {catEntries.length > 0 && (
                          <span className="text-xs bg-orange-500 text-white rounded-full px-1.5 py-0.5 font-bold">{catEntries.length}</span>
                        )}
                        <span className="text-gray-400 text-xs ml-1">{isOpen ? '▲' : '▼'}</span>
                      </button>
                      {/* Accordion body */}
                      {isOpen && (
                        <div className="divide-y">
                          {cat.items.map(item => (
                            <div key={item.id} className="flex items-center gap-3 px-3 py-2.5 bg-white">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{item.name}</p>
                                <p className="text-xs text-gray-400">
                                  On hand: {Number(item.stock_quantity).toLocaleString('en-IN')} {item.unit}
                                  {Number(item.committed_stock) > 0 && (
                                    <span className="text-blue-700" title="Open sales-order quantity still to be supplied — the production backlog">
                                      {' · '}Ordered, not supplied: {Number(item.committed_stock).toLocaleString('en-IN')}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <Input
                                type="number" min="0" step="100" placeholder="0"
                                value={produced[item.id]?.qty || ''}
                                className={`w-24 text-sm text-right ${produced[item.id] ? 'border-orange-400 bg-orange-50' : ''}`}
                                onChange={e => handleQty(item, e.target.value)}
                              />
                              <span className="text-xs text-gray-400 w-6">{item.unit?.slice(0,3)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedLocation && (
              <>
                {/* Shift Inputs — Notes + Cement Bags (required) + Labour (required).
                    Grouped in a tinted card so operators see them as one block, not
                    three loose fields. Notes stays optional; the other two are
                    mandatory audit inputs and gate the submit button. */}
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2.5">
                  <div className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">Shift Inputs</div>
                  <Input placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} className="text-sm bg-white" />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-stone-600 mb-1 block">
                        Cement Bags Used <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        type="number" inputMode="numeric" min="0" step="1"
                        placeholder="0"
                        value={cementBags}
                        onChange={e => setCementBags(e.target.value.replace(/[^0-9]/g, ''))}
                        className="text-sm bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-stone-600 mb-1 block">
                        Labour Worked <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        type="number" inputMode="numeric" min="0" step="1"
                        placeholder="0"
                        value={labourCount}
                        onChange={e => setLabourCount(e.target.value.replace(/[^0-9]/g, ''))}
                        className="text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Photo capture */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Production Photos <span className="text-red-600">*</span>
                    </Label>
                    <span className={`text-xs ${capturedImages.length ? 'text-gray-400' : 'text-red-500 font-semibold'}`}>{capturedImages.length}/5</span>
                  </div>
                  {capturedImages.length === 0 && (
                    <p className="text-[11px] text-red-600 mb-2">At least one photo is required — it attaches to the Zoho adjustment as proof of the shift's output.</p>
                  )}
                  {/* Thumbnails */}
                  {capturedImages.length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-2">
                      {capturedImages.map((src, idx) => (
                        <div key={idx} className="relative">
                          <img src={src} alt={`Photo ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                          <button onClick={() => removeImage(idx)}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Hidden file input — opens camera on mobile */}
                  <input ref={imageInputRef} type="file" accept="image/*" capture="environment"
                    multiple className="hidden" onChange={handleImageCapture} />
                  {capturedImages.length < 5 && (
                    <button onClick={() => imageInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-orange-300 rounded-lg py-3 text-sm text-orange-600 font-medium hover:bg-orange-50 transition-colors flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {capturedImages.length === 0 ? 'Take Photo / Upload' : 'Add Another Photo'}
                    </button>
                  )}
                </div>

                <Button onClick={handleSubmit}
                  disabled={submitting || !entryCount || !capturedImages.length || !cementBags.trim() || !labourCount.trim()}
                  className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-bold">
                  {submitting
                    ? 'Logging...'
                    : !capturedImages.length
                      ? '📷 Add a photo to log'
                      : (!cementBags.trim() || !labourCount.trim())
                        ? '📝 Enter cement bags & labour count'
                        : `Log ${entryCount ? entryCount + ' Item' + (entryCount > 1 ? 's' : '') : 'Production'} → Zoho`}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Recent production history */}
        <Card>
          <button className="w-full px-4 py-3 flex items-center justify-between text-left"
            onClick={() => setShowHistory(h => !h)}>
            <span className="text-sm font-semibold text-gray-700">Recent Production History</span>
            <span className="text-xs text-gray-400">{showHistory ? '▲' : '▼'} {recentLogs.length} entries</span>
          </button>
          {showHistory && (
            <div className="border-t divide-y">
              {recentLogs.length === 0 ? (
                <p className="px-4 py-4 text-sm text-gray-400">No entries yet.</p>
              ) : recentLogs.map(log => {
                // Only today's entries are editable (matches the backend guard).
                // Compare on the YYYY-MM-DD portion — log_date arrives as an
                // ISO date string, so a simple prefix comparison against today
                // in local time is enough for a factory operator's needs.
                const todayISO = new Date().toLocaleDateString('en-CA'); // 'YYYY-MM-DD'
                const isToday = (log.log_date || '').startsWith(todayISO);
                return (
                <div key={log.log_id} className="px-4 py-3">
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-sm font-medium">{fmtDate(log.log_date)}</span>
                    <div className="flex items-center gap-2">
                      {log.zoho_adjustment_id ? (
                        <span className="text-xs text-green-600">✓ Zoho synced</span>
                      ) : (
                        <>
                          <span className="text-xs text-amber-700 font-medium">⚠ Local only</span>
                          <button
                            onClick={() => retrySync(log.log_id)}
                            disabled={retryingLog === log.log_id}
                            className="text-[11px] px-2 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-wait font-medium"
                          >
                            {retryingLog === log.log_id ? 'Retrying…' : '🔄 Retry sync'}
                          </button>
                        </>
                      )}
                      {isToday && (
                        <button
                          onClick={() => setEditingLog(log)}
                          className="text-[11px] px-2 py-0.5 rounded border border-blue-400 bg-blue-50 text-blue-800 hover:bg-blue-100 font-medium"
                          title="Fix a wrong quantity, cement bags or labour count — only today's log is editable"
                        >
                          ✎ Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {(log.items || []).map((it, i) => (
                      <p key={i} className="text-xs text-gray-500">{it.name}: <strong>{Number(it.qty_produced).toLocaleString()}</strong></p>
                    ))}
                  </div>
                  {log.notes && <p className="text-xs text-gray-400 mt-1 italic">{log.notes}</p>}
                </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Edit today's production log — quantities, cement bags, labour, notes.
          Backend guards against non-today entries, so this modal doesn't need
          to re-validate the date. */}
      <EditProductionLogModal
        log={editingLog}
        onClose={() => setEditingLog(null)}
        onSaved={async () => {
          setEditingLog(null);
          try {
            const r = await axios.get(`${API}/production/logs?limit=7`);
            setRecentLogs(r.data.logs || []);
          } catch {}
        }}
      />

      {/* Sync result modal — honest per-warehouse and per-photo feedback */}
      <SyncResultModal result={syncResult} onClose={closeResultModal} />

      {/* Error modal — 400 (unknown warehouse), 500, etc. */}
      <Dialog open={!!errorMsg} onOpenChange={(o) => !o && setErrorMsg('')}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <span>❌</span> Cannot Submit
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-stone-700 whitespace-pre-wrap py-2">{errorMsg}</div>
          <DialogFooter>
            <Button onClick={() => setErrorMsg('')}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sync result modal — shows per-warehouse Zoho status and photo counts
// Compose a plain-text summary of the log for sharing on WhatsApp / any chat.
// Emojis are kept but restrained (WhatsApp preview truncates anything gaudy).
const buildProductionShareText = (result) => {
  if (!result) return '';
  const {
    log_date, items = [], zoho_adjustments = [],
    photos_uploaded = 0, photos_attempted = 0,
    created_by, notes, cement_bags, labour_count,
  } = result;
  const date = fmtDate(log_date);
  const byWh = new Map();
  items.forEach(it => {
    const key = warehouseDisplayName(it.warehouse_name) || 'Factory';
    if (!byWh.has(key)) byWh.set(key, []);
    byWh.get(key).push(it);
  });
  const totalQty = items.reduce((s, it) => s + (Number(it.qty_produced) || 0), 0);
  const lines = [`🏭 *Production Log · ${date}*`, ''];
  for (const [wh, whItems] of byWh.entries()) {
    lines.push(`📍 ${wh}`);
    whItems.forEach(it => {
      lines.push(`• ${it.name}: *${Number(it.qty_produced).toLocaleString()}* ${it.unit || 'pcs'}`);
    });
    lines.push('');
  }
  lines.push(`Total: ${totalQty.toLocaleString()} units · ${items.length} SKU${items.length === 1 ? '' : 's'}`);
  // 📦 (package) reads as "sack of cement" — the brick 🧱 was confusing because
  // the actual products in this log are also bricks.
  if (cement_bags != null) lines.push(`📦 Cement Bags used: *${cement_bags}*`);
  if (labour_count != null) lines.push(`👷 Labour worked: *${labour_count}*`);
  if (notes) lines.push(`📝 ${notes}`);
  const okAdj = zoho_adjustments.filter(z => z.adj_id && !z.error);
  if (okAdj.length) {
    lines.push(`Zoho: ✓ ${okAdj.length} adjustment${okAdj.length === 1 ? '' : 's'} synced`);
  } else if (zoho_adjustments.length) {
    lines.push('Zoho: ⚠ sync failed — saved locally');
  }
  if (photos_attempted > 0) {
    lines.push(`Photos: ${photos_uploaded}/${photos_attempted} attached to Zoho`);
  }
  if (created_by) lines.push(`By: ${created_by}`);
  return lines.join('\n');
};

const SyncResultModal = ({ result, onClose }) => {
  if (!result) return null;
  const { log_date, items_logged, zoho_adjustments = [], photos_uploaded = 0, photos_attempted = 0, photo_errors = [], all_synced } = result;
  const anyZohoFailed = zoho_adjustments.some(z => !z.adj_id || z.error);
  const anyZohoSucceeded = zoho_adjustments.some(z => z.adj_id && !z.error);
  const allZohoFailed = zoho_adjustments.length > 0 && !anyZohoSucceeded;
  const photosPartial = photos_attempted > 0 && photos_uploaded < photos_attempted;
  const photosAllFailed = photos_attempted > 0 && photos_uploaded === 0;

  let headerIcon = '✅';
  let headerText = 'Production Logged Successfully';
  let headerColor = 'text-green-700';
  if (allZohoFailed) {
    headerIcon = '❌'; headerText = 'Saved Locally — Zoho Sync Failed'; headerColor = 'text-red-700';
  } else if (anyZohoFailed || photosPartial) {
    headerIcon = '⚠️'; headerText = 'Logged with Warnings'; headerColor = 'text-amber-700';
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${headerColor}`}>
            <span>{headerIcon}</span> {headerText}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          <p className="text-stone-700">{items_logged} item(s) logged for {fmtDate(log_date)}</p>

          <div>
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Zoho Books</p>
            <ul className="space-y-1.5">
              {zoho_adjustments.map((z, i) => (
                <li key={i} className="text-sm">
                  {z.adj_id && !z.error ? (
                    <div>
                      <span className="text-green-700 font-medium">✓ {z.warehouse}</span>
                      <div className="text-xs text-stone-500 ml-4 font-mono">Adjustment ID: {z.adj_id}</div>
                    </div>
                  ) : (
                    <div>
                      <span className="text-red-700 font-medium">✗ {z.warehouse}</span>
                      <div className="text-xs text-red-600 ml-4">{z.error || 'Unknown error'}</div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {photos_attempted > 0 && (
            <div>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Photos</p>
              <p className={photosAllFailed ? 'text-red-700' : photosPartial ? 'text-amber-700' : 'text-green-700'}>
                {photosAllFailed ? '—' : photosPartial ? '⚠' : '✓'} {photos_uploaded} / {photos_attempted} photos attached
              </p>
              {photo_errors.length > 0 && (
                <ul className="text-xs text-red-600 mt-1 ml-4 space-y-0.5">
                  {photo_errors.slice(0, 5).map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              )}
            </div>
          )}

          {!all_synced && (
            <p className="text-xs text-stone-500 italic border-t pt-2">
              The entry is saved locally. {allZohoFailed && 'Once Zoho is fixed, please delete this entry from Books and re-submit here.'}
              {!allZohoFailed && anyZohoFailed && 'Fix the issue and re-submit if needed.'}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            onClick={() => {
              const text = buildProductionShareText(result);
              // wa.me/?text= (no phone number) opens WhatsApp's chat/group picker
              // so the operator can pick which team group / person to share to.
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
            }}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            💬 Share on WhatsApp
          </Button>
          <Button onClick={onClose} className="bg-amber-800 hover:bg-amber-900 text-white">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit today's production log — quantities, cement bags, labour count, notes.
// Backend enforces the same-day rule; this modal only opens when the caller
// already decided the row was for today, so no client-side date guard here.
const EditProductionLogModal = ({ log, onClose, onSaved }) => {
  const [qtyMap, setQtyMap] = React.useState({});  // key = item_id, value = string
  const [notes, setNotes] = React.useState('');
  const [cementBags, setCementBags] = React.useState('');
  const [labourCount, setLabourCount] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');

  // Reset the form each time a different log is opened. Empty string in the
  // number inputs means "clear / not set" per the create form's convention.
  React.useEffect(() => {
    if (!log) return;
    const m = {};
    (log.items || []).forEach(it => { m[it.item_id] = String(it.qty_produced || ''); });
    setQtyMap(m);
    setNotes(log.notes || '');
    setCementBags(log.cement_bags == null ? '' : String(log.cement_bags));
    setLabourCount(log.labour_count == null ? '' : String(log.labour_count));
    setErrorMsg('');
  }, [log?.log_id]);

  if (!log) return null;

  const setQty = (item_id, v) => setQtyMap(prev => ({ ...prev, [item_id]: v.replace(/[^0-9.]/g, '') }));

  const submit = async () => {
    // Build items[] preserving the original name/unit/warehouse_name but with
    // fresh qty_produced. Skip zero/blank rows so a mis-typed line drops out.
    const items = (log.items || [])
      .map(it => {
        const raw = qtyMap[it.item_id];
        const q = raw === '' || raw == null ? NaN : Number(raw);
        if (!isFinite(q) || q <= 0) return null;
        return {
          item_id: it.item_id, name: it.name,
          qty_produced: q, unit: it.unit || 'pcs',
          warehouse_name: it.warehouse_name || null,
        };
      })
      .filter(Boolean);
    if (items.length === 0) {
      setErrorMsg('Enter at least one item quantity greater than zero.');
      return;
    }
    if (cementBags.trim() === '' || labourCount.trim() === '') {
      setErrorMsg('Cement Bags and Labour Worked are required.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      await axios.put(`${API}/production/logs/${log.log_id}`, {
        items,
        notes: notes || null,
        cement_bags: parseInt(cementBags, 10),
        labour_count: parseInt(labourCount, 10),
      });
      onSaved();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <span>✎</span> Edit Today's Production Log
          </DialogTitle>
          <DialogDescription className="text-xs">
            {fmtDate(log.log_date)} · {log.created_by || 'unknown'} · adj id {log.zoho_adjustment_id || '—'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] font-semibold uppercase text-stone-500 tracking-wide mb-1 block">Items</Label>
            {/* CSS grid keeps qty inputs vertically aligned across rows regardless of
                item name length, and puts the unit inside the row's visual box. */}
            <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
              {(log.items || []).map(it => (
                <div
                  key={it.item_id}
                  className="grid items-center gap-3 px-3 py-2"
                  style={{ gridTemplateColumns: '1fr 6rem 2.25rem' }}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{it.name}</p>
                    <p className="text-[10px] text-stone-400 truncate">{it.warehouse_name || '—'}</p>
                  </div>
                  <Input
                    type="number" inputMode="numeric" min="0" step="1"
                    value={qtyMap[it.item_id] ?? ''}
                    onChange={e => setQty(it.item_id, e.target.value)}
                    className="w-full h-8 text-sm text-right tabular-nums"
                  />
                  <span className="text-xs text-stone-500 leading-none">{it.unit || 'pcs'}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-stone-400 mt-1">Set a qty to 0 (or blank) to drop that item from the entry.</p>
          </div>

          {/* Shift inputs card — matches the amber "Shift Inputs" section on the
              main log form so the edit modal reads as the same data being changed. */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2.5">
            <div className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">Shift Inputs</div>
            <Input placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} className="h-9 text-sm bg-white" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-stone-600 mb-1 block">
                  Cement Bags Used <span className="text-red-600">*</span>
                </Label>
                <Input type="number" inputMode="numeric" min="0" step="1"
                       value={cementBags}
                       onChange={e => setCementBags(e.target.value.replace(/[^0-9]/g, ''))}
                       placeholder="0"
                       className="h-9 text-sm bg-white" />
              </div>
              <div>
                <Label className="text-[11px] text-stone-600 mb-1 block">
                  Labour Worked <span className="text-red-600">*</span>
                </Label>
                <Input type="number" inputMode="numeric" min="0" step="1"
                       value={labourCount}
                       onChange={e => setLabourCount(e.target.value.replace(/[^0-9]/g, ''))}
                       placeholder="0"
                       className="h-9 text-sm bg-white" />
              </div>
            </div>
          </div>

          {errorMsg && <p className="text-xs text-red-600">{errorMsg}</p>}
          <p className="text-[10px] text-stone-400 italic">
            The linked Zoho inventory adjustment will be updated to match. Only today's log can be edited.
          </p>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── ADMIN ESTIMATES TAB ────────────────────────────────────────────────────

const EstimatesSection = ({ onDeliveryCreated }) => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('accepted');
  const [approving, setApproving] = useState({});
  const [approveTarget, setApproveTarget] = useState(null);  // estimate the picker modal is open for
  const [expanded, setExpanded] = useState({});
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => { loadEstimates(); }, [statusFilter]);

  const loadEstimates = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const resp = await axios.get(`${API}/zoho/estimates${params}`);
      setEstimates(resp.data.estimates || []);
      setExpanded({});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const sorted = [...estimates].sort((a, b) => {
    let av, bv;
    if (sortBy === 'date') { av = a.date || ''; bv = b.date || ''; }
    else if (sortBy === 'total') { av = Number(a.total) || 0; bv = Number(b.total) || 0; }
    else if (sortBy === 'customer') { av = (a.customer_name || '').toLowerCase(); bv = (b.customer_name || '').toLowerCase(); }
    else if (sortBy === 'status') { av = a.status || ''; bv = b.status || ''; }
    else if (sortBy === 'number') { av = a.estimate_number || ''; bv = b.estimate_number || ''; }
    else { av = ''; bv = ''; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Approval opens the order: estimate → accepted → Sales Order for the full
  // quantity (the supply / manufacturing plan). No challan, no delivery row and
  // no stock check here — loads are raised one at a time as call-offs.
  const submitApproval = async (est, payload) => {
    setApproving(a => ({ ...a, [est.estimate_id]: true }));
    try {
      const resp = await axios.post(`${API}/estimates/approve/${est.estimate_id}`, payload);
      const so = resp.data.salesorder_number || resp.data.order?.salesorder_number || 'N/A';
      const warn = (resp.data.warnings || []).length ? `\n\n${resp.data.warnings.join('\n')}` : '';
      alert(`✓ Order opened\nOrder: ${so}\nBook trucks from the row as the site asks for them.${warn}`);
      setApproveTarget(null);
      loadEstimates();
      if (onDeliveryCreated) onDeliveryCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Approval failed');
    } finally {
      setApproving(a => ({ ...a, [est.estimate_id]: false }));
    }
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Quotes</h2>
          <p className="text-xs text-gray-400">Live from Zoho Books{!loading && estimates.length > 0 ? ` · ${estimates.length} quotes` : ''}</p>
        </div>
        <Button size="sm" variant="outline" onClick={loadEstimates}>↺ Refresh</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['', 'All'], ['draft', 'Draft'], ['sent', 'Sent'], ['accepted', 'Accepted'], ['invoiced', 'Invoiced'], ['expired', 'Expired']].map(([val, lbl]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${statusFilter === val ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-8">Loading from Zoho…</p>
      ) : sorted.length === 0 ? (
        <p className="text-center text-gray-400 py-8">No quotes found.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          {/* Header — hidden on mobile, grid on sm+ */}
          <div className="hidden sm:grid grid-cols-[1fr_5rem_5rem_7rem_6rem_1.5rem] gap-x-3 px-4 py-2 bg-gray-50 border-b items-center">
            <SortHeader label="Customer" col="customer" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            <SortHeader label="Est #" col="number" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortHeader label="Date" col="date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortHeader label="Amount" col="total" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortHeader label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
            <div />
          </div>

          <div className="divide-y">
            {sorted.map(est => {
              const canApprove = ['draft', 'sent'].includes(est.status);
              return (
                <div key={est.estimate_id} className="bg-white">
                  {/* Mobile card layout */}
                  <div className="sm:hidden px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => toggleExpand(est.estimate_id)}>
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1 mr-3">
                        <p className="font-medium text-sm text-gray-900 truncate">{est.customer_name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{est.estimate_number} · {fmtDate(est.date)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-green-700">₹{Number(est.total).toLocaleString('en-IN')}</p>
                        <Badge className={`text-xs mt-1 ${EST_STATUS_COLORS[est.status] || 'bg-gray-100 text-gray-600'}`}>{est.status}</Badge>
                      </div>
                    </div>
                  </div>
                  {/* Desktop grid layout */}
                  <div className="hidden sm:grid grid-cols-[1fr_5rem_5rem_7rem_6rem_1.5rem] gap-x-3 px-4 py-3 items-center hover:bg-gray-50 transition-colors">
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-gray-900 truncate">{est.customer_name}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {est.cf_customer_number || ''}{est.salesperson_name ? ` · ${est.salesperson_name}` : ''}
                      </p>
                    </div>
                    <p className="text-xs font-mono text-gray-500 text-right">{est.estimate_number}</p>
                    <p className="text-xs text-gray-500 text-right">{fmtDate(est.date)}</p>
                    <p className="text-sm font-bold text-green-700 text-right">₹{Number(est.total).toLocaleString('en-IN')}</p>
                    <div className="flex justify-end">
                      <Badge className={`text-xs ${EST_STATUS_COLORS[est.status] || 'bg-gray-100 text-gray-600'}`}>{est.status}</Badge>
                    </div>
                    <button onClick={() => toggleExpand(est.estimate_id)}
                      className="text-gray-400 hover:text-blue-500 transition-colors text-base leading-none text-center">
                      {expanded[est.estimate_id] ? '▲' : '▼'}
                    </button>
                  </div>

                  {expanded[est.estimate_id] && (
                    <div>
                      <LineItemsPanel
                        id={est.estimate_id}
                        detailUrl={`${API}/zoho/estimates/${est.estimate_id}`}
                        detailKey="estimate"
                      />
                      <div className="flex flex-wrap gap-2 items-center justify-between px-4 pb-3">
                        <CustomerOwnerBar
                          customerId={est.customer_id}
                          customerName={est.customer_name}
                        />
                        <div className="flex flex-wrap gap-2 items-center justify-end ml-auto">
                          {canApprove && (
                            <Button size="sm" onClick={() => setApproveTarget(est)} disabled={approving[est.estimate_id]}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs">
                              {approving[est.estimate_id] ? 'Processing…' : '✓ Approve → Open order'}
                            </Button>
                          )}
                          {est.status === 'accepted' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => navigate('/delivery')}
                                className="text-xs border-purple-300 text-purple-700">🚛 View in Delivery</Button>
                              <OrderLoadControls
                                estimateId={est.estimate_id}
                                estimateNumber={est.estimate_number}
                                customerName={est.customer_name}
                                onCreated={onDeliveryCreated}
                                onClosed={() => { loadEstimates(); if (onDeliveryCreated) onDeliveryCreated(); }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {approveTarget && (
        <ApproveEstimateModal
          estimate={approveTarget}
          submitting={!!approving[approveTarget.estimate_id]}
          onClose={() => setApproveTarget(null)}
          onSubmit={(payload) => submitApproval(approveTarget, payload)}
        />
      )}
    </div>
  );
};

// Approving an estimate opens the order: the Sales Order for the full quantity is
// the supply / manufacturing plan. Loads (and whether a load is a Stock Transfer)
// are decided per call-off, not here. The rep confirms the customer's requested
// delivery date, which the first load defaults to.
const ApproveEstimateModal = ({ estimate, submitting, onClose, onSubmit }) => {
  const today = localISODate();
  const [neededBy, setNeededBy] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  // Prefill from the local quotes row (customer asked for a date at order time).
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/orders/${estimate.estimate_id}`)
      .then(r => {
        const nb = r.data?.order?.needed_by;
        if (alive && nb && nb >= today) setNeededBy(nb);
      })
      .catch(() => {})
      .finally(() => { if (alive) setPrefilled(true); });
    return () => { alive = false; };
  }, [estimate.estimate_id, today]);

  const confirm = () => {
    if (neededBy && neededBy < today) {
      alert('Requested delivery date cannot be in the past.');
      return;
    }
    onSubmit(neededBy ? { needed_by: neededBy } : {});
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Open order — {estimate.estimate_number}</DialogTitle>
          <DialogDescription>
            {estimate.customer_name} · ₹{Number(estimate.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs bg-stone-50 border border-stone-200 rounded px-2.5 py-2 text-stone-700 space-y-1">
            <p>The quote is accepted and an <b>order for the full quantity</b> is created in Zoho (a Sales Order there) — the plan of what to supply and manufacture.</p>
            <p>Nothing is loaded yet. Book each truck with <b>Book truck</b> as the site asks for it; stock and payment are checked per truck.</p>
          </div>
          <div>
            <Label className="text-xs">Requested delivery date <span className="text-stone-400">(first load defaults to this)</span></Label>
            <input
              type="date" value={neededBy} min={today}
              onChange={(e) => setNeededBy(e.target.value)}
              className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white mt-1"
              data-testid="approve-needed-by"
            />
            {!prefilled && <p className="text-[11px] text-stone-400 mt-1">Checking the customer's request…</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={confirm} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-submit">
            {submitting ? 'Opening…' : '✓ Open order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── LOGIN PAGE ────────────────────────────────────────────────────────────

const LoginPage = () => {
  const navigate = useNavigate();
  // "Remember me" saves only the login ID (email/mobile) to device storage so it
  // pre-fills next time. The password is never stored here — the browser's own
  // password manager handles that securely via the autoComplete fields.
  const [identifier, setIdentifier] = useState(() => localStorage.getItem('eb_saved_identifier') || '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => !!localStorage.getItem('eb_saved_identifier'));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const roleToRoute = (role) => {
    if (role === 'factory') return '/factory';
    if (role === 'transport') return '/delivery';
    return '/dashboard';
  };

  // If already logged in, redirect by role
  useEffect(() => {
    const t = localStorage.getItem('eb_token');
    if (!t) return;
    axios.get(`${API}/auth/me`).then(r => navigate(roleToRoute(r.data.role))).catch(() => {});
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setErr('');
    if (!identifier || !password) { setErr('Enter email or mobile and password'); return; }
    setSubmitting(true);
    try {
      const loginResp = await axios.post(`${API}/auth/login`, { identifier, password });
      const token = loginResp.data.access_token;
      if (!token) throw new Error('Login response missing access_token');
      localStorage.setItem('eb_token', token);
      // Store the refresh token so the session survives past the 60-min access token.
      if (loginResp.data.refresh_token) localStorage.setItem('eb_refresh', loginResp.data.refresh_token);
      // Remember the login ID for next time (never the password)
      if (remember) localStorage.setItem('eb_saved_identifier', identifier);
      else localStorage.removeItem('eb_saved_identifier');
      invalidateCachedMe();  // drop any previous user's cached role before we ask /me
      const meResp = await axios.get(`${API}/auth/me`);
      try { localStorage.setItem('eb_me', JSON.stringify(meResp.data)); } catch (e) {}
      navigate(roleToRoute(meResp.data.role));
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src={EB_LOGO} alt="EarthBlocks" className="h-10 w-auto" />
            <div>
              <h1 className="text-xl font-bold text-stone-800">EarthBlocks</h1>
              <p className="text-[11px] text-amber-700">Compressed Stabilized Earth Blocks</p>
            </div>
          </div>

          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="px-4 py-3">
              <CardTitle className="text-base text-stone-800">Sign in</CardTitle>
              <CardDescription className="text-[11px]">Use your EarthBlocks staff account</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <form onSubmit={handleSignIn} className="space-y-3">
                <div>
                  <Label htmlFor="identifier" className="text-xs">Email or mobile</Label>
                  <Input id="identifier" type="text" autoComplete="username" value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)} placeholder="you@earthblocks.in or 9876543210" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs">Password</Label>
                  <PasswordInput id="password" autoComplete="current-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-stone-300 text-amber-700 focus:ring-amber-500"
                    data-testid="remember-me"
                  />
                  <span className="text-xs text-stone-600">Remember my login ID on this device</span>
                </label>
                {err && <p className="text-xs text-red-600">{err}</p>}
                <Button type="submit" disabled={submitting}
                  className="w-full bg-amber-800 hover:bg-amber-900 text-white">
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
                <div className="text-center">
                  <Link to="/forgot-password" className="text-xs text-amber-700 hover:text-amber-900 underline">
                    Forgot password?
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>

          <Link to="/" className="block mt-3 w-full text-center py-2 rounded-md bg-stone-700 text-white text-sm font-medium hover:bg-stone-800 transition-colors">
            ← Back to Home
          </Link>

          <p className="text-xs text-stone-400 text-center mt-6">© 2026 EarthBlocks India Pvt. Ltd.</p>
        </div>
      </div>
    </div>
  );
};

// ─── FORGOT PASSWORD ───────────────────────────────────────────────────────
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: request, 2: verify+reset
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const requestOtp = async (e) => {
    e.preventDefault();
    setErr(''); setInfo('');
    if (!identifier.trim()) { setErr('Enter your email or mobile'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password/request`, { identifier: identifier.trim() });
      setInfo('If an account matches, a 6-digit code has been sent to your phone or email.');
      setStep(2);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to send code');
    } finally { setBusy(false); }
  };

  const verifyAndReset = async (e) => {
    e.preventDefault();
    setErr(''); setInfo('');
    if (!otp || otp.length !== 6) { setErr('Enter the 6-digit code'); return; }
    if (newPassword.length < 6) { setErr('New password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password/verify`, {
        identifier: identifier.trim(), otp_code: otp, new_password: newPassword,
      });
      setInfo('Password updated. Redirecting to sign in…');
      setTimeout(() => navigate('/login'), 1500);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src={EB_LOGO} alt="EarthBlocks" className="h-10 w-auto" />
            <div>
              <h1 className="text-xl font-bold text-stone-800">EarthBlocks</h1>
              <p className="text-[11px] text-amber-700">Password reset</p>
            </div>
          </div>

          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="px-4 py-3">
              <CardTitle className="text-base text-stone-800">
                {step === 1 ? 'Forgot password?' : 'Enter code + new password'}
              </CardTitle>
              <CardDescription className="text-[11px]">
                {step === 1
                  ? 'We\'ll send a 6-digit code to your registered WhatsApp.'
                  : `Enter the code and pick a new password.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              {step === 1 ? (
                <form onSubmit={requestOtp} className="space-y-3">
                  <div>
                    <Label className="text-xs">Email or mobile</Label>
                    <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                      placeholder="you@earthblocks.in or 9876543210" />
                  </div>
                  {err && <p className="text-xs text-red-600">{err}</p>}
                  {info && <p className="text-xs text-green-700">{info}</p>}
                  <Button type="submit" disabled={busy}
                    className="w-full bg-amber-800 hover:bg-amber-900 text-white">
                    {busy ? 'Sending…' : 'Send OTP code'}
                  </Button>
                  <div className="text-center">
                    <Link to="/login" className="text-xs text-stone-500 hover:text-stone-800 underline">
                      Back to sign in
                    </Link>
                  </div>
                </form>
              ) : (
                <form onSubmit={verifyAndReset} className="space-y-4">
                  <div>
                    <Label className="text-xs">6-digit code</Label>
                    <Input value={otp} maxLength={6}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456" inputMode="numeric" />
                  </div>
                  <div>
                    <Label className="text-xs">New password</Label>
                    <PasswordInput value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters" />
                  </div>
                  {err && <p className="text-xs text-red-600">{err}</p>}
                  {info && <p className="text-xs text-green-700">{info}</p>}
                  <Button type="submit" disabled={busy}
                    className="w-full bg-amber-800 hover:bg-amber-900 text-white">
                    {busy ? 'Updating…' : 'Reset password'}
                  </Button>
                  <div className="text-center">
                    <button type="button" onClick={() => { setStep(1); setOtp(''); setErr(''); setInfo(''); }}
                      className="text-xs text-stone-500 hover:text-stone-800 underline">
                      Didn't get the code? Send again
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ─── HELP PILL + DRAWER ──────────────────────────────────────────────────────
// The floating "Help" button on every staff screen. The drawer is the manual for
// the user's role(s): how a load moves, what each button does, why a button is
// disabled, who to call. The load RULES are read live from GET /api/help — the
// server derives them from its own transition table — so this panel can never
// say something the server does not enforce. The plain-English framing lives in
// src/help/content.js; `pnpm check:help` fails if a button named there no longer
// exists in this file, so a renamed button forces a help update.

let _helpRulesCache = null; // { data, at } — one fetch per session unless refreshed
const fetchHelpRules = (force = false) => {
  if (_helpRulesCache && !force) return Promise.resolve(_helpRulesCache);
  return axios.get(`${API}/help`)
    .then((r) => { _helpRulesCache = { data: r.data, at: new Date() }; return _helpRulesCache; })
    .catch(() => null);
};

const HELP_ROLE_ORDER = ['admin', 'manager', 'staff', 'transport', 'factory'];
const HELP_ROLE_LABEL = { admin: 'Admin', manager: 'Manager', staff: 'Staff', transport: 'Transport', factory: 'Factory' };

const HelpSection = ({ title, hint, children }) => (
  <section className="space-y-2">
    <div className="flex items-baseline justify-between gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{title}</h3>
      {hint && <span className="text-[11px] text-stone-400">{hint}</span>}
    </div>
    {children}
  </section>
);

const HelpDrawer = ({ open, onOpenChange, me, screen }) => {
  const userRoles = [me?.role, ...(me?.extra_roles || [])].filter(Boolean);
  // Admins see every role's manual (they field everyone's questions); others see their own roles.
  const tabs = me?.role === 'admin' ? HELP_ROLE_ORDER : HELP_ROLE_ORDER.filter((r) => userRoles.includes(r));
  const [role, setRole] = useState(null);
  const [rules, setRules] = useState(_helpRulesCache);
  const [rulesState, setRulesState] = useState(_helpRulesCache ? 'ok' : 'loading');

  useEffect(() => {
    if (!open) return;
    // Default tab: the primary role, unless another of the user's roles owns this screen.
    const owns = (r) => (ROLE_SCREENS[r] || []).includes(screen);
    const primary = tabs.includes(me?.role) ? me.role : tabs[0];
    const owner = tabs.find(owns);
    setRole(!screen || owns(primary) || !owner ? primary : owner);
    fetchHelpRules().then((c) => { if (c) { setRules(c); setRulesState('ok'); } else setRulesState('offline'); });
  }, [open]);

  const refreshRules = () => {
    setRulesState('loading');
    fetchHelpRules(true).then((c) => { if (c) { setRules(c); setRulesState('ok'); } else setRulesState('offline'); });
  };

  const manual = HELP_ROLES[role] || HELP_ROLES[tabs[0]] || HELP_ROLES.staff;
  const live = rules?.data || null;

  // Buttons grouped by screen; the screen the user is on comes first and open.
  const groups = {};
  manual.buttons.forEach((b) => { if (!groups[b.screen]) groups[b.screen] = []; groups[b.screen].push(b); });
  const screenOrder = [screen, ...(ROLE_SCREENS[role] || []), ...Object.keys(groups)]
    .filter((s, i, a) => s && groups[s] && a.indexOf(s) === i);

  // Live rows: every load transition the server knows about that is gated or
  // restricted for the role being viewed. `roles` comes from the server per row.
  const liveRows = (live?.transitions || [])
    .filter((t) => t.button)
    .map((t) => ({ ...t, youCan: (t.roles || []).includes(role) }))
    .filter((t) => t.gate || !t.youCan);

  const rulesLine = rulesState === 'ok' && rules
    ? `Rules read from the server at ${rules.at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
    : rulesState === 'loading' ? 'Reading the live rules…' : 'Could not reach the server — showing the built-in text only';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[94vw] sm:max-w-md p-0 flex flex-col gap-0" aria-describedby="eb-help-desc">
        <SheetHeader className="px-5 pt-5 pb-3 pr-12 border-b border-stone-200 text-left space-y-1">
          <SheetTitle className="text-base">Help · {manual.title}</SheetTitle>
          <SheetDescription id="eb-help-desc" className="text-xs leading-snug">{manual.tagline}</SheetDescription>
          {screen && SCREEN_LABELS[screen] && (
            <p className="text-[11px] text-stone-500">You are on: <span className="font-medium text-stone-700">{SCREEN_LABELS[screen]}</span></p>
          )}
          {tabs.length > 1 && (
            <div className="flex flex-wrap gap-1 pt-1" role="tablist" aria-label="Role">
              {tabs.map((r) => (
                <button key={r} type="button" role="tab" aria-selected={r === role} onClick={() => setRole(r)}
                  className={`rounded-full px-2.5 py-0.5 text-xs border transition-colors ${r === role
                    ? 'bg-amber-600 border-amber-600 text-white'
                    : 'bg-white border-stone-300 text-stone-600 hover:border-amber-400'}`}>
                  {HELP_ROLE_LABEL[r] || r}{userRoles.includes(r) && me?.role !== 'admin' && r !== me?.role ? ' (extra)' : ''}
                </button>
              ))}
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6 text-sm text-stone-700">
          <HelpSection title="How a load moves" hint={`${manual.flow.length} steps`}>
            <ol className="space-y-2">
              {manual.flow.map((f, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium text-stone-900">{f.step}</span>
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">{f.who}</span>
                    </div>
                    <p className="text-xs text-stone-600 leading-snug">{f.note}</p>
                  </div>
                </li>
              ))}
            </ol>
          </HelpSection>

          <HelpSection title="What each button does">
            <div className="space-y-2">
              {screenOrder.map((s) => (
                <details key={`${role}-${s}`} open={s === screen || screenOrder.length === 1} className="group rounded-md border border-stone-200">
                  <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-stone-800 flex items-center justify-between">
                    <span>{SCREEN_LABELS[s] || s}{s === screen ? ' · this screen' : ''}</span>
                    <span className="text-stone-400">{groups[s].length}</span>
                  </summary>
                  <dl className="px-3 pb-3 space-y-2">
                    {groups[s].map((b) => (
                      <div key={b.label}>
                        <dt className="font-medium text-stone-900">{b.label}</dt>
                        <dd className="text-xs text-stone-600 leading-snug">{b.does}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              ))}
            </div>
          </HelpSection>

          <HelpSection title="When a button is disabled — and why" hint={live ? 'gates read live' : undefined}>
            <ul className="space-y-2">
              {liveRows.map((t) => (
                <li key={`${t.from}-${t.to}`} className="rounded-md border border-stone-200 p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-stone-900">{t.button}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">live rule</span>
                  </div>
                  {t.does && <p className="text-xs text-stone-600 leading-snug">{t.does}</p>}
                  {t.blocked_message && (
                    <p className="mt-1 text-xs text-stone-700 leading-snug">
                      Reads <span className="font-mono text-[11px] bg-stone-100 rounded px-1">{t.blocked_message}</span>
                      {t.gate_why ? <> until: {t.gate_why}</> : null}
                    </p>
                  )}
                  {t.gate_fix && <p className="mt-1 text-xs text-stone-700 leading-snug"><span className="font-medium">Fix:</span> {t.gate_fix}</p>}
                  {!t.youCan && (
                    <p className="mt-1 text-xs text-red-700 leading-snug">
                      Not for the {HELP_ROLE_LABEL[role] || role} role — only {(t.roles || []).map((r) => HELP_ROLE_LABEL[r] || r).join(', ')}.
                    </p>
                  )}
                </li>
              ))}
              {manual.blocked.map((b) => (
                <li key={b.label} className="rounded-md border border-stone-200 p-2.5">
                  <span className="font-medium text-stone-900">{b.label}</span>
                  <p className="mt-1 text-xs text-stone-700 leading-snug">{b.why}</p>
                  <p className="mt-1 text-xs text-stone-700 leading-snug"><span className="font-medium">Fix:</span> {b.fix}</p>
                </li>
              ))}
            </ul>
            {live && (
              <div className="rounded-md bg-stone-50 border border-stone-200 p-2.5 text-xs text-stone-600 space-y-1">
                <p className="font-medium text-stone-800">Overrides, as the server allows them</p>
                <p>· {live.gates?.funded?.name || 'Payment'} gate ({live.gates?.funded?.blocks}): {(live.gates?.funded?.override_roles || []).map((r) => HELP_ROLE_LABEL[r] || r).join(', ') || 'nobody'}, written reason of at least {live.gates?.funded?.override_min_reason_chars} characters, logged.</p>
                <p>· {live.gates?.cleared?.name || 'Invoice OK'} gate ({live.gates?.cleared?.blocks}): {(live.gates?.cleared?.override_roles || []).length ? live.gates.cleared.override_roles.join(', ') : 'nobody can override'}.</p>
                <p>· Stock short at Book truck: {(live.stock_override_roles || []).map((r) => HELP_ROLE_LABEL[r] || r).join(', ')}, reason of at least {live.stock_override_min_reason_chars} characters.</p>
                {live.accounts && (
                  <p>· Accounts hand-off: {live.accounts.notify_enabled
                    ? `automatic (${[live.accounts.email_configured && 'Zoho email', live.accounts.whatsapp_configured && 'WhatsApp'].filter(Boolean).join(' + ') || 'no channel configured'}); the app checks Zoho for the invoice every ${Math.round((live.accounts.probe_interval_sec || 0) / 60)} min${live.accounts.reminder_enabled === false
                        ? '. Reminders are switched off on this server, so Accounts are told once and not chased again.'
                        : ` and reminds Accounts after ${Math.round((live.accounts.reminder_after_min || 0) / 60)} h, then every ${Math.round((live.accounts.reminder_every_min || 0) / 60)} h.`}`
                    : 'automatic notification is switched off on this server — use "Message Accounts on WhatsApp" on the load.'}</p>
                )}
              </div>
            )}
          </HelpSection>

          <HelpSection title="Who to call for what">
            <ul className="space-y-1.5">
              {manual.contacts.map((c) => (
                <li key={c.who} className="flex gap-2">
                  <span className="shrink-0 w-28 font-medium text-stone-900">{c.who}</span>
                  <span className="text-xs text-stone-600 leading-snug">{c.need}</span>
                </li>
              ))}
            </ul>
          </HelpSection>

          <HelpSection title="What's new">
            <ul className="space-y-1.5">
              {HELP_CHANGES.slice(0, 6).map((c, i) => (
                <li key={i} className="flex gap-2">
                  <span className="shrink-0 font-mono text-[11px] text-stone-400 pt-0.5">{c.date}</span>
                  <span className="text-xs text-stone-600 leading-snug">{c.note}</span>
                </li>
              ))}
            </ul>
          </HelpSection>
        </div>

        <div className="px-5 py-2 border-t border-stone-200 text-[11px] text-stone-500 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span>v{APP_VERSION} · built {BUILD_TIME}</span>
          <span>
            {rulesLine}
            {rulesState !== 'loading' && (
              <button type="button" onClick={refreshRules} className="ml-1.5 underline decoration-dotted hover:text-amber-700">refresh</button>
            )}
          </span>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ─── SALES TICKER ───────────────────────────────────────────────────────────
//
// A strip along the bottom of every sales screen: what this salesperson has to
// act on, worst first. Collapsed it is one line — the three counts and the single
// most urgent row. Tapping it opens the full list upward.
//
// Nothing on the strip can be snoozed or dismissed. The only way a row leaves is
// to do the thing it names. That is deliberate: the strip is a view of work the
// business already has, not a second list somebody has to keep in step.

const TICKER_BANDS = {
  overdue: { label: 'overdue', dot: 'bg-red-500',     chip: 'border-red-200 bg-red-50 text-red-800' },
  today:   { label: 'today',   dot: 'bg-amber-500',   chip: 'border-amber-200 bg-amber-50 text-amber-900' },
  fresh:   { label: 'new',     dot: 'bg-emerald-500', chip: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
};
const TICKER_BAND_KEYS = ['overdue', 'today', 'fresh'];

const rolesOfMe = (me) => [me?.role, ...(me?.extra_roles || [])].filter(Boolean);

// The ticker belongs to the salesperson. Admin and manager get the Dashboard
// tiles instead — they stand behind every customer, so a personal worklist would
// just be the whole company's backlog squeezed into a strip.
const showsSalesTicker = (me) => {
  const r = rolesOfMe(me);
  return r.includes('staff') && !r.includes('admin') && !r.includes('manager');
};

// Claim straight off the row. The customer is unclaimed until someone takes them,
// and whoever takes them owns every quote and truck that follows — so the button
// says who won rather than quietly succeeding.
const TickerClaimButton = ({ row, onClaimed }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const claim = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/customers/${row.customer_id}/claim`, { customer_name: row.customer_name || '' });
      if (onClaimed) onClaimed();
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not claim this customer');
      setBusy(false);
    }
  };
  if (err) return <span className="text-[11px] text-red-700 max-w-[14rem] text-right">{err}</span>;
  return (
    <button type="button" onClick={claim} disabled={busy}
      className="shrink-0 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold px-2.5 py-1">
      {busy ? 'Claiming…' : 'Claim'}
    </button>
  );
};

const SalesTickerRow = ({ row, onOpen, onClaimed }) => {
  const band = TICKER_BANDS[row.band] || TICKER_BANDS.today;
  return (
    <li>
      <div role="button" tabIndex={0}
        onClick={() => onOpen(row)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(row); } }}
        className="w-full flex items-start gap-2.5 px-4 py-2 hover:bg-stone-50 cursor-pointer focus:outline-none focus-visible:bg-stone-50">
        <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${band.dot}`} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-stone-800 font-medium truncate">{row.title}</span>
          <span className="block text-[11px] text-stone-500 truncate">
            {row.customer_name ? `${row.customer_name} · ` : ''}{row.detail}
          </span>
        </span>
        {row.reference && (
          <span className="hidden sm:inline shrink-0 mt-0.5 rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[10px] text-stone-600 font-mono">
            {row.reference}
          </span>
        )}
        {row.can_claim && row.customer_id
          ? <TickerClaimButton row={row} onClaimed={onClaimed} />
          : <span aria-hidden="true" className="shrink-0 text-stone-300 text-sm mt-0.5">›</span>}
      </div>
    </li>
  );
};

const SalesTicker = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(() => { try { return JSON.parse(localStorage.getItem('eb_me') || 'null'); } catch (e) { return null; } });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [dead, setDead] = useState(false);   // endpoint missing or degraded — show nothing at all

  useEffect(() => {
    let alive = true;
    getCachedMe().then((m) => { if (alive && m) setMe(m); });
    return () => { alive = false; };
  }, []);

  const eligible = showsSalesTicker(me) && !location.pathname.startsWith('/atlas');

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sales/ticker`);
      if (r.data?.degraded) { setDead(true); return; }
      setData(r.data);
      setDead(false);
    } catch (e) {
      setDead(true);
    }
  }, []);

  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    let debounce = null;
    const refresh = () => { if (!cancelled) load(); };

    refresh();
    // Any event that changes the work fans out as `sales_ticker`; several can land
    // together (a status move publishes twice), so coalesce them into one re-read.
    const onEvent = () => { clearTimeout(debounce); debounce = setTimeout(refresh, 1200); };
    if (EB_EVENTS) EB_EVENTS.addEventListener('sales_ticker', onEvent);
    // Bands also move with the clock — a quote goes quiet, a truck's wait turns
    // overdue — and no event fires for that. Five minutes keeps the strip honest.
    const tick = setInterval(refresh, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
      clearInterval(tick);
      if (EB_EVENTS) EB_EVENTS.removeEventListener('sales_ticker', onEvent);
    };
  }, [eligible, load]);

  if (!eligible || dead || !data) return null;

  const items = data.items || [];
  const counts = data.counts || {};
  const top = items[0];
  const openRow = (row) => {
    setOpen(false);
    if (row.link) navigate(row.link);
  };

  return (<>
    {/* The strip is fixed to the viewport, so this spacer — in normal flow at the
        end of the page — keeps it off the last row of whatever is underneath. */}
    <div aria-hidden="true" className="h-12 print:hidden" />
    <div className="fixed inset-x-0 bottom-0 z-30 print:hidden">
      {open && items.length > 0 && (
        <div className="mx-auto max-w-3xl border-x border-t border-stone-200 rounded-t-xl bg-white shadow-2xl overflow-hidden">
          <ul className="max-h-[min(60vh,20rem)] overflow-y-auto divide-y divide-stone-100">
            {items.map((row) => (
              <SalesTickerRow key={row.id} row={row} onOpen={openRow} onClaimed={load} />
            ))}
          </ul>
          {data.count > items.length && (
            <div className="px-4 py-1.5 text-[11px] text-stone-500 border-t border-stone-100 bg-stone-50">
              Showing the {items.length} most urgent of {data.count}.
            </div>
          )}
        </div>
      )}
      <button type="button" onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={items.length ? `Your work: ${items.length} things to act on` : 'Your work: nothing outstanding'}
        className="w-full border-t border-stone-200 bg-white/95 backdrop-blur px-4 py-2 flex items-center gap-2 text-left hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400">
        <span aria-hidden="true" className="text-stone-400 text-xs">{open ? '▾' : '▴'}</span>
        {items.length === 0 ? (
          <span className="text-sm text-stone-500">Nothing to do. Nice work.</span>
        ) : (
          <>
            <span className="flex items-center gap-1.5 shrink-0">
              {TICKER_BAND_KEYS.filter((b) => counts[b] > 0).map((b) => (
                <span key={b} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TICKER_BANDS[b].chip}`}>
                  {counts[b]} {TICKER_BANDS[b].label}
                </span>
              ))}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-stone-700">
              {top ? top.title : ''}
            </span>
          </>
        )}
      </button>
    </div>
  </>);
};

// A broken strip must never take a working screen with it, and it must never
// leave a blank bar pinned across the bottom either — on a render error it goes.
class SalesTickerBoundary extends React.Component {
  constructor(props) { super(props); this.state = { broken: false }; }
  static getDerivedStateFromError() { return { broken: true }; }
  componentDidCatch(error, info) { console.error('Sales ticker failed to render:', error, info); }
  render() { return this.state.broken ? null : this.props.children; }
}

const HelpPill = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // ProtectedRoute has just written eb_me, so the first render already knows the role.
  const [me, setMe] = useState(() => { try { return JSON.parse(localStorage.getItem('eb_me') || 'null'); } catch (e) { return null; } });
  useEffect(() => {
    let alive = true;
    getCachedMe().then((m) => { if (alive && m) setMe(m); });
    return () => { alive = false; };
  }, []);
  const screen = screenForPath(location.pathname);
  if (location.pathname.startsWith('/atlas') || !me) return null;
  // The sales ticker owns the bottom edge on a salesperson's screen; sit above it
  // rather than on top of it.
  const lift = showsSalesTicker(me) ? 'bottom-14' : 'bottom-4';
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        aria-label="Help for your role" title="Help — what each button does and why it may be disabled"
        className={`fixed ${lift} right-4 z-40 inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold pl-2.5 pr-4 py-2 shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 print:hidden`}>
        <span aria-hidden="true" className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold">?</span>
        Help
      </button>
      <HelpDrawer open={open} onOpenChange={setOpen} me={me} screen={screen} />
    </>
  );
};

// The pill is mounted on every staff screen, so a bug in it must never take the
// screen down with it: on a render error the pill simply disappears.
class HelpBoundary extends React.Component {
  constructor(props) { super(props); this.state = { broken: false }; }
  static getDerivedStateFromError() { return { broken: true }; }
  componentDidCatch(error, info) { console.error('Help pill failed to render:', error, info); }
  render() { return this.state.broken ? null : this.props.children; }
}

// ─── PROTECTED ROUTE ───────────────────────────────────────────────────────

const ProtectedRoute = ({ roles, children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // 'checking' | 'ok' | 'denied'

  useEffect(() => {
    const t = localStorage.getItem('eb_token');
    if (!t) { navigate('/login'); return; }
    axios.get(`${API}/auth/me`)
      .then((r) => {
        try { localStorage.setItem('eb_me', JSON.stringify(r.data)); } catch (e) {}
        const userRoles = [r.data.role, ...(r.data.extra_roles || [])];
        if (!roles || roles.some((x) => userRoles.includes(x))) setState('ok');
        else setState('denied');
      })
      .catch(() => navigate('/login'));
  }, []);

  if (state === 'checking') {
    return <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500 text-sm">Checking access…</div>;
  }
  if (state === 'denied') {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-700">Access denied</CardTitle>
            <CardDescription>Your account does not have permission to view this page.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => { localStorage.removeItem('eb_token'); localStorage.removeItem('eb_refresh'); invalidateCachedMe(); navigate('/login'); }} variant="outline" className="w-full">
              Sign in as a different user
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  // NotificationsHost opens the SSE connection and renders toast slide-ins.
  // Mounted here so it activates on every authenticated page and cleans up on logout.
  // HelpPill is the floating "Help" button (hides itself on /atlas). SalesTicker is
  // the bottom strip of outstanding work; it shows for salespeople only. Both are
  // fixed to the viewport, so the spacer keeps the strip off the last row of the
  // page underneath it.
  return (<>
    <NotificationsHost />
    {children}
    <SalesTickerBoundary><SalesTicker /></SalesTickerBoundary>
    <HelpBoundary><HelpPill /></HelpBoundary>
  </>);
};

// ─── PORTAL NAVIGATION HUB (Staff/Admin) ─────────────────────────────────────

// Portal switcher rendered as a single segmented control so the links read as one
// uniform group (not loose candy-coloured pills). `full` stretches the segments to
// equal widths across the row on mobile; inline (auto width) on desktop.
// `admin` shows the Atlas segment. The /atlas route is admin-only, so for anyone
// else the link would only lead to the access-denied card — hide it instead.
const PortalNav = ({ full = false, admin = false }) => {
  const seg = `text-xs px-3 py-1.5 rounded-md text-stone-600 hover:bg-white hover:text-amber-800 hover:shadow-sm font-medium transition-all whitespace-nowrap text-center${full ? ' flex-1' : ''}`;
  return (
    <div className={`${full ? 'flex w-full' : 'inline-flex'} items-center gap-0.5 rounded-lg border border-stone-200 bg-stone-100 p-0.5`}>
      <Link to="/factory" className={seg}>🏭 Factory</Link>
      <Link to="/delivery" className={seg}>🚛 Transport</Link>
      <Link to="/" className={seg}>👤 Customer</Link>
      {admin && <Link to="/atlas" className={seg}>📐 Atlas</Link>}
    </div>
  );
};

// ─── Public order tracking (/track) — customer enters estimate # + mobile ────
const TRACK_STAGES = [
  { key: 'quote',      label: 'Quote submitted',   icon: '📝', note: 'Under review by our team' },
  { key: 'order',      label: 'Order confirmed',   icon: '📋', note: 'Trucks are sent as your site needs them' },
  { key: 'confirmed',  label: 'Truck scheduled',   icon: '✅', note: 'This truck is scheduled for delivery' },
  { key: 'loaded',     label: 'Loaded',            icon: '📦', note: 'Loaded and preparing to depart' },
  { key: 'in_transit', label: 'Out for delivery',  icon: '🚚', note: 'On the way to you' },
  { key: 'delivered',  label: 'Delivered',         icon: '🎉', note: 'Truck delivered' },
];
// 'cancelled' is not a stage — it is shown as a banner above the timeline.

const OrderTrackingPage = () => {
  // ?ref=EST-2238 — the Order History rows in the customer portal link here with
  // the number already filled in, so the customer only types their mobile. The
  // mobile is never put in the URL: it is half of the check that this is their
  // order, and URLs get shared, logged and pasted into chats.
  const location = useLocation();
  const prefilledRef = (new URLSearchParams(location.search).get('ref') || '').trim();
  const [ref, setRef] = useState(prefilledRef);
  const [mobile, setMobile] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => { document.title = 'EarthBlocks | Track your order'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setResult(null);
    if (!ref.trim() || mobile.replace(/\D/g, '').length < 10) {
      setErr('Enter your order number and 10-digit mobile.'); return;
    }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/track`, { params: { ref: ref.trim(), mobile } });
      if (r.data.found) setResult(r.data);
      else setErr('No order found for that number + mobile. Please check and try again.');
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not look up your order. Please try again.');
    } finally { setBusy(false); }
  };

  const currentIdx = result ? TRACK_STAGES.findIndex(s => s.key === result.stage) : -1;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <header className="bg-white shadow-sm border-b-2 border-amber-700">
        <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={EB_LOGO} alt="EarthBlocks" className="h-9 w-auto" />
            <div>
              <h1 className="text-lg font-bold text-stone-800">Track your order</h1>
              <p className="text-[11px] text-amber-700 font-medium">EarthBlocks</p>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild><Link to="/">Home</Link></Button>
        </div>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-4 py-6">
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="px-4 py-4">
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">Order number <span className="text-stone-400">— quote or invoice</span></Label>
                <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="EST-2238 or INV2627-175" />
              </div>
              <div>
                <Label className="text-xs">Mobile (as on the order)</Label>
                <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" inputMode="numeric"
                  autoFocus={!!prefilledRef} />
              </div>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <Button type="submit" disabled={busy} className="w-full bg-amber-800 hover:bg-amber-900 text-white">
                {busy ? 'Checking…' : 'Track order'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className="border-stone-200 shadow-sm mt-4">
            <CardContent className="px-4 py-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-stone-800">{result.customer_name}</p>
                  <p className="text-[11px] text-stone-400 font-mono">
                    {result.estimate_number}{result.invoice_number ? ` · ${result.invoice_number}` : ''}
                  </p>
                  {result.has_delivery && result.call_off_seq ? (
                    <p className="text-[11px] text-stone-500">
                      Load {result.call_off_seq}{result.scheduled_date ? ` · scheduled ${fmtDate(result.scheduled_date)}` : ''}
                    </p>
                  ) : result.needed_by ? (
                    <p className="text-[11px] text-stone-500">Requested delivery: {fmtDate(result.needed_by)}</p>
                  ) : null}
                </div>
                {result.source_factory && (() => { const ft = factoryTag(result.source_factory); return ft ? (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${ft.pill}`}>🏭 {ft.label}</span>
                ) : null; })()}
              </div>
              {result.stage === 'cancelled' && (
                <div className="mb-3 rounded-md border border-stone-300 bg-stone-100 px-3 py-2 text-xs text-stone-700">
                  This load was cancelled{result.cancelled_at ? ` on ${fmtDate(result.cancelled_at)}` : ''}. Please contact your sales representative about the next load.
                </div>
              )}
              <ol className="space-y-0">
                {TRACK_STAGES.map((s, i) => {
                  const done = currentIdx >= 0 && i <= currentIdx;
                  const current = i === currentIdx;
                  return (
                    <li key={s.key} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm border ${done ? 'bg-amber-700 border-amber-700 text-white' : 'bg-white border-stone-300 text-stone-300'}`}>
                          {done ? s.icon : '•'}
                        </span>
                        {i < TRACK_STAGES.length - 1 && <span className={`w-0.5 flex-1 min-h-[18px] ${i < currentIdx ? 'bg-amber-700' : 'bg-stone-200'}`} />}
                      </div>
                      <div className={`pb-3 ${current ? '' : ''}`}>
                        <p className={`text-sm font-medium ${done ? 'text-stone-800' : 'text-stone-400'}`}>
                          {s.label}{current && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">Now</span>}
                        </p>
                        <p className="text-[11px] text-stone-400">{s.note}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
              {result.stage === 'delivered' && (result.pod_media || []).length > 0 && (
                <div className="mt-2 border-t border-stone-100 pt-2">
                  <p className="text-[11px] text-stone-500 mb-1">Proof of delivery</p>
                  <div className="flex gap-2 flex-wrap">
                    {result.pod_media.filter(m => (m.url || m).toString().match(/^data:image|\.(jpg|jpeg|png|webp)/i)).slice(0, 4).map((m, idx) => (
                      <img key={idx} src={m.url || m} alt="POD" className="w-16 h-16 object-cover rounded border border-stone-200" />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        <p className="text-[11px] text-stone-400 text-center mt-4">Need help? Contact your EarthBlocks sales representative.</p>
      </main>
    </div>
  );
};

// Catches render errors in any page so one broken screen doesn't white-out the app.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary caught:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <img src={EB_LOGO} alt="EarthBlocks" className="h-10 mx-auto mb-4 object-contain" />
            <h1 className="text-lg font-bold text-stone-800 mb-1">Something went wrong</h1>
            <p className="text-sm text-stone-500 mb-4">This page hit an unexpected error. Please reload — your data is safe.</p>
            <button onClick={() => window.location.reload()}
              className="bg-amber-800 hover:bg-amber-900 text-white px-4 py-2 rounded-md text-sm">
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main App Component
/* Which build is running, and a way off a stale one.

   Top right of every page. Quiet by default - version and build time in small
   grey type - and when the service worker reports a newer app waiting, it turns
   into a button that installs it.

   This matters most for the installed PWA. The shell is served cache-first, so
   without something that both SHOWS the running build and offers the new one, an
   installed app can sit on a stale build indefinitely and the only obvious cure
   is deleting and reinstalling it. */
const VersionStamp = () => {
  const [waiting, setWaiting] = useState(() =>
    (typeof window !== 'undefined' && window.__ebUpdate) || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => setWaiting(e.detail || window.__ebUpdate || null);
    window.addEventListener('eb:sw-update', onUpdate);
    return () => window.removeEventListener('eb:sw-update', onUpdate);
  }, []);

  const applyUpdate = () => {
    setBusy(true);
    const sw = waiting && waiting.waiting;
    // The worker stepping forward fires controllerchange, and index.js reloads
    // on that. The timeout is the backstop for the case where the waiting worker
    // has already gone by the time we ask.
    if (sw) sw.postMessage({ type: 'SKIP_WAITING' });
    else window.location.reload();
    setTimeout(() => window.location.reload(), 4000);
  };

  if (waiting) {
    return (
      <button
        onClick={applyUpdate}
        disabled={busy}
        title={`Running v${APP_VERSION}, built ${BUILD_TIME} IST. A newer version is ready.`}
        className="fixed top-2 right-2 z-[60] rounded-full bg-amber-600 hover:bg-amber-500
                   px-3 py-1 text-[11px] font-semibold text-white shadow-lg
                   focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70"
      >
        {busy ? 'Updating...' : 'Update available'}
      </button>
    );
  }
  // Nothing to show when the app is current. The build is already stamped in the
  // footer ("Last updated ..."), so a second copy pinned over the header only
  // repeated it. The atlas carries its own stamp instead — it has no footer, and
  // it is the page where knowing which build you are looking at actually matters.
  return null;
};

const App = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <VersionStamp />
      <Routes>
        {/* Customer Portal */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/quote-request" element={<QuoteRequest />} />
        {/* The atlas is staff-only for now.

            It is a quoting tool before it is a customer tool: it prices a wall
            off live rates and knows the SKU behind every block, so it goes out
            behind the login the dashboard already uses rather than beside the
            public pages above. Customers get their own way in later, and that
            needs a customer identity this app does not have yet - verify-otp
            issues no session, and there is no customers table to hang one on.

            Gated BEFORE it is ever published, so there is no window in which it
            is reachable by anyone who finds the URL. */}
        <Route path="/atlas" element={
          <ProtectedRoute roles={['admin']}><BlockAtlas /></ProtectedRoute>
        } />
        <Route path="/reorder" element={<ReorderForm />} />
        <Route path="/track" element={<OrderTrackingPage />} />
        <Route path="/quote-success" element={<QuoteSuccess />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Admin/Staff Dashboard */}
        <Route path="/dashboard" element={
          <ProtectedRoute roles={['admin','manager','staff']}><Dashboard /></ProtectedRoute>
        } />
        <Route path="/zoho-setup" element={<ZohoSetup />} />
        <Route path="/zoho-callback" element={<OAuthCallback />} />

        {/* Factory Staff Portal — gated */}
        <Route path="/factory" element={
          <ProtectedRoute roles={['factory','admin']}><FactoryPortal /></ProtectedRoute>
        } />

        {/* Transport Staff Portal */}
        {/* manager: cancel / link invoice / override; factory: mark loading completed */}
        <Route path="/delivery" element={
          <ProtectedRoute roles={['transport','admin','manager','factory']}><DeliveryPortal /></ProtectedRoute>
        } />
        <Route path="/delivery/:deliveryId" element={
          <ProtectedRoute roles={['transport','admin','manager','factory']}><DeliveryDetail /></ProtectedRoute>
        } />
        <Route path="/inbox" element={
          <ProtectedRoute roles={['admin','manager','transport','factory']}><WhatsAppInbox /></ProtectedRoute>
        } />
      </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;